import type { NoticeParams } from "../pages/engines/lockCalculator/model/noticeParams";
import { type Translation } from "../hooks/useTranslations";
import { type UILanguageCode } from "../types";

const fr: Translation = {
  languageCode: "fr" as UILanguageCode,
  projects: {
    all: "Tous les projets",
    new: "Nouveau projet",
    singular: "Projet",
    plural: "Projets",
    my: "Mes projets",
    createNew: "Créer un nouveau projet",
    delete: "Supprimer le projet",
    search: "Rechercher des projets",
    shareWithUser: "Partager le projet avec un utilisateur",
    editPermissions: "Modifier les permissions",
    editSharings: "Modifier les partages",
    invitations: "Invitations au projet",

    invitationStatus: {
      pending: "En attente",
      accepted: "Accepté",
      rejected: "Refusé",
      cancelled: "Annulé",
      statusChanges: {
        accept: "Invitation acceptée avec succès.",
        rejected: "Invitation refusée.",
      },
    },

    invitePage: {
      badge: "Invitation d'accès au projet",
      fallbackAuthorName: "un membre de l'équipe",
      collaborationLead: "vous a invité à collaborer sur ce projet et à consulter son contenu partagé.",
      invitedBy: "Invité par",
      invitationSentTo: "Invitation envoyée à",
      projectName: "Nom du projet",
      description: "Description",
      inviteNoLongerAvailable:
        "Cette invitation n'est plus disponible car elle a déjà été acceptée, refusée ou annulée.",
      acceptSignedIn: "L'acceptation de cette invitation ouvrira immédiatement le projet.",
      acceptSignedOut: "L'acceptation de cette invitation vous demandera de vous connecter avant d'accéder au projet.",
      acceptAction: "Accepter l'invitation",
      rejectAction: "Refuser l'invitation",
    },

    questions: {
      delete: "Voulez-vous vraiment supprimer ce projet?",
      unshare: "Voulez-vous vraiment annuler le partage du projet avec cet utilisateur?",
    },

    errors: {
      deleteProjectWithConfigurationsNotPossible:
        "Il n'est pas possible de supprimer un projet s'il contient des configurations.",
      deleteProjectWithDocumentsNotPossible:
        "Il n'est pas possible de supprimer un projet s'il contient des documents.",
      notFound: "Projet non trouvé.",
      sharingNotFound: "Partage non trouvé.",
      sharingAlreadyExists: "Le projet est déjà partagé avec cet utilisateur.",
      documentNotFound: "Document non trouvé.",
      missingDocumentName: "Nom du document manquant.",
      documentAlreadyPresent: "Document déjà présent.",
      missingProjectStatus: "Le statut du projet est manquant.",
      inviteReadNotAllowed: "Vous n'êtes pas autorisé à voir cette invitation.",
      inviteInsertNotAllowed: "Vous n'êtes pas autorisé à envoyer une invitation pour ce projet.",
      inviteUpdateNotAllowed: "Vous n'êtes pas autorisé à accepter ou refuser cette invitation.",
      inviteDeleteNotAllowed: "Vous n'êtes pas autorisé à supprimer cette invitation.",
      inviteAlreadyExists: "Une invitation existe déjà pour cet utilisateur.",
      inviteNotFound: "Invitation non trouvée.",
      inviteAlreadyCancelled: "Cette invitation a déjà été annulée.",
      inviteAlreadyAccepted: "Cette invitation a déjà été acceptée.",
      inviteAlreadyRejected: "Cette invitation a déjà été refusée.",
    },
  },

  calculations: {
    all: "Tous les calculs",
    new: "Nouveau calcul",
    plural: "Calculs",
    create: "Créer un calcul",
    createNew: "Créer un nouveau calcul",
    delete: "Supprimer le calcul",
    duplicate: "Dupliquer le calcul",
    my: "Mes calculs",
    search: "Rechercher des calculs",
    reset: "Réinitialiser le calcul",
    newCalculationName: "Nom du nouveau calcul",
    shareWithUser: "Partager les calculs avec un utilisateur",
    editSharings: "Modifier les partages",
    invitations: "Invitations au calcul",
    invitePage: {
      badge: "Invitation d'accès à un calcul",
      collaborationLead: "vous a invité à collaborer sur ce calcul et à consulter son contenu partagé.",
      calculationName: "Nom du calcul",
      inviteNoLongerAvailable:
        "Cette invitation n'est plus disponible car elle a déjà été acceptée, refusée ou annulée.",
      acceptSignedIn: "Accepter cette invitation ouvrira immédiatement le calcul.",
      acceptSignedOut: "Accepter cette invitation vous demandera de vous connecter avant d'accorder l'accès au calcul.",
    },

    questions: {
      delete: "Voulez-vous vraiment supprimer ce calcul ?",
      unshare: "Voulez-vous vraiment annuler le partage du calcul avec cet utilisateur ?",
      reset: "Voulez-vous vraiment réinitialiser toutes les données du calcul actuel ?",
    },

    errors: {
      notFound: "Calcul non trouvé.",
      sharingNotFound: "Partage non trouvé.",
      sharingAlreadyExists: "Le calcul est déjà partagé avec cet utilisateur.",
      moduleNotSupported: "Module de calcul non pris en charge.",
      moduleRestricted: "Ce module ne permet pas de créer, modifier ou dupliquer des calculs.",
      inviteInsertNotAllowed: "Vous n'êtes pas autorisé à envoyer une invitation pour ce calcul.",
      inviteUpdateNotAllowed: "Vous n'êtes pas autorisé à accepter ou refuser cette invitation.",
      inviteDeleteNotAllowed: "Vous n'êtes pas autorisé à supprimer cette invitation.",
      inviteAlreadyExists: "Une invitation pour cet utilisateur existe déjà.",
      inviteNotFound: "Invitation introuvable.",
      inviteAlreadyCancelled: "Cette invitation a déjà été annulée.",
      inviteAlreadyAccepted: "Cette invitation a déjà été acceptée.",
      inviteAlreadyRejected: "Cette invitation a déjà été refusée.",
    },
  },

  sharingRequests: {
    title: "Demandes d'accès",
    requestedAt: "Demandée le",
    message: "Message",
    accept: "Accepter",
    reject: "Refuser",
    acceptTitle: "Accepter la demande d'accès",
    rejectQuestion: "Voulez-vous vraiment refuser cette demande d'accès?",
    accepted: "Demande d'accès acceptée.",
    rejected: "Demande d'accès refusée.",
    notShared: {
      projectTitle: "Projet non partagé",
      calculationTitle: "Calcul non partagé",
      projectText: "Ce projet n'a pas été partagé avec vous, vous ne pouvez donc pas y accéder.",
      calculationText: "Ce calcul n'a pas été partagé avec vous, vous ne pouvez donc pas y accéder.",
      requestText: "Vous pouvez demander à son propriétaire de le partager avec vous : il recevra votre demande par e-mail.",
      messageLabel: "Message au propriétaire",
      request: "Demander l'accès",
      sent: "Votre demande d'accès a été envoyée.",
      pending: (date: string) =>
        `Vous avez demandé l'accès le ${date} : vous recevrez un e-mail dès que le propriétaire aura répondu.`,
    },
    managePage: {
      projectTitle: "Demande d'accès à un projet",
      calculationTitle: "Demande d'accès à un calcul",
      requestedBy: "Demandée par",
      project: "Projet",
      calculation: "Calcul",
      projectLead: "vous demande l'accès à ce projet.",
      calculationLead: "vous demande l'accès à ce calcul.",
      alreadyAccepted: "Cette demande a déjà été acceptée.",
      alreadyRejected: "Cette demande a déjà été refusée.",
    },
    errors: {
      notFound: "Demande d'accès introuvable.",
      readNotAllowed: "Vous n'êtes pas autorisé à consulter cette demande d'accès.",
      alreadyExists: "Vous avez déjà demandé l'accès : le propriétaire n'a pas encore répondu.",
      alreadyShared: "Il a déjà été partagé avec vous : rechargez la page.",
      alreadyAccepted: "Cette demande d'accès a déjà été acceptée.",
      alreadyRejected: "Cette demande d'accès a déjà été refusée.",
    },
  },

  notifications: {
    title: "Notifications",
    projectSharingRequest: (user: string, project: string) =>
      `${user} demande l'accès au projet ${project}`,
    calculationSharingRequest: (user: string, calculation: string) =>
      `${user} demande l'accès au calcul ${calculation}`,
    projectSharingInvite: (user: string, project: string) =>
      `${user} vous a invité au projet ${project}`,
    calculationSharingInvite: (user: string, calculation: string) =>
      `${user} vous a invité au calcul ${calculation}`,
  },

  users: {
    add: "Ajouter un utilisateur",
    all: "Tous les utilisateurs",
    plural: "Utilisateurs",
    remove: "Supprimer l'utilisateur",
    invite: "Inviter un utilisateur",
    search: "Rechercher des utilisateurs",
    usersEmail: "E-mail des utilisateurs",
    usersName: "Nom des utilisateurs",

    questions: {},

    pending: {
      plural: "Utilisateurs en attente",
      edit: "Modifier l'utilisateur en attente",
      delete: "Supprimer l'utilisateur en attente",
      createdAt: "Ajouté",
      info: "Si cet e-mail n'a pas encore de compte, l'utilisateur reste en attente jusqu'à sa première connexion, où il reçoit ces rôles et permissions.",
    },

    errors: {
      notFound: "Utilisateur non trouvé.",
      alreadyExists: "Un utilisateur avec cet e-mail existe déjà.",
      pendingAlreadyExists: "Un utilisateur en attente avec cet e-mail existe déjà.",
    },
  },

  items: {
    all: "Tous les articles",
    singular: "Article",
    plural: "Articles",
    createNew: "Créer un nouvel article",
    delete: "Supprimer l'article",
    search: "Rechercher des articles",
    list: "Liste des articles",

    questions: {
      delete: "Voulez-vous vraiment supprimer cet article?",
    },

    errors: {
      notFound: "Article non trouvé.",
      doesNotExist: "L'article avec ce code n'existe pas.",
      missingCode: "Le code de l'article est manquant.",
    },
  },

  clients: {
    all: "Tous les clients",
    plural: "Clients",
    createNew: "Créer un nouvel client",
    delete: "Supprimer l'client",
    search: "Rechercher des clients",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce système?",
    },

    errors: {
      notFound: "Client non trouvé.",
      insertNotAllowed: "Vous n'êtes pas autorisé à insérer un nouvel client.",
      readNotAllowed: "Vous n'êtes pas autorisé à lire cet client.",
      updateNotAllowed: "Vous n'êtes pas autorisé à mettre à jour cet client.",
      deleteNotAllowed: "Vous n'êtes pas autorisé à supprimer l'client.",
    },
  },

  accessControl: {
    title: "Contrôle d'accès",
    plural: "Contrôle d'accès",
    disclaimer:
      "Le contrôle d'accès définit quels utilisateurs peuvent utiliser chaque application. Il ne lève pas le géoblocage : un utilisateur autorisé reste refusé dans les pays où l'application, ou l'un de ses modules, est géobloquée. Pour permettre à un utilisateur d'accéder depuis n'importe où, attribuez-lui la permission geo_blocking:bypass (par exemple avec le rôle geo-blocking-bypass) : elle lève tout géoblocage, sur toutes les applications et tous les modules.",
    applications: "Applications",
    modules: "Modules",
    modulesDisclaimer:
      "Le contrôle d'accès d'un module ne s'applique que dans l'application principale : l'application propre au module, s'il en a une, suit son propre onglet. Un utilisateur exclu d'un module ne le voit pas, ni ses calculs, et ne peut pas les ouvrir.",

    settings: {
      lockSwitchLabel: "Restreindre l'application aux utilisateurs autorisés",
      lockSwitchDescription:
        "Lorsqu'elle est activée, seuls les utilisateurs disposant d'une règle 'autorisé' ou d'un accès administrateur peuvent utiliser l'application.",
      confirmEnable: "Voulez-vous vraiment restreindre l'application aux seuls utilisateurs autorisés ?",
      confirmDisable: "Voulez-vous vraiment autoriser à nouveau tous les utilisateurs authentifiés ?",
    },

    // the modules' tab: a module's lock switch, in the main application only
    moduleSettings: {
      lockSwitchLabel: "Restreindre le module aux utilisateurs autorisés",
      lockSwitchDescription:
        "Lorsqu'elle est activée, seuls les utilisateurs disposant d'une règle 'autorisé' ou d'un accès administrateur peuvent utiliser le module dans l'application principale.",
      confirmEnable: "Voulez-vous vraiment restreindre le module aux seuls utilisateurs autorisés ?",
      confirmDisable: "Voulez-vous vraiment autoriser à nouveau tous les utilisateurs authentifiés ?",
    },

    userAccess: {
      notAllowed: "Non autorisé",
      causes: {
        rule: "Règle",
        default: "Par défaut",
        admin: "Administrateur",
        locked: "App restreinte",
      },
    },

    rules: {
      plural: "Règles d'accès",
      app: "Application",
      createNew: "Ajouter une règle",
      search: "Rechercher par e-mail ou motif",
      delete: "Supprimer la règle",
      email: "E-mail",
      user: "Utilisateur",
      status: "Statut",
      statusAllowed: "Autorisé",
      statusBlocked: "Bloqué",
      reason: "Motif",
      createdAt: "Créé le",
      import: "Importer",
      importTitle: "Importer des règles",
      importFrom: "Importer depuis",
      currentStatus: "Statut actuel",
      selectAll: "Tout sélectionner",
      importHint:
        "Les règles déjà présentes avec un statut différent ne sont pas sélectionnées : sélectionnez-les pour écraser le statut et le motif actuels.",

      questions: {
        delete: "Voulez-vous vraiment supprimer cette règle d'accès ?",
      },
    },

    forbiddenTitle: "Non autorisé",
    forbiddenText: "Vous n'êtes pas autorisé à accéder à cette application.",
    signedInAs: "Connecté en tant que",

    errors: {
      forbidden: "Vous n'êtes pas autorisé à accéder à cette application.",
      ruleNotFound: "Règle d'accès introuvable.",
      ruleMissingEmail: "L'e-mail est obligatoire.",
      ruleInvalidStatus: "Le statut doit être autorisé ou bloqué.",
      ruleCannotTargetSelf: "Vous ne pouvez pas ajouter une règle d'accès pour vous-même.",
      settingsReadNotAllowed: "Vous n'êtes pas autorisé à lire les paramètres d'accès.",
      settingsUpdateNotAllowed: "Vous n'êtes pas autorisé à mettre à jour les paramètres d'accès.",
      ruleReadNotAllowed: "Vous n'êtes pas autorisé à lire les règles d'accès.",
      ruleInsertNotAllowed: "Vous n'êtes pas autorisé à créer une règle d'accès.",
      ruleDeleteNotAllowed: "Vous n'êtes pas autorisé à supprimer une règle d'accès.",
      lastAdmin:
        "Vous ne pouvez pas supprimer le dernier utilisateur capable de gérer le contrôle d'accès de l'application.",
      invalidScope: "Portée d'accès inconnue.",
      invalidModule: "Module inconnu.",
    },
  },

  geoBlocking: {
    plural: "Géoblocage",
    disclaimer:
      "Le géoblocage définit depuis quels pays chaque application, ou un module, peut être utilisé. Les règles d'un module s'appliquent partout où le module est utilisé, dans l'application principale comme dans la sienne ; celles d'une application, uniquement à cette application. Sans règle, tous les pays sont autorisés ; dès qu'une règle est « Autorisé », seuls les pays autorisés le sont. Le contrôle d'accès ne le lève pas : pour permettre à un utilisateur d'accéder depuis n'importe où, attribuez-lui la permission geo_blocking:bypass (par exemple avec le rôle geo-blocking-bypass).",
    settings: "Régions et pays",
    modules: "Modules",
    rules: {
      singular: "Règle",
      target: "Région ou pays",
      type: "Type",
      region: "Région",
      country: "Pays",
      status: "Statut",
      statusAllowed: "Autorisé",
      statusBlocked: "Bloqué",
      createdAt: "Créée",
    },
    regions: {
      plural: "Régions",
      singular: "Région",
      countries: "Pays",
    },
    countries: {
      plural: "Pays",
      code: "Code",
    },
    errors: {
      moduleBlocked: "Vous n'êtes pas autorisé à accéder à ce module.",
      ruleAlreadyExists: "Une règle existe déjà pour cette région ou ce pays.",
      regionNameAlreadyExists: "Une région avec ce nom existe déjà.",
      regionInUse: "La région est utilisée par une règle de géoblocage.",
      blocksSelf: "Cette modification bloquerait votre propre accès à l'application.",
    },
  },

  usageStatistics: {
    plural: "Statistiques d'utilisation",
    from: "Du",
    to: "Au",
    apps: "Applications",
    visits: "Visites",
    description: "Nombre d'utilisateurs distincts par jour (UTC).",
    errors: {
      readNotAllowed: "Vous n'êtes pas autorisé à consulter les statistiques d'utilisation.",
      invalidPeriod: "Choisissez une période valide, d'un an au maximum.",
    },
  },

  helpRequests: {
    title: "Aide",
    dialogTitle: "Demander de l'aide",
    intro: "Décrivez votre problème : notre équipe de conseil technique vous répondra par e-mail.",
    type: "Type",
    subject: "Objet",
    message: "Message",
    screenshot: "Capture d'écran",
    attachScreenshot: "Joindre la capture d'écran de la page actuelle",
    screenshotUnavailable: "La capture d'écran de la page n'a pas pu être réalisée.",
    attachments: "Pièces jointes",
    attachmentsHint: (maxFiles: number, maxMegabytes: number) =>
      `Jusqu'à ${maxFiles} fichiers, ${maxMegabytes} Mo au total.`,
    removeAttachment: "Supprimer la pièce jointe",
    diagnosticsInfo:
      "Des informations techniques sur votre navigateur et les dernières erreurs de la page seront jointes pour nous aider à analyser le problème.",
    send: "Envoyer",
    success: "Votre demande a été envoyée. Vous recevrez une confirmation par e-mail.",

    errors: {
      invalidForm: "La demande n'a pas pu être lue ou dépasse la taille autorisée.",
      missingSubject: "L'objet est obligatoire.",
      subjectTooLong: "L'objet est trop long.",
      missingMessage: "Le message est obligatoire.",
      messageTooLong: "Le message est trop long.",
      tooManyAttachments: (maxFiles: number) => `Vous pouvez joindre jusqu'à ${maxFiles} fichiers.`,
      attachmentsTooLarge: (maxMegabytes: number) => `Les pièces jointes dépassent ${maxMegabytes} Mo au total.`,
      invalidScreenshot: "La capture d'écran n'est pas valide.",
      invalidDiagnostics: "Les informations techniques ne sont pas valides.",
    },
  },

  feedbacks: {
    title: "Signaler aux développeurs",
    dialogTitle: "Envoyer un retour",
    intro: "Signalez un bug ou partagez une suggestion : l'équipe de développement la recevra.",
    types: {
      bug: "Bug",
      suggestion: "Suggestion",
      other: "Autre",
    },
    success: "Merci, votre retour a été envoyé.",

    errors: {
      sendNotAllowed: "Vous n'êtes pas autorisé à envoyer un retour.",
      invalidType: "Choisissez un type de retour valide.",
    },
  },

  home: {
    greeting: (name: string) => `Bonjour, ${name}`,
    subtitle: "Choisissez un calculateur pour commencer un nouveau calcul.",
    availableCalculators: "Calculateurs disponibles",
  },

  modules: {
    all: "Tous les modules",
    plural: "Modules",
    createNew: "Créer un nouveau module",
    delete: "Supprimer le module",
    search: "Rechercher des modules",
    singular: "Module",

    questions: {
      delete: "Voulez-vous vraiment supprimer ce module ?",
    },

    errors: {
      notFound: "Module non trouvé.",
      insertNotAllowed: "Vous n'avez pas l'autorisation de créer un nouveau module.",
      readNotAllowed: "Vous n'avez pas l'autorisation de lire ce module.",
      updateNotAllowed: "Vous n'avez pas l'autorisation de modifier ce module.",
      deleteNotAllowed: "Vous n'avez pas l'autorisation de supprimer ce module.",
    },
  },

  categories: {
    all: "Toutes les catégories",
    plural: "Catégories",
    createNew: "Créer une nouvelle catégorie",
    delete: "Supprimer la catégorie",
    search: "Rechercher des catégories",
    view: "Afficher le contenu delle categorie", // o semplicemente "Voir les catégories"
    edit: "Modifier la catégorie",

    questions: {
      delete: "Voulez-vous vraiment supprimer cette catégorie ?",
    },

    errors: {
      notFound: "Catégorie non trouvée.",
      insertNotAllowed: "Vous n'avez pas l'autorisation de créer una nouvelle catégorie.",
      readNotAllowed: "Vous n'avez pas l'autorisation de lire cette catégorie.",
      updateNotAllowed: "Vous n'avez pas l'autorisation de modifier cette catégorie.",
      deleteNotAllowed: "Vous n'avez pas l'autorisation de supprimer cette catégorie.",
    },
  },

  roles: {
    plural: "Rôles",
  },

  permissions: {
    plural: "Permissions",
  },

  secrets: {
    plural: "Secrets",

    errors: {
      missingValue: "Valeur d'secret manquant.",
    },
  },

  adminDashboard: {
    projects: "Consulter et gérer les projets de tous les utilisateurs",
    calculations: "Consulter et gérer les calculs de tous les utilisateurs",
    modules: "Configurer les modules de calcul et leurs paramètres",
    categories: "Gérer les catégories utilisées pour regrouper les modules",
    items: "Gérer le catalogue des articles",
    clients: "Gérer les clients API, leurs secrets et leurs autorisations",
    users: "Gérer les utilisateurs et leurs rôles",
    accessControl: "Restreindre les applications et gérer les utilisateurs autorisés ou bloqués",
    usageStatistics: "Consultez combien d'utilisateurs accèdent chaque jour aux applications et aux modules",
    geoBlocking: "Autorisez ou bloquez les applications et les modules par pays",
  },

  common: {
    date: "Date",
    active: "Actif",
    add: "Ajouter",
    errorInvalidVisibility: "Visibilité non valide : choisissez publique, interne ou masquée.",
    administration: "Administration",
    all: "Tout",
    alreadyPresent: "Déjà présent",
    angle: "Angle",
    angles: "Angles",
    archived: "Archivé",
    author: "Auteur",
    automaticSync: "Synchronisation automatique",
    back: "Retour",
    baseHeight: "Hauteur de base",
    cancel: "Annuler",
    categories: "Catégories",
    category: "Catégorie",
    clamped: "Pincé",
    thickness: "Épaisseur",
    close: "Fermer",
    code: "Code",
    clearPoints: "Effacer les points",
    undo: "Annuler",
    components: "Composants",
    confirmed: "Confirmé",
    coordinates: "Coordonnées",
    copy: "Copie",
    country: "Pays",
    create: "Créer",
    created: "Créé",
    createNew: "Créer nouveau",
    curve: "Courbe",
    customer: "Client",
    dataHasBeenSavedSuccessfully: "Les données ont été enregistrées avec succès.",
    default: "Par défaut",
    degrees: "Degrés",
    delete: "Supprimer",
    description: "Description",
    designer: "Concepteur",
    details: "Détails",
    download: "Télécharger",
    dragAndDropOrUploadAnImage: "Glissez-déposez ou cliquez pour télécharger une image",
    draw: "Dessiner",
    dropIt: "Déposez-le !",
    duplicate: "Dupliquer",
    edit: "Modifier",
    editor: "Éditeur",
    email: "E-mail",
    error: "Erreur",
    errorLoadingData: "Erreur de chargement des données",
    extensions: "Extensions",
    factor: "Facteur",
    gap: "Jeu",
    failed: "Échoué",
    fieldContainsInvalidValueInsertBiggerOne: "Le champ contient une valeur invalide, insérez une valeur plus grande.",
    fieldInvalidValue: "Le champ contient une valeur invalide.",
    colorHexInvalid: "Veuillez saisir une couleur HEX valide au format #RRGGBB.",
    finish: "Terminer",
    fixed: "Fixe",
    forbidden: "Non autorisé",
    from: "De",
    generalData: "Données générales",
    genericError: "Une erreur est survenue, veuillez réessayer.",
    groovingMinHeight: "Hauteur min. de rainure",
    groovingMaxHeight: "Hauteur max. de rainure",
    groovingThickness: "Épaisseur de rainure",
    height: "Hauteur",
    help: "Aide",
    hidden: "Caché",
    hide: "Masquer",
    home: "Accueil",
    installed: "Installé",
    intermediate: "Intermédiaire",
    isVisible: "Est visible",
    languages: {
      arabic: "Arabe",
      chinese: "Chinois",
      croatian: "Croate",
      czech: "Tchèque",
      danish: "Danois",
      dutch: "Néerlandais",
      english: "Anglais",
      estonian: "Estonien",
      finnish: "Finnois",
      french: "Français",
      german: "Allemand",
      greek: "Grec",
      hungarian: "Hongrois",
      icelandic: "Islandais",
      italian: "Italien",
      japanese: "Japonais",
      korean: "Coréen",
      latvian: "Letton",
      lithuanian: "Lituanien",
      norwegian: "Norvégien",
      plural: "Langues",
      polish: "Polonais",
      portugese: "Portugais",
      russian: "Russe",
      singular: "Langue",
      slovak: "Slovaque",
      slovenian: "Slovène",
      spanish: "Espagnol",
      swedish: "Suédois",
      turkish: "Turc",
    },
    translations: "Traductions",
    lastAccess: "Dernier accès",
    lastModified: "Dernière modification",
    lastName: "Nom de famille",
    lastSync: "Dernière synchronisation",
    length: "Longueur",
    screwDiameter: "Diamètre de vis",
    screwHeadHeight: "Hauteur de tête de vis",
    loading: "Chargement...",
    location: "Emplacement",
    login: "Connexion",
    logout: "Se déconnecter",
    loop: "Boucle",
    mappings: "Correspondances",
    max: "Maximum",
    maxHeight: "Hauteur maximale",
    meters: "À",
    min: "Minimum",
    minHeight: "Hauteur minimale",
    minimumOrderQuantity: "Quantité minimale de commande",
    minOrderQuantity: "Qté min commande",
    minValue: "Valeur minimale",
    minWidth: "Largeur minimale",
    myProfile: "Mon profil",
    name: "Nom",
    never: "jamais",
    next: "Suivant",
    no: "Non",
    noItemsFound: "Aucun élément trouvé.",
    notFound: "Non trouvé",
    offer: "Offre",
    ok: "Ok",
    onlyImagesAreAllowed: "Seules les images sont autorisées.",
    open: "Ouvrir",
    optional: "optionnel",
    order: "Ordre",
    overwrite: "Écraser",
    owner: "Propriétaire",
    pitch: "Pente",
    print: "Imprimer",
    public: "Public",
    everyOne: "Tout le monde",
    remove: "Retirer",
    removeImage: "Retirer l'image",
    reset: "Réinitialiser",
    resetToDefault: "Réinitialiser par défaut",
    resetView: "Réinitialiser la vue",
    resourceNotFound: "La ressource que vous recherchez est introuvable.",
    roof: "Toit",
    rothoblaas: "Rothoblaas",
    rothoblaasUsers: "Utilisateurs Rothoblaas",
    save: "Enregistrer",
    search: "Rechercher",
    select: "Sélectionner",
    selectProjectInwhichCreateCopy: "Sélectionnez le projet dans lequel créer la copie",
    sentAt: "Envoyé le",
    sessionExpired: "Session expiré",
    settings: "Paramètres",
    shared: "Partagés",
    sharedByMe: "Partagé par moi",
    sharedWithMe: "Partagé avec moi",
    canEdit: "Peut modifier",
    viewOnly: "Lecture seule",
    sharedViaProject: "Partagé via le projet",
    shape: "Forme",
    show: "Afficher",
    span: "Portée",
    spans: "Portées",
    status: "Statut",
    stripeLength: "Longueur de bande",
    substructureHeight: "Hauteur de sous-structure",
    substructures: "Sous-structures",
    support: "Support",
    supportHeight: "Hauteur de support",
    supports: "Supports",
    sync: "Synchroniser",
    testEnvironment: "Environnement de test",
    to: "Mètres",
    toBeInstalled: "À installer",
    toInspect: "À inspecter",
    type: "Type",
    unlock: "Déverrouiller",
    update: "Mettre à jour",
    value: "Valeur",
    visible: "Visible",
    visibility: "Visibilité",
    unitSystem: "Système d'unités",
    unitSystemMetric: "Métrique",
    unitSystemImperial: "Impérial",
    width: "Largeur",
    quantity: "Quantité",
    yes: "Oui",
    chooseSecondPointDifferentXY: "Choisissez un deuxième point avec x et y différents.",
    zoomIn: "Zoom avant",
    zoomOut: "Zoom arrière",
    youAreNotAllowed: "Vous n'êtes pas autorisé à accéder à cette ressource.",
    area: "Aire",
    units: "Unités",
    volume: "Volume",
    preview: "Aperçu",
    summary: "Résumé",

    unitM: "m",
    unitCm: "cm",
    unitGPa: "GPa",
    unitMm: "mm",
    unitInch: "in",
    unitN: "N",
    unitNMm: "N·mm",
    unitDeg: "°",
    unitPcs: "pcs",
    unitM2: "m²",
    unitM4: "m^4",
    unitKn: "kN",
    unitKnM2: "kN/m²",
    unitKnM: "kN/m",
    unitMpa: "MPa",
    titlePreview: "Aperçu",
    wasteFactor: "Facteur de gaspillage",

    cookiePolicy: "Politique de cookies",
    privacyPolicy: "Politique de confidentialité",
    termsAndConditions: "Conditions générales",
    writePermissions: "Permissions d'écriture",
    invitationHasBeenSentSuccessfully: "Invitation envoyée avec succès.",
    resendInvite: "Renvoyer l'invitation",
    cancelInviteQuestion: "Voulez-vous vraiment annuler cette invitation ?",
    deleteInviteQuestion: "Voulez-vous vraiment supprimer cette invitation ?",
    invite: "Inviter",
    singleOptionAvailable:
      "Champ désactivé : une seule option sélectionnable est disponible pour la configuration choisie.",
  },

  concreteResinModule: {
    addCalculation: "Enregistrer le calcul",
    anchorMapping: "Correspondance d'ancrage",
    anchorMappings: "Correspondances d'ancrage",
    cartridges: "Cartouches",
    chemicalAnchor: "Ancrage chimique",
    chemicalAnchors: "Ancrages chimiques",
    downloadPdf: "Télécharger le PDF",
    downloadPdfNoCalculationsSavedHint: "Enregistrez au moins un calcul pour télécharger le PDF.",
    holeDepth: "Profondeur du trou ( h₁ )",
    holeDiameter: "Diamètre du trou ( d₀ )",
    holeDiameterImperial: "Foret US [in]",
    holeDiameterImperialInfo:
      'Calculé à partir du diamètre du trou : le plus petit foret au 1/64" qui ne lui est pas inférieur.',
    holeNumbers: "Nombre de trous",
    holes: "trous",
    instruction2:
      "Ce calcul estime la quantité de résine en fonction du facteur de surconsommation sélectionné. La surconsommation dépend souvent de l'application et de l'expérience de l'installateur.",
    instructionsSummaryPrefix:
      "La quantité de résine est estimée sur le remplissage du trou indiqué dans l'ETA du produit : en partant du fond du trou nettoyé, le remplir d'adhésif jusqu'aux deux tiers environ en retirant lentement la buse de mélange statique au fur et à mesure du remplissage, afin d'éviter les poches d'air ; si la buse n'atteint pas le fond du trou, utiliser une buse d'extension appropriée. Appliquez et injectez le produit conformément à la documentation Rothoblaas pour l'utilisation prévue. Rothoblaas se réserve le droit de modifier les méthodes de calcul, les conceptions ou les modèles sans préavis ni responsabilité ; les ressources de conception les plus à jour sont sur",
    instructionsSummarySuffix: ".",
    liters: "Litres",
    mlQuantity: "Quantité ml",
    noCalculationsSaved: "Aucun calcul enregistré",
    overageFactor: "Facteur de déchet",
    rodDiameter: "Diamètre de la tige ( d )",
    titleCalculationsSaved: "Calculs enregistrés",
    titleHoleGeometry: "Géométrie du trou",
    titleInstructionsAndNotes: "Instructions et notes",
    titleSummary: "Résumé",
    totalVolumeRequired: "Volume total requis",
    unitOverageFactor: "%",
    unitSystemImperial: "Impérial",
    unitSystemMetric: "Métrique",
    waste: "déchet",
    cartridgesShort: "cart.",
    unitLiters: "L",
    errors: {
      anchorInUseByMapping: "L'ancrage est utilisé par une correspondance : supprimez d'abord ses correspondances",
      anchorInUseByEntry:
        "L'ancrage est utilisé dans des calculs enregistrés : réglez-le sur Caché au lieu de le supprimer",
      anchorNotFound: "L'ancrage chimique est introuvable.",
      anchorNotAvailable: "L'ancrage chimique choisi n'est plus disponible : choisissez-en un autre.",
      anchorAlreadyPresent: "L'article est déjà un ancrage chimique.",
      mappingAlreadyPresent: "L'ancrage chimique a déjà une correspondance avec ces diamètres.",
      invalidRodDiameter: "Le diamètre de la tige n'est pas disponible pour l'ancrage chimique choisi.",
      invalidHoleDiameter: "Le diamètre du trou ne correspond pas au diamètre de tige choisi.",
      mappingNotFound: "La correspondance d'ancrage est introuvable.",
      entryMissingChemicalAnchor: "Choisissez un ancrage chimique avant d'enregistrer le calcul",
      entryIncompleteCalculation: "Remplissez tous les champs avant d'enregistrer le calcul",
    },
  },

  usLiftingCalculatorModule: {
    calculator: {
      aboveTarget: (target: number) => `Le levage est dans la capacité mais dépasse l'objectif de ${target} %.`,
      allSavedMembersCleared: "Tous les éléments enregistrés ont été effacés.",
      allowAnotherDevice:
        "Autoriser un autre dispositif si aucune configuration du dispositif sélectionné n'est vérifiée.",
      alternateDevice: "Aucune configuration vérifiée avec le dispositif sélectionné.",
      anchorPointsDetail: "Nombre de points d'ancrage",
      angleDiagramAlt: "Plages d'angle de levage du dispositif et du type de levage sélectionnés",
      angleDiagramTitle: "Plages d'angle de levage",
      angleFromHorizontal: "Angle par rapport à l'horizontale",
      angleLegendTitle: "Légende des plages d'angle",
      anglePhotoTitle: "Angle de levage",
      applicableLifts: "Levages concernés",
      automatic: "Automatique",
      backToModeSelection: "Retour à la sélection du mode",
      beamDepth: "Hauteur de poutre",
      beamDepthDetail: "Hauteur de poutre",
      beamOrientationAlt: (orientation: string) => `RAPTOR sur la poutre : ${orientation}`,
      beamSpecifications: (depth: string, width: string) => `hauteur ${depth} × largeur ${width}`,
      beamWidth: "Largeur de poutre",
      beamWidthDetail: "Largeur de poutre",
      buildingPdf: "Création du PDF…",
      capacityTablePreview: "Aperçu de la capacité d'un dispositif de levage",
      capacityUtilization: "Utilisation de la capacité",
      clearSavedMembers: "Effacer les éléments enregistrés",
      clearSavedMembersConfirm:
        "Voulez-vous vraiment effacer tous les éléments enregistrés ? Les levages enregistrés de tous les modes seront supprimés ; cette action est irréversible.",
      clearSavedMembersTitle: "Effacer les éléments enregistrés ?",
      cltPanelDepthDetail: "Épaisseur du panneau CLT",
      cltThickness: "Épaisseur CLT",
      columnSpecifications: (width: string) => `largeur ${width}`,
      columnWidth: "Largeur du poteau",
      columnWidthDetail: "Largeur du poteau",
      completedExcelTemplate: "Modèle Excel complété",
      contactTechSupport: "Contacter l'assistance technique",
      contactUs: "Nous contacter",
      copied: "Copié",
      copyFailed: "Échec de la copie",
      copyTable: "Copier le tableau",
      customLiftDesign: "Conception de levage personnalisée",
      datasheetNoticeText:
        "Consultez la fiche technique de chaque dispositif de levage pour connaître les conditions d'utilisation, les instructions d'installation, les capacités des fixations, les orientations de levage autorisées ainsi que toutes les exigences et limitations applicables.",
      datasheetNoticeTitle: "Cet outil ne remplace pas les fiches techniques applicables.",
      decreaseByEighth: "Réduire de 1/8 de pouce",
      deviceFamilyUnavailable: (name: string) => `${name} — Indisponible`,
      deviceImageAlt: "Dispositif de levage sélectionné",
      devices: "Dispositifs de levage",
      dimensionFormatError: "Saisissez la dimension en pouces, ex. 4 1/8.",
      downloadExcel: "Télécharger l'Excel",
      downloadTemplate: "Télécharger le modèle",
      downloaded: "Téléchargé",
      draftOpened: "Brouillon ouvert",
      eServiceTemplateRequired: "Modèle eService approuvé requis",
      eServiceXlsx: "eService XLSX",
      editingMember: (name: string) => `Modification de ${name}`,
      engineeringReviewRequired: "Vérification technique requise",
      enlargeImage: "Agrandir",
      enterLiftName: "Saisissez un nom de levage.",
      enterNameOrSave:
        "Saisissez un nom d'élément ou sélectionnez Enregistrer le levage pour le nommer dans la fenêtre.",
      enterWeightBeforeSaving: "Saisissez le poids de l'élément levé avant d'enregistrer.",
      enterWeightForRecommendation: "Saisissez le poids de l'élément levé pour obtenir une recommandation.",
      export: "Exporter",
      exportFailed: "Échec de l'export",
      exportFilledExcel: "Exporter l'Excel complété",
      exportHelp: "Choisissez ce qui est envoyé au rapport PDF.",
      exportMemberExcel: "Exporter l'Excel des éléments",
      exportReport: "Exporter le rapport",
      fastenerTakeoff: "Métré des fixations",
      guidanceApplication: "Application",
      guidanceInstallation: (device: string) => `Installation ${device}`,
      guidanceLabel: "Indications d'installation",
      guidanceLoadDirections: "Directions de charge autorisées",
      guidanceMinimumDistances: "Distances minimales",
      guidanceMinimumUnavailable: "Saisissez une largeur valide pour afficher les distances minimales.",
      howRecommendationsSelected: "Comment les recommandations sont choisies",
      increaseByEighth: "Augmenter de 1/8 de pouce",
      installation: "Installation",
      installationIllustrations: "Illustrations de montage",
      installationIllustrationsTitle: "WASP / WASPL - Illustrations de montage",
      installationLine: (installation: string) => `Installation : ${installation}`,
      installationType: "Type d'installation",
      installationTypeColumn: "Type d'installation",
      installationTypes: {
        inclined: "Incliné",
        perpendicular: "Perpendiculaire avec fraisage",
        standard: "Standard",
      },
      issues: {
        angleUnsupported: "Sélectionnez un angle de levage pris en charge. L'angle saisi n'a pas été remplacé.",
        beamDepthMaximum: (maximum: string) => `La hauteur de poutre ne doit pas dépasser ${maximum}.`,
        beamDepthMinimum: (minimum: string) =>
          `La hauteur de poutre doit être d'au moins ${minimum}. La dimension saisie n'a pas été modifiée.`,
        beamDepthPositive:
          "La hauteur de poutre doit être un nombre positif. La dimension saisie n'a pas été modifiée.",
        beamOrientationRestricted:
          "L'orientation RAPTOR sélectionnée est limitée par la largeur de la poutre et le nombre de vis.",
        beamWidthMaximum: (maximum: string) => `La largeur de poutre ne doit pas dépasser ${maximum}.`,
        beamWidthMinimum: (minimum: string) =>
          `La largeur de poutre doit être d'au moins ${minimum}. La largeur saisie n'a pas été modifiée.`,
        beamWidthPositive: "La largeur de poutre doit être un nombre positif. La largeur saisie n'a pas été modifiée.",
        cltEdgeGrainMinimum: (minimum: string) =>
          `L'épaisseur CLT doit être d'au moins ${minimum} pour les levages sur chant et par basculement.`,
        cltFaceGrainMinimum: (minimum: string, device: string) =>
          `L'épaisseur CLT doit être d'au moins ${minimum} pour les levages ${device} sur la face.`,
        cltSlabMaximum: (maximum: string) => `L'épaisseur de la dalle CLT ne doit pas dépasser ${maximum}.`,
        cltThicknessPositive: "L'épaisseur CLT doit être un nombre positif. La dimension saisie n'a pas été modifiée.",
        columnWidthMinimum: (minimum: string) =>
          `La largeur du poteau doit être d'au moins ${minimum}. La dimension saisie n'a pas été modifiée.`,
        columnWidthPositive:
          "La largeur du poteau doit être un nombre positif. La dimension saisie n'a pas été modifiée.",
        memberDepthMaximum: (maximum: string) => `La dimension de l'élément ne doit pas dépasser ${maximum}.`,
      },
      liftName: "Nom du levage",
      liftNamePlaceholder: "Saisissez un nom de levage",
      liftNotOverwritten: "Le levage n'a pas pu être remplacé.",
      liftNotSaved: "Le levage n'a pas pu être enregistré.",
      liftOverwritten: (name: string) => `${name} a été remplacé avec ses propriétés et sa recommandation actuelles.`,
      liftRecommendation: "Recommandation de levage",
      liftSaved: (name: string) => `${name} a été enregistré avec ses propriétés et sa recommandation actuelles.`,
      liftType: "Type de levage",
      liftTypeFallbackNote:
        "Aucune configuration vérifiée n'est disponible pour le basculement. Le levage vertical sur la face est proposé comme alternative ; il ne vérifie pas un basculement.",
      liftedMemberWeight: "Poids de l'élément levé",
      liftingDevice: "Dispositif de levage",
      liftingPoints: "Points de levage",
      liftingSetup: "Configuration de levage",
      liftingSetupValue: (screws: number | string, devices: number | string) =>
        `${screws} vis × ${devices} dispositifs`,
      marginLine: (margin: string) => `Marge : ${margin}`,
      memberLoaded: (name: string) => `${name} a été chargé.`,
      memberName: "Nom de l'élément",
      memberNamePlaceholder: "Saisissez un nom d'élément",
      memberSpecifications: "Caractéristiques de l'élément",
      memberType: "Type d'élément",
      memberTypes: {
        beam: "Poutre",
        "clt-slab": "CLT Slab",
        "clt-wall": "CLT Wall",
        column: "Poteau",
      },
      memberWeight: "Poids de l'élément",
      memberWorkspace: "Espace de travail des éléments",
      minimumDistancesCaption: (mounting: string, screwCount: number | string) =>
        `${mounting} · Vis par dispositif : ${screwCount}`,
      mode1Description: "Configurez et vérifiez un levage précis.",
      mode1Pill: "Mode 1",
      mode2Description: "Saisissez un élément pour trouver des options de levage.",
      mode2Eyebrow: "Mode 2 : Recommandation de levage",
      mode2Pill: "Mode 2",
      mode3Description: "Importez plusieurs éléments pour obtenir des recommandations de levage.",
      mode3Pill: "Mode 3",
      modeSelection: "Sélection du mode",
      moduleTitle: "US Lifting Calculator",
      nameInUse: (name: string) =>
        `Un levage enregistré s’appelle déjà « ${name} ». Remplacez-le ou saisissez un autre nom.`,
      newMember: "Nouvel élément",
      noCapacitySubtitle: "La combinaison sélectionnée ne figure pas dans les tableaux récapitulatifs intégrés.",
      noCapacityTable: "Aucune table de capacité n'est indiquée pour cette configuration.",
      noSavedLifts: "Aucun levage enregistré n'est disponible.",
      noSavedMembers: "Aucun élément enregistré",
      noSavedMembersToProcess: "Enregistrez d'abord un élément en Mode 1 ou 2.",
      noSavedRecommendationLifts: "Aucun levage du Mode 2 n'est enregistré.",
      noScrews: (filters: string[]) => `Aucune vis ${filters.join(" et ")}`,
      noScrewsListed: "Aucune vis indiquée",
      notAvailableYet: "Disponible dans une version ultérieure.",
      numberOfAnchorPoints: "Nombre de points d'ancrage",
      numberOfLiftingDevices: "Nombre de dispositifs de levage",
      numberOfRaptors: "Nombre de RAPTOR",
      openEmailEnquiry: "Ouvrir une demande par e-mail",
      openInMode1: "Ouvrir en Mode 1",
      openInMode2: "Ouvrir en Mode 2",
      openOrCreateMember: "Ouvrir ou créer un élément",
      openSavedSelect:
        "Ouvrez chaque levage enregistré, sélectionnez une option à capacité vérifiée pour le métré et enregistrez-le à nouveau.",
      openSupportMenu: "Ouvrir le menu d'assistance",
      optionAlternativeTitle: (slot: number, label: string) => `Option ${slot} - ${label} alternative`,
      optionTitle: (slot: number, label: string) => `Option ${slot} - ${label}`,
      orientation: "Orientation",
      orientationUnavailable: (orientation: string) => `${orientation} - Indisponible pour cette largeur de poutre`,
      overwriteExisting: "Remplacer l'existant",
      overwritten: "Remplacé",
      parallel: "Parallèle",
      parallelMounting: "Montage parallèle",
      perpendicular: "Perpendiculaire",
      perpendicularMounting: "Montage perpendiculaire",
      perpendicularWithMillingShort: "Perp. avec fraisage",
      printCurrentScreen: "Imprimer l'écran actuel",
      printCurrentScreenHelp: "Le levage affiché, avec son résultat et ses images de référence.",
      printPdf: "Imprimer en PDF",
      printSavedMembers: "Imprimer les éléments enregistrés",
      printSavedMembersHelp:
        "Chaque levage enregistré est imprimé sur sa propre page, suivi d'un métré des fixations combiné lorsque des levages du Mode 2 sont disponibles.",
      processList: "Traiter la liste",
      processSavedMembers: "Traiter la liste des éléments enregistrés",
      productCode: "Code produit",
      productPage: "Page produit",
      properties: "Propriétés",
      quantityRequired: "Quantité requise",
      raptors: "RAPTOR",
      readyToSave: "La recommandation actuelle peut être enregistrée.",
      reasons: {
        noAdditionalVgspl: "Aucun autre nombre ou longueur de vis VGSPL vérifié n'est disponible avec ces réglages.",
        noVgsplAlternative: "Aucune alternative VGSPL vérifiée n'est disponible avec ces réglages.",
        notEligible: (family: string) =>
          `Aucune configuration ${family} admissible n'est disponible avec ces réglages.`,
        notWithinCapacity: (family: string) =>
          `Aucune configuration ${family} n'est dans la capacité avec ces réglages.`,
        waspNoAlternative: (series: string) =>
          `Aucune alternative ${series} vérifiée n'est disponible avec WASP avec ces réglages.`,
        waspNotWithinCapacity: (series: string) =>
          `Aucune configuration ${series} n'est dans la capacité avec ces réglages.`,
      },
      recommendedConfigurations: "Configurations recommandées",
      recommendedLiftConfiguration: "Configuration de levage recommandée",
      requiredFields: (fields: string) => `requis : ${fields}`,
      reset: "Réinitialiser",
      result: "Résultat",
      review: {
        allSupportedDevices: "Tous les dispositifs pris en charge",
        determinism:
          "Il s'agit d'une recherche déterministe dans des tables, pas d'une IA en direct. Les sélecteurs affichent des angles tabulés distincts ; les schémas de plages restent des références. Cette mise à jour ne vaut pas approbation technique.",
        deviceScope: "Dispositifs inclus dans cette revue",
        methodChecks:
          "Vérifier dimensions, orientation, angle tabulé, longueur et nombre de vis avant le classement. Des données obligatoires manquantes empêchent la vérification.",
        methodRaptor:
          "Les levages RAPTOR de poutres et sur la face comparent VGSPL, une alternative VGSPL avec un autre nombre ou une autre longueur de vis, et HBSPL. Les levages RAPTOR MINI et les levages RAPTOR sur chant ou par basculement comparent uniquement VGSPL et HBSPL.",
        methodWasp: "Si autorisé : WASP → WASPL → RAPTOR MINI → RAPTOR.",
        raptorNote: "Les images d'application et de distances minimales RAPTOR sont affichées avec les résultats.",
        raptorOnly: "RAPTOR uniquement",
        scopeNote: "Le périmètre RAPTOR limite la sélection et la recherche, sans constituer une validation.",
      },
      reviewSettings: "Recommandations et réglages de revue",
      riggingBasis:
        "Capacité totale d'élingage = nombre de points d'ancrage x capacité d'un dispositif à la borne inférieure, la plus prudente, de la plage d'angle sélectionnée (ou exactement 90 degrés). Une répartition égale de la charge est supposée, mais n'est pas confirmée par cet outil.",
      riggingMemo:
        "Cette orientation n'est pas approuvée par la fiche technique. Des applications avec plus de points d'ancrage que les configurations publiées sont possibles si elles sont conçues par un ingénieur en élingage qualifié externe.",
      saveLift: "Enregistrer le levage",
      saveMember: "Enregistrer l'élément",
      saveMode1Lift: "Enregistrer le levage du Mode 1",
      saveMode2Lift: "Enregistrer le levage du Mode 2",
      saved: "Enregistré",
      savedLiftConfigurations: "Configurations de levage enregistrées",
      savedLiftCount: (count: number) => (count === 1 ? "1 levage enregistré" : `${count} levages enregistrés`),
      savedLiftLoaded:
        "Levage enregistré chargé. Modifiez une propriété et sélectionnez Enregistrer le levage pour le mettre à jour.",
      savedLifts: "Levages enregistrés",
      savedLiftsEyebrow: "Levages enregistrés",
      savedLiftsFileSuffix: "levages-enregistres",
      savedMembers: "Éléments enregistrés",
      savedMembersAvailable: (count: number) => `Éléments enregistrés disponibles : ${count}`,
      savedMembersDescription:
        "Générez des recommandations depuis les propriétés enregistrées sans importer Excel. Les levages enregistrés restent inchangés.",
      savedOn: (mode: string, date: string) => `${mode} · Enregistré le ${date}`,
      savedPill: "Enregistrés",
      savedRecommendations: "Recommandations enregistrées",
      savedResult: "Résultat enregistré",
      screw: "Vis",
      screwCountBeamWidth: (count: number) => `${count} - Indisponible pour cette largeur de poutre`,
      screwCountNotListed: (count: number) => `${count} — non indiqué`,
      screwLength: "Longueur de vis (in)",
      screwLengthOption: (length: string, code: string) => `${length} — ${code}`,
      screwLengthPhotoAlt: "Longueur de vis par rapport à l'épaisseur de l'élément",
      screwLengthReferenceText:
        "Pour les applications de levage dont la composante de charge est perpendiculaire au fil du bois, afin d'atteindre la capacité maximale du système de levage, il est recommandé que la longueur de vis ancrée dans le bois, hors plaque RAPTOR de 10 mm, dépasse 0,7·d (L - 10 mm > 0,7·d ; d = épaisseur de l'élément en bois) afin d'éviter la fissuration. Si ce rapport n'est pas respecté, les vérifications critiques au cisaillement et à la déformation peuvent être effectuées selon l'AWC-NDS, chapitres 3 et 5.",
      screwLengthReferenceTitle: "Influence du rapport entre la longueur de vis et l'épaisseur de l'élément",
      screwLengthViewerTitle: "Référence de longueur de vis",
      screwType: "Type de vis",
      screwsAtLeast: (minimum: string) => `d'au moins ${minimum}`,
      screwsColumn: "Nb de vis",
      screwsLongerThan: (minimum: string) => `de plus de ${minimum}`,
      screwsPerDevice: "Vis par dispositif",
      screwsPerDeviceDetail: "Vis par dispositif",
      screwsPerRaptor: "Vis par RAPTOR",
      screwsPerRaptorFigure: (count: number) => `${count} vis par RAPTOR`,
      screwsPerRaptorFigureAlt: (count: number) => `RAPTOR installé avec ${count} vis`,
      screwsRow: (count: number) => `${count} vis`,
      screwsShorterThan: (maximum: string) => `de moins de ${maximum}`,
      selectCompletedTemplate: "Sélectionnez un modèle complété pour commencer.",
      selectListedScrew: "Sélectionnez une configuration de vis listée avant d'enregistrer.",
      selectNewMember: "Sélectionnez Nouvel élément pour créer un levage enregistré, ou ouvrez un élément enregistré.",
      selectedConfiguration: "Configuration sélectionnée",
      shortScrewEmbedment: "La fixation actuelle ne pénètre pas de 0,7*d",
      shortScrewRatio: (length: string) =>
        `La vis sélectionnée est plus courte que la longueur recommandée de 0,7d (${length}). Effectuez les vérifications critiques au cisaillement et à la déformation selon l'AWC-NDS, chapitres 3 et 5. Ces vérifications ne sont pas réalisées par cet outil.`,
      specificGravity: "Densité relative",
      statusAboveTarget: "Vérifié - au-dessus de l'utilisation cible",
      statusAlternativeLift: "Capacité vérifiée - levage alternatif",
      statusDimension: "Dimension hors plage",
      statusEngineeringReview: "Vérifié — Revue d'ingénierie requise",
      statusNoCapacity: "Capacité non indiquée",
      statusNotVerified: "Non vérifié",
      statusOrientation: "Orientation indisponible",
      statusPending: "En attente",
      statusReview: "À vérifier",
      statusUnavailable: "Indisponible",
      statusVerified: "Vérifié",
      submitFeedback: "Envoyer un commentaire",
      takeoffEmailBasis: "Base de calcul : USA - NDS",
      takeoffEmailIncluded: (count: number) => `Levages enregistrés du Mode 2 inclus : ${count}`,
      takeoffEmailRequest: "Merci de vérifier la disponibilité et les prix des quantités indiquées.",
      takeoffEmailSubject: "US Lifting Calculator - métré des fixations",
      takeoffEmailTotal: (total: string) => `Total des fixations : ${total}`,
      takeoffEmailUnits: "Unités : impériales (lb / in)",
      takeoffLength: "Longueur",
      takeoffTotal: "TOTAL",
      targetUtilization: "Définir l'utilisation cible (%)",
      targetUtilizationShort: "Utilisation cible",
      techSupportSubject: "US Lifting Calculator - assistance technique",
      template: "Modèle",
      templateDownloaded: (device: string) => `Modèle ${device} téléchargé en unités impériales (lb / in).`,
      totalCapacity: "Capacité totale",
      totalFasteners: "Total des fixations",
      totalRiggingCapacity: "Capacité totale d'élingage",
      totalScrews: "Vis au total",
      unitIn: "in",
      unitLb: "lb",
      upload: {
        columns: {
          anchors: "Nb d'ancrages",
          angle: "Angle",
          liftingDevice: "Dispositif de levage",
          memberName: "Nom de l'élément",
          memberType: "Type d'élément",
          screw: "Vis",
          screwCount: "Nb de vis",
          status: "Statut",
          totalCapacity: "Capacité de levage totale",
          totalScrews: "Nombre total de vis",
          utilization: "Utilisation",
          weight: "Poids",
        },
        defaultAngle: "(par défaut)",
        enteredTableReference: "Angle saisi → référence du tableau",
        externalRigging: "Conception d'élingage externe requise",
        faceGrainAlternative: "Alternative verticale sur la face ; ne vérifie pas le basculement.",
        fields: {
          angle: "Angle de levage",
          columnNotWithWasp: "Le poteau n'est pas disponible avec WASP / WASPL",
          columnRequiresRaptorMini: "Le poteau requiert RAPTOR MINI",
          device: "Dispositif de levage",
          installationType: "Type d'installation",
          liftType: "Type de levage",
          memberType: "Type d'élément",
          thicknessDepth: "Épaisseur / hauteur",
          weight: "Poids",
        },
        firstRows: (shown: number, total: number) =>
          `Affichage des ${shown} premiers éléments sur ${total}. L'Excel complété les contient tous.`,
        issues: {
          angleColumn: "Le levage des poteaux requiert exactement 90 degrés par rapport à l'horizontale.",
          angleNotNumeric:
            "L'angle de levage doit être un angle numérique par rapport à l'horizontale ou une plage affichée pour le dispositif sélectionné.",
          angleOutOfRange: (minimum: number) =>
            `L'angle de levage doit être compris entre ${minimum} et 90 degrés par rapport à l'horizontale pour le dispositif sélectionné. L'angle saisi n'a pas été modifié.`,
          angleOutsideBand:
            "L'angle d'élingue saisi est hors de la plage sélectionnée du tableau. Ressaisissez et recalculez l'angle réel ; un autre angle d'élingage n'est pas vérifié.",
          angleRiggingMismatch:
            "L'angle proposé ne correspond pas à l'élingage saisi. Corrigez la saisie avant d'utiliser un résultat.",
          beamWidthRequired: "La largeur de poutre est requise pour les lignes de poutres.",
          materialConflict: (text: string) =>
            `Le nom du matériau et la densité relative se contredisent dans "${text}". Confirmez le matériau prévu ; aucune valeur n'a été substituée.`,
          materialUnrecognized: (text: string) =>
            `Matériau bois non reconnu "${text}". Utilisez un matériau de la liste, DF, SYP ou une densité relative numérique prise en charge.`,
          materialUnsupported: (text: string, choices: string) =>
            `Densité relative non prise en charge "${text}". Choisissez ${choices} ; la valeur saisie n'a pas été remplacée.`,
          orientationInvalid: "L'orientation doit être Parallèle ou Perpendiculaire.",
          orientationRestricted: "L'orientation RAPTOR demandée est limitée par la largeur de la poutre.",
          processingFailed: "Cet élément n'a pas pu être traité.",
        },
        missing: (fields: string) => `Manquant ou invalide : ${fields}.`,
        notices: {
          andMore: (names: string, more: number) => `${names} et ${more} autres`,
          anglesMapped: (count: number) =>
            `${count === 1 ? "1 angle a été associé" : `${count} angles ont été associés`} aux plages du tableau : la valeur inférieure de la plage est la référence du tableau. L'angle d'élingue saisi est inchangé ; il doit rester dans cette plage.`,
          appliesTo: (names: string) => `S'applique à : ${names}.`,
          materialDefault: (value: string, names: string) =>
            `G = ${value} a été utilisé pour les matériaux vides : ${names}. Confirmez le matériau bois.`,
          vgs13: "Les vis VGS13 et leurs charges de service concernent uniquement WASPL.",
        },
        processed: (members: number, verified: number, saved: number) =>
          `${members} ${members === 1 ? "élément traité" : "éléments traités"}. ${verified} avec des recommandations vérifiées. ${saved} enregistrés comme éléments.`,
        processedResults: "Résultats traités",
        processedSaved: (members: number, verified: number) =>
          `${members} éléments enregistrés traités ; ${verified} avec des recommandations vérifiées. Les levages enregistrés restent inchangés.`,
        processing: "Traitement du classeur...",
        processingSaved: "Traitement des éléments enregistrés...",
        requestedUsed: "Demandé → Utilisé",
        unverified: "Non vérifié",
        verified: "Vérifié",
        wasplOnlyShort: "WASPL uniquement",
        workbookExported: "Excel complété exporté.",
      },
      uploadFilledTemplate: "Importer le modèle complété",
      uploadList: "Importer une liste pour recommandations",
      uploadListEyebrow: "Importer une liste",
      uploadListFileSuffix: "liste-importee",
      uploadMemberList: "Importer une liste d'éléments",
      useForTakeoff: "Utiliser cette option pour le métré des levages enregistrés",
      utilization: "Utilisation",
      verifiedRecommendationRequired: "Une recommandation à capacité vérifiée est requise pour enregistrer ce levage.",
      viewSavedLifts: "Voir les levages enregistrés",
      wallPanelLiftType: "Type de levage du panneau mural",
      waspOnly: "WASP uniquement - cette configuration n'est pas autorisée avec WASPL.",
      wasplOnly: "WASPL uniquement - cette configuration n'est pas autorisée avec WASP.",
      weight: "Poids",
      weightLabel: "Poids de l'élément levé",
      weightPlaceholder: "Saisissez le poids, ex. 2500",
      woodSpecificGravity: "Type de bois / densité relative",
      woodSpecificGravityShort: "Bois / densité relative",
    },
    generalPrinciples: {
      title: (device: string) => `${device} — Principes généraux`,
      introRaptor:
        "Extrait de la fiche technique RAPTOR fournie. Ces principes sont propres à RAPTOR ; pour les autres dispositifs de levage, consulter la fiche technique correspondante.",
      introRaptorMini: "Extrait de la fiche technique RAPTOR MINI fournie. Ces principes sont propres à RAPTOR MINI.",
      introWasp: "Extrait de la fiche technique WASP / WASPL fournie. Ces principes s'appliquent à WASP comme à WASPL.",
      fastenerLengthPlate:
        "La longueur du connecteur doit être choisie à chaque fois en fonction des dimensions de l'élément en bois, de la position du connecteur, de l'angle de levage, du poids de la charge à lever et de la disposition de la plaque de levage. Dans tous les cas, il est recommandé que les connecteurs aient une longueur supérieure au minimum requis, mais telle que la pointe ne dépasse pas de l'élément à lever.",
      fastenerLength:
        "La longueur du connecteur doit être choisie à chaque fois en fonction des dimensions de l'élément en bois, de la position du connecteur, de l'angle de levage et du poids de la charge à lever. Dans tous les cas, il est recommandé que les connecteurs aient une longueur supérieure au minimum requis, mais telle que la pointe ne dépasse pas de l'élément à lever.",
      wllBasis:
        "Les valeurs de capacité de levage (WLL) indiquées reposent sur des calculs effectués selon l'ICC-ES Evaluation Report ESR-4645, l'AWC-NDS 2018, l'AWC Technical Report 12 et sur les résultats des essais réalisés. La documentation source indique un coefficient de sécurité égal ou supérieur à 5,0. Les exigences applicables de la ",
      oshaLink: "section 1926.251 de l'OSHA",
      wllBasisAfterLink:
        " doivent être confirmées pour l'opération de levage concernée. Ce calculateur n'établit ni la conformité OSHA ni l'approbation de l'ensemble du dispositif d'élingage.",
      specificGravityBasis: (maxCapacity: string) =>
        `Une densité relative G = 0,42 a été retenue comme base de calcul. Pour les essences de bois de densité relative différente, le tableau de conversion suivant avec les facteurs de modification a été utilisé. Les valeurs ne dépassent jamais la capacité maximale de la plaque de ${maxCapacity}.`,
      specificGravityBasisWasp: (waspCapacity: string, wasplCapacity: string) =>
        `Une densité relative G = 0,42 a été retenue comme base de calcul. Pour les essences de bois de densité relative différente, le tableau de conversion suivant avec les facteurs de modification a été utilisé. Les valeurs ne dépassent jamais la capacité maximale de ${waspCapacity} pour le WASP et de ${wasplCapacity} pour le WASPL.`,
      capacityFactors:
        "Toutes les valeurs de capacité des vis sont ajustées par un facteur d'accélération dynamique de 1/1,1, un facteur de durée de charge CD = 1,25, un facteur de température Ct = 1,0 et un facteur d'humidité CM = 1,0, et supposent que les vis sont posées sur chantier juste avant le levage.",
      brittleFailureElement:
        "Les vérifications d'éventuelles réductions de résistance dues à une rupture fragile liée à la géométrie de l'élément levé doivent être effectuées séparément selon l'AWC-NDS, en particulier lorsque le connecteur engage moins de 70 % de la hauteur de l'élément levé.",
      brittleFailureBeam:
        "Les vérifications d'éventuelles réductions de résistance dues à une rupture fragile liée à la géométrie de l'élément levé doivent être effectuées séparément selon l'AWC-NDS, en particulier lorsque le connecteur engage moins de 70 % de la hauteur de la poutre.",
      loadToGrain:
        "Tous les calculs CLT supposent un angle entre la charge et le fil de 90°, quelle que soit l'orientation, sans réduction de la longueur du connecteur pour le levage sur chant.",
      otherConfigurationsPlate:
        "Pour le calcul de la capacité de levage de la plaque dans des configurations de pose autres que celles indiquées ici, contacter le service technique Rothoblaas.",
      otherConfigurations:
        "Pour le calcul de la capacité de levage dans des configurations de pose autres que celles indiquées ici, contacter le service technique Rothoblaas.",
      fatigue: (device: string) =>
        `Pour respecter les limites de fatigue de la Service Class 0 de l'ASME BTH01-2023, aucun dispositif de levage ${device} ne doit être utilisé pour plus de 20 000 cycles de charge.`,
      noPreDrilling: (specificGravity: string) =>
        `Les valeurs de capacité de levage indiquées sont calculées pour la plaque fixée avec des vis posées sans pré-perçage, conformément à l'ICC Evaluation Report ESR-4645. Toutes les distances minimales doivent être augmentées pour des densités relatives supérieures à ${specificGravity}.`,
      installation:
        "Les connecteurs doivent être posés conformément à leurs instructions de pose afin d'éviter tout dommage et de conserver toutes leurs performances selon leurs paramètres de résistance de calcul.",
      noImpactWrenches:
        "L'utilisation de visseuses à percussion/clés à chocs n'est pas autorisée. Respecter l'angle d'insertion à l'aide d'un avant-trou et/ou d'un gabarit de pose. Éviter la flexion. Assurer un serrage correct. Nous recommandons l'utilisation de visseuses à couple contrôlé, par ex. avec TORQUE LIMITER. Sinon, serrer avec une clé dynamométrique.",
      hbsPlateValues:
        "Les valeurs indiquées dans la fiche technique pour la plaque de transport fixée avec des vis HBS PLATE ont été calculées en tenant compte de la géométrie et des paramètres mécaniques de la version HBS PL.",
      hbsPContact:
        "Pour les capacités portantes de la plaque de transport fixée avec la vis HBS P, contacter le service technique pour plus d'assistance.",
      singleUse: (screws: string) =>
        `${screws} ne peuvent être utilisées qu'une seule fois. Une fois serrées et chargées, les vis ne doivent pas être desserrées puis réutilisées pour fixer la plaque de transport. Dès que l'élément en bois à transporter a été levé à sa position finale et que la plaque de transport n'est plus nécessaire à cet effet, les vis doivent être dévissées et éliminées correctement.`,
      screwsHbsPlateVgs: "Les vis HBS PLATE et VGS",
      screwsVgs: "Les vis VGS",
      vgsPlateReuse: (device: string) =>
        `VGS PLATE est le seul connecteur réutilisable. Pour l'utilisation avec le dispositif ${device}, se reporter aux fiches techniques et aux notices de VGS PLATE et du JIG REUSE correspondant.`,
      qualifiedPersonnelPlate:
        "La plaque de levage ne doit être utilisée que par du personnel qualifié. Le manuel d'utilisation (fourni sous forme de code QR sur le produit et disponible sur www.rothoblaas.com) doit être lu et compris avant utilisation. Les informations et instructions qu'il contient doivent être respectées. En cas de doute, contacter le service technique Rothoblaas avant utilisation.",
      qualifiedPersonnelDevice:
        "Le dispositif de levage ne doit être utilisé que par du personnel qualifié. Le manuel d'utilisation (fourni sous forme de code QR sur le produit et disponible sur www.rothoblaas.com) doit être lu et compris avant utilisation. Les informations et instructions qu'il contient doivent être respectées. En cas de doute, contacter le service technique Rothoblaas avant utilisation.",
      specificGravityColumn: "Densité relative [G]",
      modificationFactorColumn: "Facteur de modification",
    },
    admin: {
      family: "Famille",
      devicesAndScrews: "Dispositifs et vis",
      lifts: "Levages",
      anchorCount: "Nombre d'ancrages",
      anchorCounts: "Nombres d'ancrages",
      angle: "Angle",
      capacities: "Capacités",
      capacity: "Capacité",
      capacityFactor: "Facteur de capacité",
      capacityLb: "Capacité [lb]",
      device: "Dispositif",
      deviceFamilies: "Familles de dispositifs",
      deviceFamily: "Famille de dispositifs",
      deviceFamilyLiftType: "Type de levage de la famille",
      deviceFamilyLiftTypes: "Limites de dimensions",
      deviceScrewMappings: "Associations dispositifs-vis",
      deviceScrewMapping: "Association dispositif-vis",
      devices: "Dispositifs",
      diameter: "Diamètre",
      installationType: "Type d'installation",
      length: "Longueur",
      liftType: "Type de levage",
      liftTypes: "Types",
      maxCapacity: "Capacité maximale",
      maxMemberDepth: "Hauteur maximale de l'élément",
      maxMemberWidth: "Largeur maximale de l'élément",
      memberType: "Type d'élément",
      minMemberDepth: "Hauteur minimale de l'élément",
      minMemberWidth: "Largeur minimale de l'élément",
      plateThickness: "Épaisseur de la plaque",
      productUrl: "URL du produit",
      requiresRiggingReview: "Nécessite une vérification de l'élingage",
      screw: "Vis",
      screwCount: "Vis par dispositif",
      screwFamilies: "Familles de vis",
      screwFamily: "Famille de vis",
      screws: "Vis",
      specificGravities: "Densités relatives",
      specificGravity: "Densité relative",
      washer: "Rondelle",
    },
    errors: {
      calculationInvalidInput: "Le calcul contient une valeur non valide.",
      calculationNotAvailable: "Une valeur sélectionnée n'est plus disponible. Vérifiez la sélection.",
      capacityAlreadyExists:
        "Une capacité avec le même dispositif, la même vis, le même type de levage, le même type d'installation, le même nombre de vis par dispositif et le même angle existe déjà.",
      capacityNotFound: "La capacité est introuvable.",
      deviceAlreadyExists: "L'article est déjà un dispositif.",
      deviceFamilyNameAlreadyExists: "Une famille de dispositifs avec ce nom existe déjà.",
      deviceFamilyLiftTypeAlreadyExists: "La famille de dispositifs a déjà ce type de levage.",
      deviceFamilyLiftTypeInvalidLimits:
        "Les limites de l'élément doivent être supérieures à zéro, et chaque maximum non inférieur à son minimum.",
      deviceFamilyLiftTypeNotFound: "Le type de levage de la famille est introuvable.",
      deviceScrewMappingAlreadyExists: "La famille de dispositifs a déjà cette famille de vis.",
      deviceScrewMappingNotFound: "La famille de vis de la famille de dispositifs est introuvable.",
      deviceInUseByLift:
        "Le dispositif est utilisé dans des levages enregistrés : définissez-le comme Caché au lieu de le supprimer.",
      deviceNotFound: "Le dispositif est introuvable.",
      liftTypeAnchorCountAlreadyExists: "Le type de levage a déjà ce nombre d'ancrages.",
      liftTypeAnchorCountNotFound: "Le nombre d'ancrages est introuvable.",
      screwAlreadyExists: "L'article est déjà une vis.",
      screwInUseByLift:
        "La vis est utilisée dans des levages enregistrés : définissez-la comme Cachée au lieu de la supprimer.",
      screwNotFound: "La vis est introuvable.",
      specificGravityAlreadyExists: "Une densité relative avec cette valeur existe déjà.",
      specificGravityInUseByLift:
        "La densité relative est utilisée dans des levages enregistrés : définissez-la comme Cachée au lieu de la supprimer.",
      specificGravityNotFound: "La densité relative est introuvable.",
      uploadFileTooLarge: "Le fichier dépasse 10 Mo.",
      uploadInvalidWorkbook: "Le fichier importé n'est pas un classeur .xlsx valide.",
      uploadMetricTemplate:
        "Utilisez le modèle USA - NDS avec les poids en lb et les dimensions en pouces. Les modèles métriques ou européens ne sont pas acceptés.",
      uploadMissingColumns: "Il manque des colonnes obligatoires dans le modèle.",
      uploadNoHeader: "Le classeur doit contenir une ligne d'en-tête avec Member Name et Member Type.",
      uploadNoRows: "Aucune ligne d'élément n'a été trouvée.",
      uploadNoWorksheet: "Aucune feuille de calcul n'a été trouvée dans le classeur importé.",
    },
  },

  deckDesignerModule: {
    configurations: {
      singular: "Configuration",
      plural: "Configurations",
      errors: {
        connectorFamilyNotAvailable:
          "La famille de connecteurs choisie n'est plus disponible : choisissez-en une autre.",
        connectorNotAvailable: "Le connecteur choisi n'est plus disponible : choisissez-en un autre.",
        screwNotAvailable: "Une vis choisie n'est plus disponible : choisissez-en une autre.",
        supportFamilyNotAvailable: "La famille de supports choisie n'est plus disponible : choisissez-en une autre.",
        supportNotAvailable: "Le support choisi n'est plus disponible : choisissez-en un autre.",
        substructureNotAvailable: "La sous-structure choisie n'est plus disponible : choisissez-en une autre.",
        invalidTotalHeight: "Hauteur maximale extrados terrasse (H) n'est pas valide pour la composition sélectionnée.",
        noSupportForRequiredHeightRange: (min: number, max: number) =>
          `Avec la configuration actuelle, le support doit couvrir une hauteur (h) ${min === max ? `de ${min} mm` : `de ${min} à ${max} mm`} : aucun support disponible ne couvre cette plage. Modifiez H ou la pente.`,
        noSupportForSpacing:
          "L'entraxe des supports le long des lambourdes est trop faible pour les supports disponibles (au moins 1,5 fois leur largeur est nécessaire). Réduisez l'entraxe des lambourdes.",
        noSupportFamilyOptions: "Aucune option disponible avec la configuration actuelle.",
        noSupportForSlopeDrop: {
          title: "Aucun support disponible ne peut couvrir le dénivelé du terrain, quelle que soit H.",
          requiredRange: (min: number, max: number) =>
            `Hauteur de support nécessaire (h) : de ${min} à ${max} mm (dénivelé ${max - min} mm)`,
          widestSupport: (supportCode: string, rangeMm: number) =>
            `Dénivelé maximal couvert par un support : ${rangeMm} mm (${supportCode})`,
          fix: (maxSlope: number | null) =>
            `${maxSlope != null ? `Réduisez la pente à ${maxSlope}° maximum` : "Réduisez la pente"}, puis choisissez H parmi les valeurs compatibles.`,
        },
        noCompatibleConnectorSubstructureScrew:
          "Aucune vis compatible avec le connecteur et la sous-structure sélectionnés.",
        noCompatibleSupportScrew: "Aucune vis compatible avec le support sélectionné.",
        noCompatibleConnectorBoardScrew: "Aucune vis compatible avec l'épaisseur de lame sélectionnée.",
        noCompatibleConnectorFamilyMapping:
          "Aucune famille de connecteurs n'est compatible avec la sous-structure, le type de fixation, le type de rainure et l'écart sélectionnés.",
        noCompatibleConnectorFamilyBoardThickness:
          "Aucune famille de connecteurs compatible ne correspond à l'épaisseur de planche actuelle.",
        noCompatibleConnectorFamilyBoardThicknessAndGroovingHeight:
          "Aucune famille de connecteurs compatible ne correspond à l'épaisseur de planche et à la hauteur de rainure actuelles.",
        noCompatibleConnector: "Aucun connecteur n'est compatible avec la configuration actuelle.",
        noCompatibleSupportFamilyMapping:
          "Aucune famille de supports n'est compatible avec la classe de matériau de sous-structure sélectionnée.",
      },
      calculation: {
        shapeAndGeometricData: "Forme et dimensions",
        completeFieldsToContinue: "Remplissez tous les champs de cette page pour continuer",
        showBillOfQuantities: "Afficher la nomenclature",
        boards: "Lames",
        billOfQuantities: "Bordereau des quantités",
        downloadPdf: "Télécharger le PDF",
        downloadPdfDescription:
          "Téléchargez le PDF avec le récapitulatif complet de la configuration : données géométriques, lames, sous-structure, supports, vues de la terrasse, réactions aux appuis et liste des produits.",
        downloadPdfDisabledHint: "Complétez la configuration pour télécharger le PDF.",
        downloadPdfDisabledBoardFitHint:
          "Résolvez l'avertissement sur la largeur des lames et le joint pour télécharger le PDF.",
        billOfQuantitiesEmpty: "Sélectionnez les données de la terrasse pour calculer le bordereau des quantités.",
        calculationDetailsEmpty: "Sélectionnez les données de la terrasse pour afficher les détails du calcul.",
        showCompatibleValues: "Voir les valeurs compatibles",
        stratigraphySummaryTitle: "Stratigraphie actuelle",
        availableHeightForConnectorAndSupport: "Hauteur disponible pour le connecteur et le support",
        connectorThickness: "Épaisseur du connecteur",
        remainingHeightForSupport: "Hauteur restante pour le support",
        billOfQuantitiesProduct: "Produit",
        billOfQuantitiesRequiredQuantity: "Quantité nécessaire",
        billOfQuantitiesQuantityToOrder: "Quantité à commander",
        calculationDetails: "Détails du calcul",
        deckArea: "Surface de terrasse",
        totalBoards: "Lames",
        joists: "Lambourdes",
        joistsLegend: "Lambourdes de sous-structure",
        layoutCalculation: "Calcul du calepinage",
        longJoists: "Lambourdes longues",
        shortJoists: "Lambourdes courtes",
        longJoist: "Lambourde longue",
        shortJoist: "Lambourde courte",
        supportCalculation: "Calcul des supports",
        joistsSpacing: "Entraxe des lambourdes",
        maxSupportSpacing: "Entraxe max. des supports",
        supportsPerLongJoist: "Supports par lambourde longue",
        supportsPerShortJoist: "Supports par lambourde courte",
        supportSpacing: "Entraxe des supports",
        supportsCount: "Nombre de supports",
        lShaped: "Forme en L",
        horizontal: "Horizontal",
        vertical: "Vertical",
        rectangular: "Forme rectangulaire",
        boardDirection: "Sens des lames",
        fasteningType: "Type de fixation",
        hiddenFastening: "Invisible",
        hiddenFasteningDescription:
          "Les lames sont fixées sans vis apparentes, à l'aide de connecteurs ou de clips cachés.",
        visibleFastening: "Visible",
        visibleFasteningDescription: "Les lames sont fixées avec des vis visibles à la surface de la terrasse.",
        groovingType: "Type de rainure",
        smoothGrooving: "Lisse",
        groovedGrooving: "Rainurée",
        maxBase: "Largeur max. (L1)",
        maxHeight: "Longueur max. (L2)",
        substructureSection: (unit: string) => `Section de l'élément bs x hs [${unit}]`,
        secondaryBase: "Largeur secondaire (l1)",
        secondaryHeight: "Longueur secondaire (l2)",
        totalHeight: "Hauteur maximale extrados terrasse (H)",
        slope: "Pente",
        slopeDirectionTitle: "Pente du terrain",
        slopeDirectionHint:
          "Faites glisser sur le dessin pour tracer la direction dans laquelle le terrain descend. Sans direction, le terrain reste plat.",
        slopeIncompleteWarning:
          "La pente et sa direction sont toutes deux nécessaires : les modifications de la pente ne seront pas enregistrées. Fermer quand même ?",
        totalHeightTooLittle: (height: string) =>
          `La composition sélectionnée exige une hauteur totale d'au moins ${height}.`,
        secondaryItemDoesNotFitTotalHeight: (code: string) =>
          `L'activation de ${code} rendrait la famille de connecteurs sélectionnée incompatible avec la hauteur totale (H).`,
        totalHeightIncompatible: (minHeight: string, maxHeight: string) =>
          `La hauteur totale pour cette composition doit être comprise entre ${minHeight} et ${maxHeight}.`,
        planeInteractionHint: "Vous pouvez dessiner et modifier la terrasse directement ici.",
        planeInteractionStart: "Commencer à dessiner",
        // the button of the tutorial reopened from its icon
        planeInteractionContinue: "Continuer à dessiner",
        showPlaneInteractionHint: "Afficher le tutoriel",
        planeInteractionFeatureDraw: "Cliquez sur le plan pour tracer le périmètre de la terrasse.",
        planeInteractionFeatureLength:
          "Après avoir sélectionné un point, saisissez la longueur du côté (Retour arrière pour la corriger) et appuyez sur Entrée pour la confirmer.",
        planeInteractionFeatureDrag: "Faites glisser les côtés de la terrasse pour en modifier les dimensions.",
        planeInteractionFeatureEditLabel: "Cliquez sur une étiquette pour saisir la dimension souhaitée.",
        planeInteractionFeatureDifferentBoards:
          "Faites glisser les lames de largeur différente, si présentes, d'un côté à l'autre.",
        planeInteractionFeatureClear:
          "Utilisez l'icône de la corbeille pour effacer les points et recommencer le dessin.",
        liveLoad: "Charge d'exploitation",
        deflection: "Flèche limite (EN1995-1-1 §7 / EN1999-1-1 §4)",
        gap: "Largeur de jeu compatible",
        gapIncompatible: (minimumGap: string, maximumGap: string) =>
          `Le jeu doit être compris entre ${minimumGap} et ${maximumGap}.`,
        joistsSpacingIncompatible: (minimumSpacing: number, maximumSpacing: number, unit: string) =>
          `L'entraxe des lambourdes doit être compris entre ${minimumSpacing} ${unit} et ${maximumSpacing} ${unit}.`,
        material: "Matériau",
        connectorMaterial: "Matériau de connecteur",
        timberMaterial: "Bois",
        wpcMaterial: "WPC",
        boardWidth: "Largeur de lame",
        differentBoardWidthDragHint: "Vous pouvez faire glisser les lames d'un côté à l'autre de la terrasse.",
        differentBoard: "Lame de largeur différente",
        differentBoardsColumn: "Lames de largeur différente",
        longDifferentBoardWidth: "Lame longue",
        shortDifferentBoardWidth: "Lame courte",
        boardThickness: "Épaisseur de lame",
        boardThicknessMustNotExceedBoardWidth: "L'épaisseur de la lame ne doit pas dépasser sa largeur.",
        boardThicknessExceedsConnectorFamilyMaxThickness: (maxValue: string) =>
          `L'épaisseur de la lame dépasse le maximum autorisé par la famille de connecteurs sélectionnée (${maxValue}).`,
        boardThicknessBelowAsymmetricalGroovingMinimum: (minValue: string) =>
          `L'épaisseur de la lame est trop faible pour la rainure asymétrique sélectionnée (minimum ${minValue}).`,
        boardThicknessBelowGroovingMinimum: (minValue: string) =>
          `L'épaisseur de la lame doit être d'au moins ${minValue} pour le type de rainure sélectionné.`,
        boardThicknessOutOfSymmetricalGroovingRange: (minValue: string, maxValue: string) =>
          minValue === maxValue
            ? `L'épaisseur de la lame doit être de ${minValue} pour la configuration actuellement sélectionnée.`
            : `L'épaisseur de la lame doit être comprise entre ${minValue} et ${maxValue} pour la rainure symétrique avec la famille de connecteurs sélectionnée.`,
        groovingHeight: "Hauteur de la rainure",
        groovingHeightOutOfConnectorFamilyRange: (minValue: string, maxValue: string) =>
          `La hauteur de la rainure doit être comprise entre ${minValue} et ${maxValue} pour la famille de connecteurs sélectionnée.`,
        groovingHeightBelowConnectorMinimum: (minValue: number, unit: string) =>
          `La hauteur de la rainure doit être d'au moins ${minValue} ${unit} pour le connecteur sélectionné.`,
        boardWidthMustNotExceedDeckDimension: (maxValue: string) =>
          `La largeur de lame ne doit pas dépasser ${maxValue} pour la direction sélectionnée.`,
        sideNarrowerThanBoardWidth: "Ce côté ne peut pas être plus étroit que la largeur de lame sélectionnée.",
        boardFitWarning:
          "La largeur de lame et le jeu sélectionnés ne correspondent pas exactement aux dimensions de la terrasse. Pour optimiser, choisissez l'une des 3 options suivantes :",
        boardFitApplied:
          "Modification appliquée. Si elle ne vous convient pas, cliquez sur « Annuler » pour rétablir les valeurs précédentes.",
        appliedFitParameter: "Paramètre",
        appliedFitPrevious: "Précédent",
        appliedFitCurrent: "Actuel",
        setBoardWidth: "Définir la largeur de lame",
        setBoardWidthTo: "Définir la largeur de lame à :",
        setDeckDimensions: "Définir les dimensions",
        setDeckDimensionsTo: "Définir les dimensions à :",
        setDifferentBoardWidth: "Définir les largeurs des lames différentes",
        setDifferentBoardWidthTo: "Définir les largeurs des lames différentes à :",
        topView: "Vue de dessus",
        sideView: "Vue latérale",
        bottomView: "Vue de dessous",
        slopeView: "Pente",
        slopeViewSide: (from: number, to: number) => `Côté ${from}–${to}`,
        slopeViewLegendDeck: "Terrasse",
        slopeViewLegendTerrain: "Terrain",
        slopeViewExaggeration: "Exagération de la pente (affichage uniquement)",
        slopeViewShowArrow: "Afficher la flèche de pente",
        unitSystemMetric: "Métrique",
        unitSystemImperial: "Impérial",
        unitSystemChangeTitle: "Changer d'unité de mesure",
        unitSystemChangeWarning:
          "Changer d'unité de mesure conserve la forme et les dimensions ; tous les autres champs seront réinitialisés. Voulez-vous continuer ?",
        slopeViewExaggerationHelp:
          "Accentue la pente dans la vue 3D pour mieux la voir. Ce n'est qu'un effet visuel : les valeurs et la configuration ne changent pas.",
        slopeViewClickHint: "Cliquez sur une étiquette pour voir h, t et H à cet endroit.",
        slopeViewNotToScale: "Schéma illustratif, pas à l'échelle.",
        slopeViewUnreachable: "Non atteignable",
        slopeViewLegendTotalHeight: "H — hauteur maximale extrados terrasse",
        slopeViewLegendSupportHeight: "h — hauteur disponible pour le support",
        slopeViewLegendTerrainHeight: "t — hauteur du terrain par rapport au sommet le plus bas, où H est mesurée",
        supportCompatibleHRange: "H compatible",
        supportMinHeight: "Hauteur support min",
        supportMaxHeight: "Hauteur support max",
        supportSlopeUnchanged: "La pente correspond à celle saisie.",
        supportSlopeReduced: "La pente sera réduite par rapport à celle saisie.",
        supportSlopeIncreased: "La pente sera augmentée par rapport à celle saisie.",
        supportTotalHeightUnchanged: "H correspond à la valeur saisie.",
        supportTotalHeightReduced: "H sera réduite par rapport à la valeur saisie.",
        supportTotalHeightIncreased: "H sera augmentée par rapport à la valeur saisie.",
        supportFlatTerrainOnly: "Uniquement avec des pentes à 0°",
        supportSuggestedSlope: "Pente",
        supportWidth: "Largeur du support",
        suggestedValueUnchanged: "La valeur correspond à celle saisie.",
        suggestedValueReduced: "La valeur sera réduite par rapport à celle saisie.",
        suggestedValueIncreased: "La valeur sera augmentée par rapport à celle saisie.",
        supportSpacingSuggestionsInfo:
          "Pour chaque support, la configuration la plus proche qui espace les supports d'au moins 1,5 fois leur largeur. La section de l'élément et l'entraxe des lambourdes changent d'abord ; la flèche et la charge d'exploitation seulement si cela ne suffit pas.",
        noSupportSpacingSuggestion: "Aucune configuration n'espace les supports d'au moins 1,5 fois leur largeur.",
        supportApplySuggestion: "Définir les valeurs",
        noSupportCompatibleWithSlope:
          "Aucun support n'est compatible avec la pente saisie : sans pente, il en existerait au moins un. Essayez de la réduire, d'augmenter H, ou de modifier les dimensions de la terrasse.",
        noSupportCompatibleGeneric:
          "Aucun support n'est compatible avec la configuration actuelle. Essayez de modifier la hauteur totale H, le support choisi, ou les autres éléments de la terrasse (planche, connecteur, élément secondaire).",
        supportScrew: "Vis de support",
        connectorSubstructureScrew: "Vis connecteur sous-structure",
        connectorBoardScrew: "Vis connecteur lame",
        regularLongBoards: "Lames longues régulières",
        regularShortBoards: "Lames courtes régulières",
        differentLongBoards: "Lames longues de largeur différente",
        differentShortBoards: "Lames courtes de largeur différente",
      },
    },
    connectorFamilies: {
      singular: "Famille de connecteurs",
      plural: "Familles de connecteurs",
      isScrew: "Est une vis",
      requiresBoardScrew: "Nécessite une vis de lame",
      maxBoardThickness: "Épaisseur max. de la lame",
      description: "Description",
      errors: {
        nameAlreadyPresent: "Une famille de connecteurs avec le même nom existe déjà.",
      },
    },
    connectorMappings: {
      singular: "Association de connecteurs",
      plural: "Associations de connecteurs",
      minimumGap: "Jeu minimum",
      maximumGap: "Jeu maximum",
    },
    connectors: {
      singular: "Connecteur",
      plural: "Connecteurs",
      minGroovingHeight: "Hauteur de rainure minimale",
      minBoardWidth: "Largeur minimale de lame",
      errors: {
        inUseByConfiguration: "Le connecteur est utilisé dans un calcul enregistré et ne peut pas être supprimé.",
        itemAlreadyPresent: "Un connecteur avec le même élément existe déjà.",
        screwAlreadyPresent: "Un connecteur avec la même vis existe déjà.",
      },
    },
    fasteningTypes: {
      singular: "Type de fixation",
      plural: "Types de fixation",
      errors: {
        nameAlreadyPresent: "Un type de fixation avec le même nom existe déjà.",
      },
    },
    groovingTypes: {
      singular: "Type de rainure",
      plural: "Types de rainure",
      errors: {
        nameAlreadyPresent: "Un type de rainure avec le même nom existe déjà.",
      },
    },
    connectorMaterials: {
      singular: "Matériau de connecteur",
      plural: "Matériaux de connecteur",
      errors: {
        inUseByConnector: "Le matériau est utilisé par un connecteur : modifiez ou supprimez d'abord le connecteur.",
        nameAlreadyPresent: "Un matériau de connecteur avec le même nom existe déjà.",
      },
    },
    secondaryItems: {
      singular: "Element secondaire",
      plural: "Elements secondaires",
      use: "Utiliser un element secondaire",
      canUse: "Peut utiliser un element secondaire",
      errors: {
        inUseByConfiguration:
          "L'élément secondaire est utilisé dans un calcul enregistré et ne peut pas être supprimé.",
        inUseByConnectorMapping:
          "L'élément secondaire est utilisé par une association de connecteurs : supprimez d'abord l'association.",
      },
    },
    screwFamilies: {
      singular: "Famille de vis",
      plural: "Familles de vis",
      errors: {
        inUseByScrew: "La famille de vis est utilisée par une vis : supprimez d'abord ses vis.",
        inUseByConnectorFamily:
          "La famille de vis est associée à une famille de connecteurs : retirez-la d'abord de celle-ci.",
        inUseBySupportFamily:
          "La famille de vis est associée à une famille de supports : retirez-la d'abord de celle-ci.",
        nameAlreadyPresent: "Une famille de vis avec le même nom existe déjà.",
      },
    },
    screws: {
      singular: "Vis",
      plural: "Vis",
      errors: {
        inUseByConfiguration: "La vis est utilisée dans un calcul enregistré et ne peut pas être supprimée.",
        inUseByConnector: "La vis est utilisée comme connecteur : supprimez d'abord ce connecteur.",
        itemAlreadyPresent: "Une vis avec le même élément existe déjà.",
      },
    },
    substructures: {
      singular: "Sous-structure",
      plural: "Sous-structures",
      base: "Largeur",
      secondMomentOfArea: "Moment quadratique",
      countries: "Pays",
      countryOptions: {
        eu: "Europe",
        us: "États-Unis",
        all: "Tous",
      },
      errors: {
        inUseByConfiguration: "La sous-structure est utilisée dans un calcul enregistré et ne peut pas être supprimée.",
        itemAlreadyPresent: "Une sous-structure avec le même élément existe déjà.",
        baseHeightAlreadyPresent: "Une sous-structure avec la même base et hauteur existe déjà.",
      },
    },
    substructureFamilies: {
      singular: "Famille de sous-structures",
      plural: "Familles de sous-structures",
      errors: {
        nameAlreadyPresent: "Une famille de sous-structures avec le même nom existe déjà.",
      },
    },
    substructureMaterialClasses: {
      singular: "Classe de matériau de sous-structure",
      plural: "Classes de matériau de sous-structure",
      modulusOfElasticity: "Module d'élasticité",
      errors: {
        inUseByConfiguration:
          "La classe de matériau est utilisée dans un calcul enregistré et ne peut pas être supprimée.",
        inUseBySupport: "La classe de matériau est associée à un support : retirez-la d'abord de celui-ci.",
        nameAlreadyPresent: "Une classe de matériau de sous-structure avec le même nom existe déjà.",
      },
    },
    substructureMaterials: {
      singular: "Matériau de sous-structure",
      plural: "Matériaux de sous-structure",
      errors: {
        nameAlreadyPresent: "Un matériau de sous-structure avec le même nom existe déjà.",
      },
    },
    supportFamilies: {
      singular: "Famille de supports",
      plural: "Familles de supports",
      maxLiveLoad: "Charge d'exploitation max.",
      errors: {
        nameAlreadyPresent: "Une famille de supports avec le même nom existe déjà.",
      },
    },
    supports: {
      singular: "Support",
      plural: "Supports",
      errors: {
        inUseByConfiguration: "Le support est utilisé dans un calcul enregistré et ne peut pas être supprimé.",
        itemAlreadyPresent: "Un support avec le même élément existe déjà.",
      },
    },
    substuructures: {
      singular: "Sous-structure",
      plural: "Sous-structures",
    },
    substuructureMaterials: {
      singular: "Matériau de sous-structure",
      plural: "Matériaux de sous-structure",
    },
    substuructureFamilies: {
      singular: "Famille de sous-structures",
      plural: "Familles de sous-structures",
    },
  },

  lockCalculatorModule: {
    preview3d: "Aperçu 3D",
    preDrillingLegend: {
      header: "Pré-perçage poutre principale",
      joist: "Pré-perçage solive",
      yes: "oui",
      no: "non",
    },
    tabs: {
      codeNorm: "Norme",
      structure: "Structure",
      connectors: "Connecteurs",
      loading: "Charges",
      designInput: "Données",
      configuration: "Configuration",
      loads: "Charges",
      results: "Résultats",
      calculationComparison: "Comparaison des calculs",
      bom: "Nomenclature",
      report: "Rapport PDF",
    },
    env: {
      title: "Paramètres Environnementaux",
      standard: "Norme",
      serviceClass: "Classe de Service",
      loadDurationClass: "Classe de Durée de Charge",
      continuousCtrl: "Contrôle Continu",
      continuousCtrlLabel: "Production soumise à contrôle continu (COV < 15%)",
      coefficients: "Coefficients de calcul",
      kmod: "Coefficient de modification (durée de chargement et classe de service)",
      gammaM: "Coefficient partiel de sécurité de l'assemblage (γM)",
      gammaMAluminium: "Coefficient partiel de sécurité de l'aluminium (γM)",
      customCoefficients: "Personnalisé",
      customCoefficientsHint: "Modifier kmod et γM pour ce calcul uniquement, à la place des valeurs normatives",
      normativeValue: "Valeur normative",
      restoreNormative: "Rétablir",
    },
    structural: {
      title: "Configuration Structurelle",
      configType: "Résistance latérale",
      connectionPosition: "Position du connecteur",
      upliftResistanceConfig: "Configuration de résistance au soulèvement",
      inclinedFastener: "Fixation inclinée",
      headerOrientation: "Orientation de la poutre principale",
      lateralResistance: {
        columnNotch: "Column notch",
        headerBeamNotch: "Entaille de la poutre principale",
        joistBeamNotch: "Entaille de la solive",
        inclinedFasteners: "Fixations inclinées",
        lockstop: "LOCK STOP",
        exposed: "Apparente",
      },
      headerOrientationOptions: { beam: "Poutre", column: "Poteau" },
      generateAllConfigurations: "Générer toutes les configurations",
      goToManualConfiguration: "Aller à la configuration manuelle/semi-manuelle",
    },
    connection: {
      title: "Configuration de la Connexion",
      standard: "Norme",
      serviceClass: "Classe de Service",
      loadDurationClass: "Classe de Durée de Charge",
      connector: "Connecteur",
      finish: "Finition",
      connectorOne: "Connecteur 1",
      connectorTwo: "Connecteur 2",
      productType: "Type de Produit",
    },
    fasteners: {
      headerFastener: "Fixation de poutre principale",
      joistFastener: "Fixation de solive",
      upliftFastener: "Fixation Anti-Soulèvement",
      lockstop: "Lockstop",
    },
    customMaterial: {
      create: "Créer un matériau personnalisé",
      edit: "Modifier le matériau personnalisé",
      remove: "Supprimer le matériau",
      badge: "personnalisé",
      name: "Nom",
      baseGrade: "Basé sur",
      keepCurrentBase: "Conserver la base actuelle",
      baseGradeHint: "La catégorie, la norme et la famille structurelle sont héritées du matériau de base",
      inherited: "Hérité de la base",
      family: "Famille structurelle",
      category: "Catégorie",
      normative: "Norme",
      properties: "Propriétés",
      advanced: "Avancées",
      rhoK: "Masse volumique caractéristique ρk",
      rhoM: "Masse volumique moyenne ρm",
      fc0K: "Compression parallèle au fil fc,0,k",
      fc90K: "Compression perpendiculaire au fil fc,90,k",
      fvK: "Cisaillement fv,k",
      frK: "Cisaillement de roulement fr,k",
      faxK: "Arrachement axial fax,k",
      usedInCalculations: (n: number) => `Utilisé dans ${n} calculs : ils seront mis à jour au prochain recalcul.`,
      deleteConfirm: "Voulez-vous vraiment supprimer ce matériau personnalisé ?",
      deleteBlocked: "Suppression impossible : le matériau est utilisé dans d'autres calculs.",
    },
    timber: {
      sectionTitle: "Configuration Géométrique",
      title: "Éléments en Bois",
      header: "Poutre principale",
      joist: "Solive",
      headerMaterialGrade: "Classe de résistance de la poutre principale",
      headerWidth: "Largeur de la poutre principale (b)",
      headerHeight: "Hauteur de la poutre principale (h)",
      joistMaterialGrade: "Classe de résistance de la solive",
      joistWidth: "Largeur de la solive (b)",
      joistHeight: "Hauteur de la solive (h)",
      joistAngle: "Angle de la solive α",
      joistAngleBeta: "Angle de la solive β",
      connectorTopDistance: "Distance du connecteur au bord supérieur de la solive",
      joistTopOffset: "Décalage du haut de la solive par rapport à la poutre principale",
      joistTopAlignment: "Position du haut de la solive",
      joistTopAligned: "Alignée avec le haut du poteau",
      joistTopOffsetOption: "Décalée par rapport au poteau",
    },
    connector: {
      sectionTitle: "Connecteur & Visserie",
      setupTitle: "Configuration Connexion",
      selectionTitle: "Choix Connecteur",
      fastenersTitle: "Visserie",
      connectorPairCount: "Nombre de paires de connecteurs",
      noCompatibleConnector: "Aucun connecteur LOCK-T compatible avec les sections saisies.",
      noCompatibleSecondConnector: "Aucun second connecteur compatible avec celui sélectionné",
      generateFromData: "Générer les configurations à partir des données saisies",
      generateManual: "Générer une solution manuelle",
    },
    loads: {
      sectionTitle: "Actions de Calcul (Ed)",
      title: "Charges (Ed)",
      fvD: "Effort Tranchant (Fv,d)",
      faxD: "Effort Axial (Fax,d)",
      flatD: "Effort Latéral (Flat,d)",
      fupD: "Effort de Soulèvement (Fup,d)",
      alphaDeg: "Angle α",
    },
    combined: {
      title: "Vérification Combinée",
      description: "Sélectionnez les directions à inclure dans la vérification combinée:",
      includeFv: "Fv,d (Cisaillement)",
      includeFup: "Fup,d (Soulèvement)",
      includeFax: "Fax,d (Arrachement)",
      includeFlat: "Flat,d (Latéral)",
    },
    configuration: {
      title: "Configuration",
      configType: "Type de Configuration",
      connectionCount: "Nombre de Connexions",
    },
    errors: {
      fillRequiredFields: "Veuillez remplir tous les champs obligatoires.",
    },
    selectPlaceholder: "Select...",
    helpTexts: {
      standard: "Norme de calcul à appliquer (Eurocode 5 ou NTC 2018)",
      serviceClass: "Conditions d'humidité et température du site (EC5 §2.3.1.3)",
      loadDurationClass: "Durée de la charge appliquée (permanente, long terme, moyen terme, court terme, instantanée)",
      continuousCtrl: "Production certifiée avec COV < 15% permet γM réduit (EC5 §2.4.1)",
      lateralResistanceConfig: "Type de configuration pour la vérification de résistance latérale",
      upliftResistanceConfig:
        "Choisissez d'utiliser la fixation inclinée pour le calcul de résistance au soulèvement. Partage la même fixation sélectionnée à l'étape Connectors pour la configuration latérale.",
      headerOrientation: "Orientation de la poutre principale : horizontale (poutre) ou verticale (poteau).",
      headerMaterialGrade: "Classe de bois de la poutre principale (GL24h, GL28h, LVL, C24...)",
      headerWidth:
        "Largeur de la poutre principale (dimension b). En orientation poteau, la largeur propre du connecteur doit rentrer dans cette mesure moins une tolérance (17,5mm pour Mini, 24mm pour Midi).",
      headerHeight:
        "Hauteur de la poutre principale (dimension h). En orientation poutre, elle limite la hauteur de montage maximale (assembly_height) du module connecteur (≤ hauteur poutre principale − 15mm).",
      joistMaterialGrade:
        "Classe de bois de la solive. Une densité (rho_k) supérieure à 420 kg/m³ nécessite un pré-perçage, ce qui réduit la largeur maximale admise pour le connecteur (voir largeur de la solive).",
      joistWidth:
        "Largeur de la solive (section b). La largeur propre du connecteur doit rentrer dans cette mesure — exactement si le bois nécessite un pré-perçage, ou avec une tolérance supplémentaire de 8mm (Mini) / 6mm (Midi) sinon.",
      joistHeight:
        "Hauteur de la solive (section h). Limite la hauteur de montage maximale (assembly_height) du module connecteur (≤ hauteur solive), sans tolérance.",
      angleAlpha: "",
      joistTopAlignment:
        "Poutre principale en poteau. Alignée : le haut de la solive est à fleur avec le haut du poteau et le c_min est vérifié sur la distance du connecteur au haut de la solive. Décalée : le haut de la solive se trouve sous le poteau (champ de décalage) et le c_min est vérifié sur la somme du décalage et de la distance du connecteur.",
      joistTopOffset:
        "De combien le haut de la solive se trouve sous le haut du poteau. Si vide, le c_min du LOCK choisi est utilisé.",
      connectorTopDistance:
        "Distance verticale entre le bord supérieur de la solive et l'ensemble de connecteurs (la poutre principale suit). Si vide : avec la solive alignée au poteau, le c_min du LOCK choisi est utilisé ; avec la solive décalée, 0 (connecteur à fleur du haut de la solive). Pour LOCKT135 sans pré-perçage, le minimum est de 5 mm. Avec poteau comme poutre principale, le c_min doit être atteint : par cette distance seule si la solive est alignée, par sa somme avec le décalage si elle est décalée.",
      angleBeta:
        "Angle de cisaillement de la solive dans le plan X-Z, indépendant de l'angle α. Détermine la longueur maximale admise pour la fixation de la solive : jusqu'à 20° la limite est de 48,35mm (Mini) / 65,7mm (Midi) ; au-delà, aucune fixation n'est compatible (limite 0).",
      connectorPairCount: "Nombre de paires de connecteurs LOCK dans l'assemblage",
      headerLock:
        "Un lock plus large côté poutre principale divise par deux les connecteurs de la nomenclature et met à jour les vis de ce côté. Le choisir ou le modifier peut retirer le LOCK STOP de la configuration : le résultat doit alors être recalculé. Le lock plus large et le LOCK STOP s'excluent : choisir l'un retire l'autre.",
      connectionCount: "Nombre total d'assemblages identiques dans la structure",
      connectorOneLock: "Modèle LOCK du connecteur 1",
      connectorOneFinish: "Finition de surface du connecteur 1",
      connectorTwoLock: "Modèle LOCK du connecteur 2 (avec deux paires de connecteurs)",
      connectorTwoFinish: "Finition de surface du connecteur 2",
      headerFastener: "Fixation du côté poutre principale",
      joistFastener: "Fixation du côté solive",
      upliftFastener: "Fixation pour la vérification au soulèvement",
      lockstop: "Élément LOCK STOP pour résistance latérale",
      lockstopWidth: "Réglage de largeur pour l'élément LOCK STOP (mm)",
      fvD: "Effort de cisaillement vertical (valeur de calcul)",
      fupD: "Effort de soulèvement (valeur de calcul)",
      faxD: "Effort axial (valeur de calcul)",
      flatD: "Effort latéral perpendiculaire à la poutre (valeur de calcul)",
      combined:
        "Vérification combinée : (Fax,Ed / Rax,d)² + (Fv,Ed / Rv,d)² ≤ 1. Sélectionnez les directions de charge à inclure.",
    },
    notices: {
      title: "Avis",
      goToField: "Aller au champ",
      goToAction: "Aller au bouton",
      count: (n: number) => (n === 1 ? "1 avis" : `${n} avis`),
      severity: { error: "Erreur", warning: "Attention", info: "Info" },
      axes: { vertical: "vertical (Fv)", uplift: "soulèvement (Fup)", axial: "axial (Fax)", lateral: "latéral (Flat)" },
      members: { header: "poutre principale", joist: "solive" },
      reasons: {
        columnWidth: "la largeur du poteau",
        topDistance: "la distance du connecteur au bord supérieur",
        joistWidth: "la largeur de la solive",
        assemblyHeight: "les hauteurs des sections",
        evoRequired: "l'exigence EVO de la classe de service 3",
      },
      coefficients: { kmod: "kmod", gammaM: "γM (assemblage)", gammaMAluminium: "γM (aluminium)" },
      slots: { header: "vis de la poutre principale", joist: "vis de la solive", inclined: "vis inclinée" },
      inputs: {
        standardId: "norme",
        serviceClassId: "classe de service",
        loadDurationClassId: "classe de durée de charge",
        "connectors[0].lockItemId": "premier connecteur",
        "connectors[1].lockItemId": "deuxième connecteur",
        headerFastenerItemId: "vis de la poutre principale",
        joistFastenerItemId: "vis de la solive",
        lockstopItemId: "LOCK STOP",
        headerMaterialGradeId: "matériau de la poutre principale",
        joistMaterialGradeId: "matériau de la solive",
        headerOrientation: "orientation de la poutre principale",
        alphaDeg: "angle α",
        headerH: "hauteur de la poutre principale",
        headerB: "largeur de la poutre principale",
        joistB: "largeur de la solive",
        joistH: "hauteur de la solive",
        anyLoad: "au moins une charge",
        inclinedFastenerItemId: "vis inclinée",
        lateralResistanceConfig: "résistance latérale",
        kmod: "kmod",
        gammaM: "γM",
      },
      messages: {
        topDistanceBelowCMin: (p: NoticeParams) =>
          `La distance du connecteur ${p.distance} mm + le décalage de la solive ${p.offset} mm sont inférieurs au c_min minimal du LOCK (${p.cMin} mm) : la configuration n'est pas vérifiée. Augmentez la distance ou le décalage.`,
        topDistanceBelowLock135Min: (p: NoticeParams) =>
          `Le LOCKT135 sur une solive non pré-percée exige au moins ${p.min} mm depuis le bord supérieur (actuellement ${p.distance} mm) : la configuration n'est pas vérifiée.`,
        serviceClassNotAllowed: (p: NoticeParams) =>
          `La classe de service ${p.classNumber} n'est pas admise pour le matériau de la poutre principale (admises : ${p.allowed}) : la configuration n'est pas vérifiée.`,
        axisInputsMissing: (p: NoticeParams) =>
          `L'axe ${p.axis} a une charge mais ne peut pas être calculé : il manque ${p.missing}.`,
        noCompatibleLock: (p: NoticeParams) =>
          `Aucun LOCK ne convient à cette structure : ${p.rejected} sur ${p.total} exclus par ${p.reason}.`,
        edgeDistanceUnavailable: (p: NoticeParams) =>
          `Les distances minimales aux bords (§11) ne sont pas disponibles pour la ${p.member} : la masse volumique (${p.rhoK} kg/m³) dépasse la limite de 500 de l'EC5.`,
        customCoefficientsEnabled: () =>
          "Vous pouvez maintenant modifier kmod et γM pour ce calcul uniquement : tant que vous ne changez aucune valeur, la valeur normative s'applique.",
        coefficientOverrideDiffers: (p: NoticeParams) =>
          `${p.coefficient} vaut ${p.value} ; la valeur normative est ${p.normative}.`,
        topDistanceEqualsDefault: () => "La valeur par défaut est utilisée : le c_min du LOCK choisi.",
        resultStale: (p: NoticeParams) =>
          `Les données ont changé depuis le dernier calcul : le résultat de la configuration finale peut ne plus être à jour. Appuyez sur « ${p.button} » pour le recalculer avec les données actuelles.`,
        reportStale: (p: NoticeParams) =>
          `Les données ont changé depuis le dernier rapport : le PDF affiché n'est pas à jour. Appuyez sur « ${p.generateReport} » dans l'onglet « ${p.reportTab} ».`,
        draftsStale: (p: NoticeParams) =>
          `Les données ont changé depuis la génération des configurations : appuyez sur « ${p.button} » pour mettre la liste à jour.`,
        noDraftWithinLimits: (p: NoticeParams) =>
          `Des LOCK conviennent à la structure, mais aucune configuration générée ne respecte les charges. Vérifiez les charges ou les matériaux, puis appuyez de nouveau sur « ${p.button} ».`,
        generationInputsMissing: (p: NoticeParams) =>
          `« ${p.generateAll} » et « ${p.generateFromData} » ne sont pas disponibles : il manque ${p.missing}.`,
        manualGenerationInputsMissing: (p: NoticeParams) =>
          `« ${p.generateManual} » n'est pas disponible : il manque ${p.missing}.`,
        noCompatibleFastener: () =>
          `Des LOCK conviennent à la structure, mais aucune vis n'est compatible avec eux : vérifiez les hauteurs des poutres, l'angle et les matériaux.`,
        noCompatibleLockstop: () =>
          `Des LOCK conviennent à la structure, mais aucun LOCK STOP n'est compatible avec eux : choisissez une autre résistance latérale.`,
        combinedNeedsTwoAxes: (p: NoticeParams) =>
          `La vérification combinée exige au moins deux axes : seul l'axe ${p.axis} est sélectionné. Sélectionnez-en un autre.`,
        lateralConfigCorrected: (p: NoticeParams) =>
          `La résistance latérale est passée de « ${p.fromConfig} » à « ${p.toConfig} » : la première n'est pas disponible avec l'orientation « ${p.orientation} » de la poutre principale.`,
        continuousControlCleared: () =>
          "Le contrôle continu de la production a été effacé : il ne s'applique pas à l'Eurocode 5.",
        secondConnectorCleared: () =>
          "Le deuxième connecteur a été retiré : une seule paire de connecteurs est sélectionnée.",
        lockstopClearedByHeaderLock: () =>
          "Le LOCK STOP et le type de résistance latérale ont été retirés car un lock plus large a été choisi côté poutre principale : choisissez un nouveau type de résistance latérale et recalculez le résultat.",
        headerLockClearedByLockstop: () =>
          "Le lock plus large côté poutre principale a été retiré car un LOCK STOP a été choisi : recalculez le résultat.",
        lockstopCleared: () => "Le LOCK STOP a été retiré : la résistance latérale n'est plus réglée sur LOCK STOP.",
        combinedAxisDisabled: (p: NoticeParams) =>
          `L'axe ${p.axis} a été retiré de la vérification combinée : il n'a pas de charge.`,
        upliftConfigSetByLateral: () =>
          "La résistance au soulèvement a été réglée sur vis inclinées, comme pour la résistance latérale.",
        upliftConfigSetByLoad: () =>
          "La résistance au soulèvement a été réglée sur vis inclinées car une charge de soulèvement est saisie.",
        inclinedFastenerCleared: () =>
          "La vis inclinée a été retirée : ni la résistance latérale ni celle au soulèvement n'utilisent de vis inclinées.",
        inclinedFastenerNotAllowed: () =>
          "La vis inclinée a été retirée : le LOCK choisi n'accepte pas de vis inclinées.",
        fastenerCleared: (p: NoticeParams) =>
          `La ${p.slot} a été retirée : elle n'est pas compatible avec la sélection actuelle.`,
        finishForced: () => "La finition a été changée en aluminium : le LOCK choisi n'a pas de version EVO.",
        finishSetByServiceClass: () => "La finition a été réglée sur EVO : la classe de service 3 l'exige.",
        secondLockCleared: () => "Le deuxième LOCK a été retiré : il ne convient plus avec le premier.",
        pairCountReduced: () =>
          "Une seule paire de connecteurs a été conservée : aucun deuxième LOCK ne convient avec le premier.",
        topDistanceFollowedDefault: (p: NoticeParams) =>
          `La distance du connecteur a suivi la nouvelle valeur par défaut du LOCK choisi : ${p.before} mm → ${p.after} mm.`,
        topOffsetFollowedDefault: (p: NoticeParams) =>
          `Le décalage de la solive a suivi la nouvelle valeur par défaut du LOCK choisi : ${p.before} mm → ${p.after} mm.`,
        connectorsInvalidated: () =>
          "Les LOCK choisis ont été effacés : ils avaient été choisis pour une autre distance au bord supérieur.",
      },
    },
    wizard: {
      stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
      completeFieldsToContinue: "Remplissez tous les champs de cette page pour continuer",
    },
    finish: {
      aluminium: "Aluminium",
      evo: "EVO",
      aluminiumShort: "ALU",
      evoShort: "EVO",
    },
    continuousCtrl: {
      active: "Active",
      inactive: "Inactive",
      standard: "Standard",
      cov: "COV",
      std: "STD",
    },
    results: {
      title: "Résultats de Vérification",
      verified: "Vérifié",
      utilizationRatio: "Taux d'Utilisation",
      notYetCalculated: "Remplissez le formulaire pour effectuer la vérification.",
      notVerified: "Non vérifié",
      verifiedOk: "Vérification réussie",
      axes: {
        vertical: "Vertical (Fv)",
        uplift: "Uplift (Fup)",
        axial: "Axial (Fax)",
        lateral: "Lateral (Flat)",
      },
      load: "Load",
      resistance: "Resistance",
      eta: "η",
      failed: "FAILED",
      noLoad: "NO LOAD",
      formulaAltSingle: "η = Ed / Rd ≤ 1",
      formulaAltCombined: "Σ (Ed/Rd)² ≤ 1",
      finalConfiguration: "Configuration finale",
      removeComparison: "Retirer de la comparaison",
      slipModulus: "Module de glissement",
    },
    generatedConfigurations: {
      title: "Configurations générées",
      empty: "Aucune configuration générée pour le moment — remplissez Code & Norme, Charges et Structure.",
      lock: "Lock",
      fastener: "Vis",
      combined: "Combinée",
      chosen: "Choisie",
      compare: "Comparer",
      downloadData: "Télécharger les données",
      load: "Charger",
      selectAll: "Sélectionner toutes les configurations",
      selectRow: "Sélectionner cette configuration",
      search: "Rechercher des configurations",
    },
    bom: {
      title: "Nomenclature",
      noComponents: "Aucun composant configuré.",
      component: "Composant",
      code: "Code",
      quantity: "Qté",
      headerLock: "Lock poutre principale plus large",
    },
    report: {
      title: "Rapport PDF",
      empty: "Choisissez une configuration pour générer le rapport de calcul.",
      error: "Le rapport n'a pas pu être généré.",
      retry: "Réessayer",
      generate: "Générer le PDF",
      download: "Télécharger le PDF",
      fullscreen: "Plein écran",
      exitFullscreen: "Quitter le plein écran",
      savingChanges: "Enregistrement des modifications : le rapport est généré à partir des données enregistrées.",
      notGenerated: "Cliquez sur « Générer le PDF » pour générer le rapport de calcul.",
    },
    helpModal: {
      illustrationAlt: "Help illustration",
    },
    aria: {
      calculationSteps: "Calculation steps",
      calculationResults: "Calculation Results",
    },
    admin: {
      tabs: {
        reference: "Reference",
        catalog: "Catalog",
        coefficients: "Coefficients",
        connectors: "Lock",
      },
      catalog: {
        fasteners: "Fasteners",
        materialGrades: "Material Grades",
      },
      coefficients: {
        kmodValues: "Kmod Values",
        gammaMValues: "γM Values",
      },
      reference: {
        standards: "Standards",
        serviceClasses: "Service Classes",
        loadDurationClasses: "Load Duration Classes",
      },
      fasteners: {
        singular: "Fastener",
        diameter: "Ø (mm)",
        length: "Length (mm)",
        threadLength: "Thread Length (mm)",
        myRk: "My,Rk (kNm)",
        ftensK: "Ftens,K (kN)",
        headType: "Head Type",
      },
      materialGrades: {
        singular: "Material Grade",
        rhoK: "ρk (kg/m³)",
        rhoM: "ρm (kg/m³)",
      },
      standards: {
        singular: "Standard",
        jurisdictionCode: "Région",
        normativeType: "Type de norme",
        normativeTypes: {
          EUROCODE: "Eurocode",
          NATIONAL_CODE: "Code national",
          HARMONIZED_STANDARD: "Norme harmonisée",
          ETA: "ETA",
          DOP: "DoP",
        },
      },
      serviceClasses: {
        singular: "Service Class",
        classNumber: "Class #",
        nameIt: "Name IT",
        nameDe: "Name DE",
        nameFr: "Name FR",
        nameEs: "Name ES",
        description: "Description",
        typicalExamples: "Typical Examples",
        order: "Order",
      },
      loadDurationClasses: {
        singular: "Load Duration Class",
      },
      kmodValues: {
        singular: "Kmod Value",
        serviceClass: "Service Class",
        loadDurationClass: "Load Duration Class",
        kmod: "kmod",
      },
      gammaMValues: {
        singular: "γM Value",
        standard: "Standard",
        continuousCtrl: "Continuous Ctrl",
        notApplicable: "Not applicable (EC5)",
        gammaM: "γM",
      },
      connectors: {
        singular: "Lock Connector",
        family: "Family",
        nModules: "N. Modules",
        nInclinedUplift: "Inclined Fasteners (Uplift)",
        nInclinedLateral: "Inclined Fasteners (Lateral)",
        width: "Width (mm)",
        height: "Height (mm)",
        thickness: "Thickness (mm)",
        screwEdgeThickness: "Screw edge thickness (mm)",
        evoAvailable: "EVO Available",
        lockModule: "Lock Module",
        edgeDistance: "Edge Distance (mm)",
        assemblyHeight: "Assembly Height (mm)",
      },
      lockModules: {
        singular: "Lock Module",
        assemblyHeight: "Assembly height (mm)",
        sideHeight: "Side height (mm)",
        thickness: "Thickness (mm)",
        moduleWidth: "Module Width (mm)",
        joistFastenersPerModule: "Fixations solive / module",
        headFastenersPerModule: "Fixations de tête / module",
        kLockFactor: "kLock",
        f1AluRk: "F1,alu,Rk (kN)",
        f2AluRkPerModule: "F2,alu,Rk / module (kN)",
        kh2PerModule: "kh2 / module",
        contactAreaALock: "Contact Area ALock (mm²)",
        cMin: "c_min (mm)",
        coefficients: "Lateral/Vertical Coefficients",
      },
      lockStops: {
        singular: "Lock Stop",
        fyK: "fy,k (MPa)",
        area: "Area (mm²)",
        fLockstopRk: "FLockstop,Rk (kN)",
        isSingle: "Single",
      },
      connectorAccessories: {
        singular: "Connector Accessory",
        quantity: "Qty",
        notes: "Notes",
        accessorySku: "Accessory SKU",
        connector: "Connector",
      },
      fastenerUsages: {
        singular: "Usage des vis",
        fastener: "Vis",
        usage: "Usage",
        usageStandard: "Standard (poutre principale / solive)",
        usageInclined: "Inclinée",
        scope: "Sélectionner par",
        scopeFamilies: "Familles entières",
        scopeFasteners: "Vis individuelles",
        families: "Familles",
        fasteners: "Vis",
        exceptions: "Exceptions (à ne pas ajouter)",
        resultAlreadyPresent: "Déjà ajoutés auparavant",
        resultAdded: "Nouvellement ajoutés",
        resultNothingToDo: "Aucun élément à ajouter ou à supprimer",
        resultRemoved: "Existaient et ont été supprimés car ce sont des exceptions",
      },
      lockLockstopMappings: {
        singular: "Lock → Lockstop",
        lock: "Lock",
        lockstop: "Lockstop",
        quantity: "Quantity",
        installationLevels: "Niveaux d'installation",
      },
      lockPairLockstopMappings: {
        singular: "Paire → Lockstop",
        lockA: "Lock A",
        lockB: "Lock B",
        lockstop: "Lockstop",
        quantity: "Quantité totale",
        installationLevels: "Niveaux d'installation",
      },
      model3d: {
        singular: "Modèle 3D",
        quantity: "Quantité",
      },
      emptyValue: "--",
    },
  },
  beamReinforcementModule: {
    tabs: {
      generalData: "Données générales & charges",
      beams: "Poutres",
      screw: "Vis & connexion",
    },
    screwFamilies: {
      singular: "Famille de vis",
      plural: "Familles de vis",
      errors: {
        nameAlreadyPresent: "Une famille de vis avec le même nom existe déjà.",
      },
    },
    screws: {
      singular: "Vis",
      plural: "Vis",
      errors: {
        nameAlreadyPresent: "Une vis portant le même nom existe déjà.",
      },
      fields: {
        family: "Famille",
        headDiameter: "Diamètre de tête",
        threadedLength: "Longueur filetée",
        yieldMoment: "Moment plastique",
        tensileCapacity: "Capacité de traction",
      },
    },
    timbers: {
      singular: "Classe de bois",
      plural: "Classes de bois",
      errors: {
        nameAlreadyPresent: "Une classe de bois portant le même nom existe déjà.",
      },
      fields: {
        density: "Densité rho",
        bendingStrength: "Résistance en flexion fmk",
        shearStrength: "Résistance au cisaillement fvk",
        modulusOfElasticity: "Module d'élasticité E0,mean",
        isGlulam: "Bois lamellé-collé",
      },
    },
    drawing: {
      view2D: "Vue 2D",
      view3D: "Vue 3D",
      enable3DHint: "Saisissez les deux longueurs de poutre pour activer la vue 3D.",
    },
    calculation: {
      serviceClass: "Classe de service",
      serviceClass1: "1 — u ≤ 10%",
      serviceClass2: "2 — 10% < u ≤ 16%",
      serviceClass3: "3 — u ≥ 16%",
      loadDuration: "Durée de charge",
      loadDurationPerm: "Permanente",
      loadDurationLong: "Longue durée",
      loadDurationMedium: "Durée moyenne",
      loadDurationShort: "Courte durée",
      loadDurationInst: "Instantanée",
      tipTolerance: "Tolérance de pointe",
      permanentLoad: "Charge permanente",
      liveLoad: "Charge variable",
      beamLength: "Portée de la poutre",
      existingBeamTitle: "Poutre existante",
      reinforcementBeamTitle: "Poutre de renfort",
      strengthClass: "Classe de résistance",
      base: "Largeur",
      height: "Hauteur",
      lengthOptionalHint:
        "Optionnel. Si les deux longueurs de poutre sont définies, l'aperçu peut basculer entre 2D et 3D.",
      existingLength: "Longueur de la poutre existante",
      reinforcementLength: "Longueur du renfort",
      reinforcementSides: "Côtés de renfort",
      reinforcementSidesOne: "1 — renfort d'un seul côté",
      reinforcementSidesTwo: "2 — renfort des deux côtés",
      screw: "Vis",
      screwSpacing: "Entraxe des vis",
      preDrilled: "Trou pré-percé",
    },
    results: {
      summaryTitle: "Résumé des vérifications",
      bendingUtilization: "Taux de travail en flexion",
      shearUtilization: "Taux de travail en cisaillement",
      connectionUtilization: "Taux de travail de la connexion",
      screwAdmissible: "Vis admissible",
      screwNotAdmissible: "Vis NON admissible",
      reasonGeometry: "géométrie (l_ef / longueur / t₂)",
      reasonHeight: "h₂ < a₄,t + a₄,c",
      reasonSpacing: "entraxe < a₁ minimum",
      reasonOverload: "η > 100%",
      incompleteWarning: "Remplissez tous les champs pour calculer la vérification.",
      warningInsufficientPenetration:
        "Pénétration du filetage insuffisante dans la poutre existante (l_ef = 0). Choisissez une vis plus longue.",
      warningScrewTooLong: (max: number) =>
        `La vis dépasse la largeur disponible b₁+b₂−10 mm = ${max} mm : la pointe sort de la poutre existante.`,
      warningHeightInsufficient: (height: number, min: number) =>
        `Hauteur insuffisante : h₂ = ${height} mm < a₄,t + a₄,c = ${min} mm. Augmentez h₂ ou réduisez le diamètre de la vis.`,
      warningSpacingInsufficient: (spacing: number, min: number) =>
        `L'entraxe adopté (${spacing} mm) est inférieur à l'entraxe minimum a₁ = ${min} mm.`,
      warningConnectionOverload: (eta: number) =>
        `Connexion non vérifiée : η = ${eta}% > 100%. Réduisez l'entraxe ou augmentez la vis.`,
    },
    details: {
      title: "Détail du calcul",
      loadsSection: "Coefficients, charges et profondeur de pénétration",
      kmod: "k_mod",
      selfWeight: "Poids propre g₁",
      designLoad: "Charge de calcul q_d",
      spacingSection: "Entraxes minimums et effectifs (EC5 §8.3)",
      minimum: "minimum",
      effective: "effectif",
      outcome: "résultat",
      stiffnessSection: "Répartition des rigidités",
      bendingSection: "Vérification en flexion",
      shearSection: "Vérification en cisaillement",
      axialSection: "Résistance axiale de la vis",
      johansenSection: "Résistance de la connexion (Johansen)",
      connectionSection: "Vérification de la connexion",
      existingBeamLabel: "Poutre existante (1)",
      reinforcementBeamLabel: "Poutre de renfort (2)",
      designMoment: "Moment de calcul M_d",
      designShear: "Effort tranchant de calcul V_d",
      bendingStress: "Contrainte de flexion σ_m,d",
      bendingStrength: "Résistance en flexion f_m,d",
      shearStress: "Contrainte de cisaillement τ_d",
      shearStrength: "Résistance au cisaillement f_v,d",
      screwCount: "Vis le long de la portée",
      designShearForce: "Effort tranchant de calcul par vis V_Ed",
      connectionResistance: "Résistance de la connexion F_v,Rd",
      yes: "Oui",
      no: "Non",
    },
  },
  screwConnectorModule: {
    tabs: {
      screwFamilies: "Familles de vis",
      screwDiameters: "Diamètres de vis",
      screws: "Vis",
      washers: "Rondelles",
      timberMaterials: "Matériaux bois",
      normativeTables: "Tableaux normatifs",
    },
    screwFamilies: {
      singular: "Famille de vis",
      plural: "Familles de vis",
      errors: {
        nameAlreadyPresent: "Une famille de vis portant le même nom existe déjà.",
      },
      fields: {
        description: "Description",
        threadType: "Type de filetage",
        material: "Matériau",
        coating: "Revêtement",
        ccCode: "Code CC",
        tcCode: "Code TC",
        scEuCode: "Code SC (EU)",
        ecUsCode: "Code ESR / EC (US)",
        wdUsCode: "Code WD (US)",
        standardReference: "Référence normative",
        connectionTypes: "Types de connexion",
        substrates: "Supports",
      },
    },
    threadTypes: {
      partial: "Filetage partiel",
      full: "Filetage total",
    },
    headTypes: {
      countersunk: "Tête fraisée",
      cylindrical: "Tête cylindrique",
      hexagonal: "Tête hexagonale",
      round_plate: "Tête ronde (fixation de plaques)",
      pan_plate: "Tête tronconique (fixation de plaques)",
      reinforced_pan_plate: "Tête tronconique renforcée (fixation de plaques)",
      hexagonal_pan_plate: "Tête tronconique hexagonale (fixation de plaques)",
    },
    tipTypes: {
      three_thorns: "3 THORNS",
      self_drilling: "Pointe autoperceuse",
      standard: "Pointe standard",
      hardwood: "Pointe pour bois durs",
    },
    connectionTypes: {
      shear_screws: "Vis au cisaillement",
      steel_to_timber: "Acier-bois",
      timber_to_timber: "Bois-bois",
    },
    substrateCategories: {
      solidwood_softwood: "Bois massif - résineux",
      glulam_softwood: "Bois lamellé-collé - résineux",
      clt_softwood: "CLT - résineux",
      lvl_softwood: "LVL - résineux",
      solidwood_hardwood: "Bois massif - feuillus",
      lvl_hardwood: "LVL - feuillus",
      panels: "Panneaux",
      steel: "Acier",
    },
    compatibilityLevels: {
      notCompatible: "Non compatible",
      restricted: "Compatibilité restreinte",
      compatible: "Compatible",
    },
    washers: {
      singular: "Rondelle",
      plural: "Rondelles",
      fields: {
        code: "Code",
        innerDiameter: "Diamètre intérieur",
        outerDiameter: "Diamètre extérieur",
        height: "Hauteur",
      },
    },
    screwDiameters: {
      singular: "Diamètre de vis",
      plural: "Diamètres de vis",
      filterByFamily: "Filtrer par famille de vis",
      selectFamilyHint: "Sélectionnez une famille de vis pour gérer ses diamètres.",
      fields: {
        screwFamily: "Famille de vis",
        diameter: "Diamètre",
        coreDiameter: "Diamètre du noyau",
        threadOuterDiameter: "Diamètre extérieur du filetage",
        headDiameter: "Diamètre de tête",
        headType: "Type de tête",
        tipType: "Type de pointe",
        bit: "Embout",
        yieldMoment: "Moment plastique",
        tensileCapacity: "Résistance à la traction",
        washers: "Rondelles compatibles",
        resistanceValues: "Valeurs de résistance",
      },
    },
    resistanceValues: {
      fields: {
        resistanceParam: "Paramètre",
        substrateCategory: "Catégorie de support",
        substrateSpecies: "Essence du support",
        preDrilled: "Pré-percé",
        valueNMm2: "Valeur",
        referenceDensity: "Densité de référence",
        densityRange: "Plage de densité",
      },
      params: {
        fax_k: "f_ax,k",
        fhead_k: "f_head,k",
      },
      addRow: "Ajouter une valeur de résistance",
    },
    screws: {
      singular: "Vis",
      plural: "Vis",
      filterByDiameter: "Filtrer par diamètre de vis",
      selectDiameterHint: "Sélectionnez un diamètre de vis pour gérer ses vis.",
      fields: {
        screwDiameter: "Diamètre de vis",
        length: "Longueur",
        partialThreadLength: "Longueur de filetage partiel",
        fullThreadExtraLength: "Longueur supplémentaire de filetage total",
        tipLength: "Longueur de pointe",
      },
    },
    timberMaterials: {
      singular: "Matériau bois",
      plural: "Matériaux bois",
      fields: {
        materialCategory: "Catégorie de matériau",
        species: "Essence",
        class: "Classe",
        characteristicDensity: "Masse volumique caractéristique ρk",
        meanDensity: "Masse volumique moyenne ρmean",
        compressionStrength0: "Résistance à la compression f_c,0,k",
        compressionStrength90: "Résistance à la compression f_c,90,k",
        rollingShearStrength: "Résistance au cisaillement roulant f_r,k",
        shearStrength: "Résistance au cisaillement f_v,k",
        standardReference: "Référence normative",
      },
    },
    normativeTables: {
      title: "Tableaux normatifs",
      loadDurationClasses: {
        singular: "Classe de durée de charge",
        plural: "Classes de durée de charge",
        fields: {
          code: "Code",
          name: "Nom",
          order: "Ordre",
        },
      },
      serviceClasses: {
        singular: "Classe de service",
        plural: "Classes de service",
        fields: {
          code: "Code",
          name: "Nom",
        },
      },
      kmod: {
        title: "Valeurs k_mod",
        hint: "Facteur de modification k_mod selon la classe de service et la classe de durée de charge.",
      },
      gammaM: {
        title: "Coefficients partiels de sécurité γM",
        fields: {
          standard: "Norme",
          columnA: "Colonne A",
          columnB: "Colonne B",
        },
      },
    },
    errors: {
      familyAlreadyExists: "Une famille de vis avec ce nom existe déjà.",
      familyMissingName: "Le nom de la famille de vis est obligatoire.",
      familyNotFound: "La famille de vis est introuvable.",
      gammaMValueNotFound: "La valeur γM est introuvable.",
      kmodValueNotFound: "La valeur kmod est introuvable.",
      loadDurationClassAlreadyExists: "Une classe de durée de charge avec ce code existe déjà.",
      loadDurationClassMissingCode: "Le code de la classe de durée de charge est obligatoire.",
      loadDurationClassNotFound: "La classe de durée de charge est introuvable.",
      screwAlreadyExists: "L'article est déjà une vis.",
      screwDiameterNotFound: "Le diamètre de vis est introuvable.",
      screwNotFound: "La vis est introuvable.",
      serviceClassAlreadyExists: "Une classe de service avec ce code existe déjà.",
      serviceClassNotFound: "La classe de service est introuvable.",
      timberMaterialAlreadyExists: "Un matériau bois avec cette catégorie et cette classe existe déjà.",
      timberMaterialNotFound: "Le matériau bois est introuvable.",
      washerAlreadyExists: "Une rondelle avec ce code existe déjà.",
      washerMissingCode: "Le code de la rondelle est obligatoire.",
      washerNotFound: "La rondelle est introuvable.",
    },
    units: {
      unitMm2: "mm²",
      unitNMm2: "N/mm²",
      unitKgM3: "kg/m³",
    },
    calculation: {
      shell: {
        navLabel: "Sections du module",
        openMenu: "Ouvrir le menu",
        closeMenu: "Fermer le menu",
        inputsGroup: "Données du projet",
        resultsGroup: "Résultats",
        toolsGroup: "Outils",
        resultsView: "Vue 3D et vérifications",
        resultsSolutions: "Solutions compatibles",
        resultsPositioning: "Positionnement des vis",
        generateReport: "Générer le rapport PDF",
        reportQuestion: "Quelle version du rapport ?",
        reportCompact: "Rapport compact",
        reportCompactHint: "sans notes méthodologiques",
        reportFull: "Rapport complet",
        reportFullHint: "avec notes méthodologiques",
        exportDxf: "Exporter DXF (détail)",
        dotOk: "Complet",
        dotBad: "À vérifier",
        dotPending: "En attente",
        tutorial: "Tutorial",
      },
      tutorial: {
        dialogLabel: "Tutoriel",
        stepOf: (step: number, total: number) => `Étape ${step} sur ${total}`,
        done: "Terminé",
        redo: "Répéter",
        skip: "Passer le tutoriel",
        back: "Retour",
        next: "Suivant",
        finish: "Terminer",
        confirmTitle: "Lancer le tutoriel ?",
        confirmText:
          "Une courte démonstration des principales fonctions sur un exemple : chaque étape est exécutée pour vous, continuez avec Suivant. Les champs sont vidés pour la démonstration, qui n'utilise que des données d'exemple et montre une solution vérifiée ; à la fermeture, vos données sont restaurées telles qu'elles étaient. Le calcul n'est pas enregistré pendant le tutoriel.",
        cancel: "Annuler",
        start: "Lancer le tutoriel",
        steps: {
          intro: {
            title: "Comment remplir le calcul",
            text: "Procédez de haut en bas : à chaque étape, vous verrez la souris se déplacer sur les champs du panneau et saisir un exemple. Au démarrage, θ, b1, d1, t1, b2, d2, t2, Fn, Fv et Fa sont vides : tant que vous ne les remplissez pas, le calcul n'est pas exécuté.",
            note: "Les champs ont été vidés : à la fermeture du tutoriel, vos données sont restaurées.",
          },
          connectionType: {
            title: "2 · Type d'assemblage et angle",
            text: "Choisissez le type d'assemblage en cliquant sur l'une des huit icônes, chacune avec son nom en dessous (bois-bois, bois-bois / simple cisaillement sur deux côtés (3 éléments), bois-bois / double cisaillement (3 éléments), acier-bois avec la tôle comme élément 1 ou acier-bois / simple cisaillement sur deux côtés, avec deux tôles et le bois au centre, panneau-bois / simple cisaillement, avec un panneau à base de bois comme élément 1, panneau-bois / simple cisaillement sur deux côtés, avec deux panneaux et le bois au centre, ou panneau-bois / double cisaillement, avec le panneau au centre et le bois sur les côtés) et saisissez l'angle θ entre les deux éléments (de 1° à 180°).",
          },
          element1: {
            title: "3 · Données élément 1",
            text: "(Dans le cas à 3 éléments, c'est l'élément central.) Choisissez le matériau, l'essence et la classe (en panneau-bois uniquement des panneaux à base de bois : contreplaqué, OSB/3-OSB/4, panneaux de particules ; pour les panneaux CLT aussi les couches et le côté d'insertion : l'élément 1 uniquement côté wide). Saisissez la largeur b1, la longueur d1 (ou cochez «Élément continu» s'il n'y a pas d'extrémité) et l'épaisseur t1, en mm.",
          },
          element2: {
            title: "4 · Données élément 2",
            text: "Les mêmes données pour l'élément 2 (dans le cas à 3 éléments, elles valent aussi pour l'élément 3) : matériau, b2, d2 et épaisseur t2 en mm. Pour les panneaux CLT, vous pouvez choisir le côté wide ou narrow (narrow : une seule file de vis sur l'axe).",
          },
          threeMembers: {
            title: "5 · Assemblage à 3 éléments",
            text: "Avec «bois-bois / simple cisaillement sur deux côtés» ou «bois-bois / double cisaillement», l'élément 1 devient l'élément central (chargé, tourné de θ) ; les éléments 2 et 3 sont les deux éléments latéraux, fixes et identiques : les données de l'élément 2 valent aussi pour le 3. Dans le premier cas, les vis entrent par les deux côtés (tête sur 2 et sur 3, pointe dans l'élément central) et la résistance est doublée (deux assemblages en simple cisaillement, un par côté) ; les pointes des deux côtés peuvent se chevaucher ou, avec l'option de décaler de 1d les vis des deux côtés, le côté 3 est décalé. Dans le second, une seule vis traverse les trois éléments (tête sur l'élément 2, pointe dans l'élément 3) : double cisaillement EC5 éq. 8.7 et une seule grille de vis. Dans les deux cas apparaît la vérification au fendage de l'élément 1. Avec «acier-bois / simple cisaillement sur deux côtés», les deux tôles (éléments 1 et 3) sont de part et d'autre du bois central (élément 2) : vis des deux côtés comme dans le premier cas, fendage sur le bois. Le CLT (toujours côté wide) n'est admis que dans une seule position en bois-bois sur deux côtés (CLT-bois-CLT ou bois-CLT-bois, jamais trois CLT) et n'est pas sélectionnable en double cisaillement.",
            note: "Pour la démonstration, le type d'assemblage est réglé un instant puis remis à celui que vous avez choisi.",
          },
          panelTimber: {
            title: "6 · Assemblage panneau-bois",
            text: "Avec «panneau-bois / simple cisaillement», l'élément 1 est un panneau à base de bois (contreplaqué, OSB/3-OSB/4, panneaux de particules) côté tête de la vis, chargé et tourné de θ ; l'élément 2 est le bois, fixe. Seules les vis HBS et TBS avec tête ≥ 2·d sont utilisées. La portance locale du panneau suit l'EC5 §8.3.1.3 (l'ETA ne la fournit pas), les distances minimales le Tableau 8.2 avec a1 et a2 ×0,85 sur les deux éléments, et le kmod est la racine du produit du kmod du panneau et de celui du bois. Avec «panneau-bois / simple cisaillement sur deux côtés», les panneaux (éléments 1 et 3, identiques) sont de part et d'autre du bois central (élément 2) : vis des deux côtés, résistance doublée, fendage sur le bois, comme pour l'acier-bois sur deux côtés. Avec «panneau-bois / double cisaillement», le panneau est l'élément 1 central et le bois est sur les côtés (éléments 2 et 3, identiques) : une seule vis traverse les trois éléments, comme en bois-bois en double cisaillement (éq. 8.7), sans vérification au fendage. Le calcul s'arrête, en indiquant le motif, si l'épaisseur du panneau est inférieure au minimum (6 mm contreplaqué, 8 mm particules et OSB, dans tous les cas ≥ 1,2·d), si la tête est inférieure à 2·d ou si la classe de service n'est pas admise pour ce panneau. En double cisaillement, le CLT n'est pas sélectionnable pour les bois latéraux ; dans la vue 3D, les panneaux sont dessinés dans un bois plus foncé que le bois massif ou lamellé-collé.",
            note: "Pour la démonstration, le type d'assemblage est réglé un instant puis remis à celui que vous avez choisi.",
          },
          loads: {
            title: "7 · Charges de calcul",
            text: "Saisissez Fn (force le long de l'axe de l'élément 1), Fv (cisaillement, perpendiculaire) et Fa (arrachement, positif s'il extrait la vis) en kN. Choisissez la classe de service et la durée de la charge : γM se remplit automatiquement selon la norme.",
          },
          connection: {
            title: "8 · Choix de l'assemblage",
            text: "Choisissez la famille, le type de trou (avec ou sans pré-perçage), le diamètre et la longueur de la vis : les menus ne proposent que des vis compatibles avec les épaisseurs et les matériaux. Si l'ETA impose le pré-perçage (ρk supérieure à 550 kg/m³, 500 pour l'inox, sauf les familles HARDWOOD, LBS et KKT), le champ est bloqué sur «Avec pré-perçage» et une note en explique la raison. Avec «Utiliser une rondelle», vous pouvez allonger la vis. Si «aucune vis compatible» apparaît, modifiez les épaisseurs ou recherchez une solution.",
          },
          grid: {
            title: "9 · Grille de vis",
            text: "Définissez les rangées et les colonnes de vis : elles sont limitées au maximum permis par les distances minimales. Avec «Répartir les vis dans la zone admissible» (activé par défaut, avec les distances minimales automatiques), les rangées et colonnes choisies sont portées aux limites de la zone admissible au lieu de rester rapprochées : les espacements effectifs apparaissent sous les champs s1 et s2. Vous pouvez aussi décaler les vis, déplacer le centre de la grille ou calculer le nombre maximal de vis.",
          },
          view: {
            title: "10 · Vue 3D et vérifications",
            text: "Contrôlez la vue 3D et les vérifications (cisaillement et arrachement) : le résultat est «Admissible» ou «Non admissible». Les valeurs de calcul et les formules se trouvent dans les fiches de résultats.",
          },
          solutions: {
            title: "11 · Solutions compatibles",
            text: "Vous ne savez pas quelle vis utiliser ? Avec «Rechercher des solutions compatibles», le programme propose les vis et le nombre minimal qui satisfont toutes les vérifications. Les options «Pré-perçage» et «Rondelle» restreignent la recherche ; «Utiliser celle-ci» applique la solution.",
          },
          positioning: {
            title: "12 · Positionnement des vis",
            text: "Ici, vous voyez où tombent les vis par rapport aux bords de chaque élément : les dessins donnent les cotes du bord à l'axe de la vis et le tableau liste les distances de chaque vis. Maintenant, la fiche s'ouvre et défile vers le bas.",
            note: "La fiche revient comme elle était (ouverte ou fermée) lorsque vous passez à l'étape suivante.",
          },
          reportPreview: {
            title: "13 · Aperçu du rapport",
            text: "Avec «Afficher le rapport», vous voyez le rapport PDF que vous téléchargez, dans votre langue : vous le voyez maintenant s'ouvrir et défiler vers le bas, page après page. Avec la barre en haut, vous pouvez changer le zoom, passer en page simple ou en double page et voir l'aperçu en noir et blanc.",
            note: "S'il manque des données, les valeurs d'exemple sont saisies pour la démonstration (supprimées à la fermeture).",
          },
          reportPdf: {
            title: "14 · Rapport PDF",
            text: "Depuis Outils › Générer le rapport PDF, choisissez la version compacte ou complète ; la même commande se trouve dans la barre de titre et dans la fiche Rapport de calcul. Les données du calcul sont enregistrées automatiquement : inutile de les enregistrer à la main.",
          },
          dxf: {
            title: "15 · Exporter DXF",
            text: "Depuis Outils › Exporter DXF (détail), ou avec le bouton «Exporter DXF» de la fiche Rapport de calcul, vous téléchargez le détail de construction pour la CAO : vue de face des éléments, vues avec les cotes depuis les bords, vis avec leur diamètre réel, cartouche et tableau des positions (aussi pour l'assemblage à 3 éléments : les vis décalées du côté 3 sont sur un calque séparé ; le cartouche indique aussi les espacements effectifs lorsque les vis sont réparties). Le dessin est à l'échelle réelle 1:1 et la commande n'est active que lorsque l'assemblage est vérifié.",
            note: "Dans le tutoriel, le fichier n'est pas téléchargé : le curseur montre seulement où se trouve la commande.",
          },
        },
      },
      status: {
        pending: "En attente des données",
        verified: "Vérifié",
        notVerified: "Non vérifié",
      },
      sections: {
        tipologia: "Type de connexion et angle",
        element1: "Données élément 1",
        element1Panel: "Données élément 1 (panneau)",
        element1Central: "Données élément 1 (central)",
        element1PanelCentral: "Données élément 1 (panneau central)",
        element13Steel: "Données éléments 1 et 3 (tôles)",
        element13Panel: "Données éléments 1 et 3 (panneaux)",
        element2: "Données élément 2",
        element2Central: "Données élément 2 (bois central)",
        element23: "Données éléments 2 et 3 (latéraux)",
        loads: "Charges de calcul",
        connection: "Choix de la connexion",
        grid: "Grille de vis",
      },
      engine: {
        a4Reduce: {
          notThin: (t: string, fiveD: string) => `— non appliqué : épaisseur t=${t}mm non inférieure à 5d=${fiveD}mm.`,
          spacing: (twentyFiveD: string, values: string) =>
            `— non appliqué : nécessite a1, a3,t et a3,c ≥ 25d=${twentyFiveD}mm (actuels : ${values}).`,
        },
        block: {
          noScrew:
            "aucune vis compatible avec les épaisseurs et les matériaux saisis : modifiez les épaisseurs t1/t2 ou les matériaux, ou choisissez une autre famille/un autre diamètre.",
          length: (L: number, rule: string, values: string, washerH: number) =>
            `la longueur de la vis (L=${L} mm) n'est pas compatible avec les épaisseurs : il faut ${rule} (${values}${washerH > 0 ? `, rondelle h=${washerH} mm` : ""}).`,
          partialThread: (rule: string, aRule: { rule: string; values: string } | null, values: string) =>
            `vis à filetage partiel non compatible avec les épaisseurs (règle de compatibilité) : il faut ${rule}${aRule ? ` et ${aRule.rule} (ici ${aRule.values})` : ""} (ici ${values}).`,
          penetrationRule: (screw: string) =>
            `vis non compatible avec les épaisseurs (règle de compatibilité sur la longueur/le filetage/la pénétration pour ${screw}).`,
          minSize: (screw: string) =>
            `${screw} non compatible avec l'épaisseur/la largeur minimales des éléments (règle de compatibilité) : augmentez les épaisseurs ou choisissez un diamètre plus petit.`,
          emptyFields: (tutorial: string) => `remplissez les champs encore vides. Voir ${tutorial} pour la saisie guidée.`,
          cltNarrowPenetration: (lt1: string, pen: string, min: string, neededL: string) =>
            `pénétration dans le panneau CLT (côté étroit) L−${lt1} = ${pen} mm inférieure au minimum 10·d = ${min} mm exigé par l'ETA-11/0030 : la méthode de calcul n'est pas applicable. Augmentez la longueur de la vis (L ≥ ${lt1}+10d = ${neededL} mm) ou choisissez une vis de diamètre plus petit.`,
          penetration: (ptN: number, lt1: string, pen: string, min: string, neededL: string) =>
            `pénétration dans l'élément ${ptN} L−${lt1} = ${pen} mm non supérieure au minimum 4·d = ${min} mm : la méthode de calcul n'est pas applicable. Augmentez la longueur de la vis (L > ${lt1}+4d = ${neededL} mm) ou choisissez une vis de diamètre plus petit.`,
        },
        cltWideOnly: {
          panelSides:
            "Assemblage panneau-bois sur deux côtés : les vis pénètrent par les deux faces larges du bois central : seul le côté large est admis.",
          steelSides:
            "Assemblage acier-bois sur deux côtés : les vis pénètrent par les deux faces larges du bois central : seul le côté large est admis.",
          three:
            "Assemblage à 3 éléments : les vis traversent les éléments 2 et 3 par la face large : seul le côté large est admis.",
        },
        epsNote: {
          element1:
            '— angle entre l\'axe de la vis et le fil de l\'élément 1, utilisé UNIQUEMENT pour f<sub>h,1,k</sub> (écrasement) : différent de l\'angle force/fil utilisé pour les distances minimales a1/a2, qui reste inchangé. Décochez « automatique » pour le fixer manuellement.',
          element2: (elementRef: string) =>
            `— angle entre l'axe de la vis et le fil de <span id="lbEps2n">${elementRef}</span>, utilisé UNIQUEMENT pour f<sub>h,k</sub> (écrasement) : différent de l'angle force/fil utilisé pour les distances minimales a1/a2 (φ<sub>2</sub> ci-dessus, lié à θ/Fn/Fv), qui reste inchangé. Décochez « automatique » pour le fixer manuellement dans les deux cas.`,
          cltWide:
            'Panneau CLT, côté large (ETA-11/0030) : ε = angle entre la force et le fil de la couche extérieure, utilisé dans f<sub>h,k</sub> (écrasement comme pour le bois massif avec 110 % de la masse volumique minimale des couches). Automatique = calculé à partir de θ, F<sub>n</sub>/F<sub>v</sub> et de l\'orientation de la couche extérieure ; décochez « automatique » pour le fixer manuellement.',
        },
        layout: {
          spacingBelowMin: (spacing: string, min: string) =>
            `⚠ ${spacing} mm inférieur au minimum requis (${min} mm).`,
          lengthBelowEndDistance: (name: string, current: number, required: string) =>
            `⚠ augmentez ${name} (actuel ${current} mm) : doit être ≥ ${required} mm — la longueur de l'élément ne peut pas être inférieure à la distance d'about requise.`,
          cltHalfThickness: (d2: string, required: string, minThickness: string) =>
            `⚠ d2 = b2/2 = ${d2} mm est inférieur à ${required} mm : augmentez l'épaisseur du panneau (il faut une épaisseur totale ≥ ${minThickness} mm) ou réduisez le diamètre de la vis.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— lignes et/ou colonnes réduites à ${rows}×${cols} pour contenir dans 2·b1 = ${max} mm la distance entre les vis situées aux extrémités de la grille`,
        },
        maxScrews: {
          none: "<strong>Aucune disposition admissible</strong> — aucune vis ne respecte simultanément les distances minimales des deux éléments.",
          max: (rows: number, cols: number, total: number) =>
            `<strong>Maximum théorique : ${rows} lignes × ${cols} colonnes = ${total} vis</strong>`,
          spacing: (s1: string, s2: string) =>
            ` (entraxe s<sub>1</sub>=${s1} mm le long de l'axe de l'élément 1, s<sub>2</sub>=${s2} mm le long de l'axe de l'élément 2 — grille oblique, minimum normatif pour les deux axes)`,
          offset: (x: string, y: string) =>
            `— grille décalée du centre (décalage du centre : horiz.=${x} mm, vert.=${y} mm) pour tenir entièrement : une grille RÉGULIÈRE (même entraxe uniforme, aucun décalage) centrée sur le croisement des axes élément1/élément2 en contiendrait moins, car l'enveloppe admissible n'est pas symétrique par rapport à ce croisement.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— angle rasant (θ≤20° ou θ≥160°) : lignes et/ou colonnes réduites à ${rows}×${cols} pour contenir dans 2·b<sub>1</sub> = ${max} mm la distance entre les vis situées aux extrémités de la grille. Décochez la case correspondante pour considérer toutes les lignes/colonnes géométriquement admissibles.`,
          capped:
            "— recherche limitée à un plafond pratique de 500×500 : à l'entraxe minimal, dans cette géométrie (élément 2 à face supérieure infinie et/ou élément 1 à face F infinie), le nombre de vis admissibles pourrait être encore plus élevé.",
        },
        panel: {
          notAdmitted: (classe: string) =>
            `le panneau « ${classe} » n'est pas admis par l'ETA-11/0030 (admis : contreplaqué EN 636, OSB/3-OSB/4, panneaux de particules EN 312) : choisissez un autre matériau pour l'élément 1.`,
          noEmbedment: (d: string) =>
            `f_h,k du panneau non déterminable pour d = ${d} mm (EC5 §8.5.1.2 : pour d > 8 mm uniquement contreplaqué, OSB et panneaux de particules).`,
          noKmod: (classe: string, serviceClass: string) =>
            `k_mod du panneau « ${classe} » non défini dans EC5 Tableau 3.1 pour la classe de service ${serviceClass} : le panneau n'est pas adapté à cette classe de service.`,
          tooThin: (t1: string, minT: number, tMin: string) =>
            `épaisseur du panneau t1 = ${t1} mm inférieure au minimum max(${minT} mm ; 1,2·d) = ${tMin} mm (ETA-11/0030) : la méthode de calcul n'est pas applicable.`,
          smallHead: (dk: string, twoD: string) =>
            `diamètre de la tête dk = ${dk} mm inférieur à 2·d = ${twoD} mm : les formules EC5 §8.3.1.3 pour f_h,k des panneaux exigent une tête ≥ 2·d.`,
          onlyHbsTbs: "dans le module panneau-bois, seules les vis HBS et TBS sont admises.",
        },
        predrill: {
          reason: (rho: string, limit: number, element: number, hard: boolean, stainless: boolean) =>
            `ρk = ${rho} kg/m³ > ${limit} kg/m³ (élément ${element}${hard ? ", feuillu" : ""}, vis en ${stainless ? "acier inoxydable" : "acier au carbone"})`,
          forced: (reasons: string) => `— Pré-perçage imposé (ETA-11/0030 p. 8 et 14) : ${reasons}.`,
        },
        resistance: {
          ropeCap: (rp: string) =>
            ` — F<sub>ax</sub>/4 limité à 100 % de la part de Johansen (${rp} N, EC5 §8.2.2(2))`,
          mode: (mode: string) => `mode (${mode})`,
          steelPlate: {
            thin: "tôle mince",
            thick: "tôle épaisse",
            interp: "tôle d'épaisseur intermédiaire",
          },
        },
        results: {
          spreadNote: (s1: string, s2: string, s1Min: string, s2Min: string) =>
            `Vis réparties dans la zone admissible : s<sub>1</sub> = ${s1} mm, s<sub>2</sub> = ${s2} mm (minimums requis ${s1Min} / ${s2Min} mm) ; les vis extérieures sont aux distances minimales de bord.`,
          staggered: (lEff: string, limit: string, d: string, dir: string, element: number) =>
            `ℹ vis des deux côtés décalées : L−h = ${lEff} mm dépasse ${limit} mm, donc les vis de l'élément 3 sont décalées de 1·d = ${d} mm (direction ${dir}) par rapport à celles de l'élément ${element}, pour éviter que les pointes se chevauchent dans l'élément central.`,
          tipsOverlap: (steel3: boolean, lEff: string, limit: string, tMinus: string, fourD: string, gridTitle: string) =>
            `ℹ pointes des deux côtés chevauchantes dans ${steel3 ? "le bois" : "l'élément"} central : L−h = ${lEff} mm dépasse ${limit} mm. C'est admis (EC5 §8.3.1.1(7)) car (t − t2) = ${tMinus} mm > 4d = ${fourD} mm ; pour décaler les vis des deux côtés de 1·d, cochez la case dans le panneau ${gridTitle}.`,
          washerNet: (h: string, lEff: string) => ` — déduction faite de la rondelle (h=${h} mm) : L−h=${lEff} mm`,
          screwTooLong: (l: string, rule: string, margin: string, twoSides: boolean) =>
            `⚠ vis trop longue (L=${l}) : il faut L < ${rule} (au moins ${margin} mm de la pointe à la face opposée${twoSides ? " de l'élément central, EC5 : (t − t2) > 4d" : ""}).`,
          screwTooShortClt: (l: string, penetration: string, tenD: string, need: string) =>
            `⚠ vis trop courte (L=${l}) : pénétration dans le panneau CLT (côté étroit) ${penetration}, minimum 10·d = ${tenD} mm (ETA-11/0030) — il faut ${need}.`,
          screwTooShort: (l: string, rule: string, element: number) =>
            `⚠ vis trop courte (L=${l}) : il faut ${rule} (pénétration minimale de 4d dans l'élément ${element}).`,
          cltInfo: (thickness: string, rhok: string, classe: string) =>
            `Épaisseur totale du panneau : ${thickness} mm · ρk = ${rhok} kg/m³ (classe ${classe})`,
          cltNarrowThin: (threshold: string) =>
            ` — ⚠ inférieure à 10·d = ${threshold} mm exigés par l'ETA-11/0030 Tab. B1 pour l'insertion côté étroit.`,
        },
        screwLists: {
          noFamily: (el1: string, el2: string) =>
            `⚠ aucune famille compatible (type d'assemblage/classe de service) n'a de vis compatible avec les épaisseurs actuelles (${el1}mm, ${el2}mm) : réduisez l'épaisseur.`,
          noDiameter: (el1: string, el2: string) =>
            `⚠ aucun diamètre de cette famille n'a de longueur compatible avec les épaisseurs actuelles (${el1}mm, ${el2}mm) : réduisez l'épaisseur ou choisissez une autre famille.`,
          noLength: (lower: string, upper: string, washerH: number, el1: string, el2: string) =>
            `⚠ aucune longueur de cette famille/de ce diamètre ne se situe entre ${lower} et ${upper} mm${washerH > 0 ? ` (avec rondelle h=${washerH} mm)` : ""} (épaisseurs actuelles ${el1}mm, ${el2}mm) : réduisez l'épaisseur ou choisissez un autre diamètre.`,
        },
        threeNotes: {
          twoSides:
            "Élément 1 central (chargé, tourné de θ). Éléments 2 et 3 latéraux, fixes et identiques : les données de l'élément 2 valent aussi pour l'élément 3. Vis insérées par les deux côtés extérieurs (tête sur l'élément 2 et sur l'élément 3, pointe dans l'élément 1) : même vis et grille en miroir ; la charge se répartit par moitié sur les deux assemblages (un par côté, chacun à simple cisaillement avec un seul plan de cisaillement).",
          double:
            "Élément 1 central (chargé, tourné de θ). Éléments 2 et 3 latéraux, fixes et identiques : les données de l'élément 2 valent aussi pour l'élément 3. Une seule vis traverse les trois éléments : tête sur l'élément 2, passage dans l'élément 1, pointe dans l'élément 3 (double cisaillement, EC5 éq. 8.7) : une seule grille de vis, aucun décalage.",
          steelSides:
            "Élément 2 en bois central et fixe. Tôles d'acier sur les côtés : élément 1 et sa copie identique (élément 3), chargés et tournés de θ ; les données de l'élément 1 valent aussi pour l'élément 3. Vis insérées par les deux côtés extérieurs (tête sur les tôles, pointe dans le bois) : même vis et grille en miroir ; la charge se répartit par moitié sur les deux assemblages (un par côté, chacun à simple cisaillement avec un seul plan de cisaillement).",
          panelDouble:
            "Élément 1 = panneau à base de bois, central (chargé, tourné de θ). Éléments 2 et 3 en bois, latéraux, fixes et identiques : les données de l'élément 2 valent aussi pour l'élément 3. Une seule vis (HBS et TBS uniquement, tête ≥ 2·d) traverse les trois éléments : tête sur l'élément 2, passage dans le panneau, pointe dans l'élément 3 (double cisaillement, EC5 éq. 8.7) : une seule grille de vis, aucun décalage. Écrasement du panneau selon EC5 §8.3.1.3 / §8.5.1.2 ; distances avec a1, a2 ×0,85 ; kmod = √(kmod,panneau·kmod,bois).",
          panelSides:
            "Élément 2 en bois central et fixe. Panneaux à base de bois sur les côtés : élément 1 et sa copie identique (élément 3), chargés et tournés de θ ; les données de l'élément 1 valent aussi pour l'élément 3. Vis HBS et TBS uniquement avec tête ≥ 2·d. Vis insérées par les deux côtés extérieurs (tête sur les panneaux, pointe dans le bois) : même vis et grille en miroir ; la charge se répartit par moitié sur les deux assemblages (un par côté, chacun à simple cisaillement avec un seul plan de cisaillement). Écrasement du panneau selon EC5 §8.3.1.3 / §8.5.1.2 ; distances avec a1, a2 ×0,85 ; kmod = √(kmod,panneau·kmod,bois).",
        },
        violations: {
          edge2: (code: string) => `élément 2 : bord (${code})`,
          end2: (code: string) => `élément 2 : extrémité inférieure (${code})`,
          line1: (n: string, code: string) => `élément 1 : droite ${n} (${code})`,
          side3: (text: string) => `côté élément 3 (décalées de 1d) : ${text}`,
        },
      },
      elementRefs: {
        element1: "élément 1",
        element1Central: "élément 1, central",
        elements13Steel: "éléments 1 et 3, tôles",
        elements13Panel: "éléments 1 et 3, panneaux",
        element2: "élément 2",
        element2Central: "élément 2, bois central",
        elements23: "éléments 2 et 3",
      },
      typeSection: {
        theta: "θ — angle de l'élément 1",
        thetaUnit: "0°→F vers le bas · 180°→F vers le haut · sens antihoraire",
        connectionType: "Type de connexion",
        types: {
          legno: "bois-bois / simple cisaillement",
          legno3: "bois-bois / simple cisaillement sur deux côtés",
          legno3d: "bois-bois / double cisaillement",
          acciaio: "acier-bois / simple cisaillement",
          acciaio3: "acier-bois / simple cisaillement sur deux côtés",
          pannello: "panneau-bois / simple cisaillement",
          pannello3: "panneau-bois / simple cisaillement sur deux côtés",
          pannello3d: "panneau-bois / double cisaillement",
        },
      },
      // Names of the engine's material type keys (and of the species fallbacks of panels/steel)
      materialTypes: {
        solidwoodSoftwood: "Bois massif résineux",
        glulam: "Bois lamellé-collé",
        clt: "Panneaux CLT",
        lvl: "LVL (lamibois)",
        solidwoodHardwood: "Bois massif feuillu",
        panels: "Panneaux à base de bois",
        steel: "Acier",
        panelsSpecies: "Panneaux OSB / bois-ciment",
        steelSpecies: "Acier de construction",
      },
      element: {
        materialType: "Type de matériau",
        materialTypeUnit: "BD bois",
        species: "Essence de bois",
        class: "Classe",
        customName: "Nom du matériau",
        customNameUnit: "personnalisé",
        customNamePlaceholder: "p. ex. Bois spécial XYZ",
        rhoHint:
          "ρ<sub>k</sub> et ρ<sub>mean</sub> sont remplis depuis la base de données lors du choix du matériau et restent modifiables.",
        rhoMissing: "ρ<sub>k</sub> non disponible dans la base de données, saisir manuellement",
        fiber: (n: number) => `φ${n === 1 ? "₁" : "₂"} — orientation du fil`,
        fiberUnit1: "par rapport à l'axe de l'élément 1",
        fiberUnit2: "par rapport à l'axe de l'élément 2",
        fiberUnit23: "par rapport à l'axe des éléments 2 et 3",
        eps: (n: number) => `ε${n === 1 ? "₁" : "₂"} — angle axe de la vis/fil`,
        epsUnit1: "uniquement pour fₕ,ₖ de l'élément 1",
        epsUnit2: "uniquement pour fₕ,ₖ de l'élément 2",
        epsUnit23: "uniquement pour fₕ,ₖ des éléments 2 et 3",
        epsAuto: (n: number) => `ε<sub>${n}</sub> automatique`,
        epsAutoUnit1: "toujours 90° (vis toujours ⟂ au fil, indépendamment de θ)",
        epsAutoUnit2: "90° pour acier-bois (vis toujours ⟂ au fil, indépendamment de θ) ; dérivé de θ pour bois-bois",
        continuous: "Élément continu",
        continuousUnit: "aucune extrémité réelle : il se prolonge au-delà de l'assemblage des deux côtés",
        thickness: (n: number) => `épaisseur t${n}`,
        rhomeanUnit: "kg/m³ — pour Kₛₑᵣ",
        rhomeanMissing: "Non disponible dans la base de données pour cette classe : saisir manuellement",
        douglas: "Essence Douglas",
        douglasUnit: "sans pré-perçage : l'ETA-11/0030 exige a1 et a3 ×1,5",
        a4reduce: (n: number) => `Réduire a4,c,${n} (rive non chargée) à 3d`,
        a4reduceUnit1: "bois mince, exige a1 et a3,t,1 ≥ 25d",
        a4reduceUnit2: "bois mince, exige a1 et a3 ≥ 25d",
        a1e: (n: number) => `a1,élément${n}`,
        a2e: (n: number) => `a2,élément${n}`,
        a1Unit: "mm · espacement min. le long de l'axe",
        a2Unit: "mm · espacement min. perpendiculaire",
        a3t1: "a3,t,1 (extrémité chargée)",
        a3t2Unit: "mm · extrémité, 0°≤θ≤90°",
        a3c2Unit: "mm · extrémité, 90°<θ≤180°",
      },
      clt: {
        lato: "Côté d'insertion",
        latoOptions: {
          narrow: "Narrow (chant étroit, vis parallèle au plan)",
          wide: "Wide (face large, vis perpendiculaire au plan)",
        },
        latoHint1: "Élément 1 dans une connexion bois-bois : seul le côté wide est admis.",
        latoHint2:
          "Côté narrow : b<sub>2</sub> = épaisseur totale du panneau et d<sub>2</sub> = b<sub>2</sub>/2 (automatiques) ; t<sub>2</sub> (profondeur d'insertion) est modifiable.",
        numStrati: "Nombre de couches",
        disposizione: "Disposition des couches",
        disposizioni: {
          fisso: "Fixe (alternée 0°/90°)",
          alternati: "Alternées",
          accoppiati: "Couplées (0° couplées sur les couches extérieures)",
          zeroDoppio: "Toutes les couches à 0° doublées (11 couches seulement)",
          userDefined: "Définition utilisateur (par couche)",
        },
        spessoriMode: "Épaisseur des couches",
        spessoriModes: {
          uniforme: "Uniforme (même épaisseur pour toutes les couches)",
          differenziato: "Différenciée (0° / 90°)",
        },
        spessoreSingle: "Épaisseur d'une couche",
        spessore0: "Épaisseur des couches à 0°",
        spessore90: "Épaisseur des couches à 90°",
        classe: "Classe bois massif résineux",
        userLayers: "Couches — épaisseur, direction, classe",
        userLayersUnit: "de l'extérieur vers l'intérieur",
        layer: (i: number) => `Couche ${i}`,
        layerThickness: "Épaisseur (mm)",
        layerDirection: "Direction du fil",
        layerClass: "Classe bois massif",
      },
      loadsSection: {
        fn: "Fn, effort axial",
        fnUnit: "kN · le long de l'axe de l'élément 1",
        fv: "Fv, effort tranchant",
        fvUnit: "kN · perpendiculaire à l'axe de l'élément 1",
        fa: "Fa, effort d'arrachement",
        faUnit: "kN · le long de l'axe de la vis, positif = arrachement",
        resultantAngle: "angle résultante/fil",
        withdrawal: "arrachement",
        notYetVerified: "pas encore vérifié à l'arrachement",
        serviceClass: "Classe de service",
        loadDuration: "Durée de la charge",
        gammaMRef: "Réf. γM",
        gammaMRefUnit: "connexions/assemblages",
        standards: {
          EC5: "EC5 (Tableau 2.3 — connexions)",
          NTC2018: "NTC2018 (Tab. 4.4.III — assemblages)",
        },
        column: "Colonne",
        columns: {
          A: "A — production courante",
          B: "B — avec contrôle FPC certifié",
        },
        gammaMAuto: "γ<sub>M</sub> automatique (selon la norme sélectionnée)",
      },
      connectionSection: {
        family: "Famille de vis",
        foro: "Type de trou",
        predrilled: "Avec pré-perçage",
        nonPredrilled: "Sans pré-perçage",
        diameter: "Diamètre extérieur du filet d",
        length: "Longueur de la vis L",
        lengthUnit: "mm · depuis la base de données des vis",
        washer: "Utiliser une rondelle",
        washerUnit:
          "augmente le diamètre efficace de la tête (d<sub>h</sub>) dans la vérification de traversée de la tête (F<sub>ax,ε,Rk</sub>)",
        autoSpacing: "Distances minimales automatiques (a1, a2, a3, a4 selon la norme)",
      },
      notes: {
        panel:
          "Élément 1 = panneau à base de bois (contreplaqué, OSB/3-OSB/4, panneaux de particules), côté tête de vis, chargé et pivoté de θ. Élément 2 = bois, fixe. Uniquement des vis HBS et TBS avec tête ≥ 2·d. Ancrage dans le panneau selon EC5 §8.3.1.3 (éq. 8.20–8.22) ou §8.5.1.2 (éq. 8.36–8.37) pour d &gt; 8 mm ; distances minimales dans le panneau selon EC5 Tableau 8.2 avec α = 0 et sans dépendance à ρ<sub>k</sub>, et a<sub>1</sub>, a<sub>2</sub> ×0,85 sur panneau et bois (§8.3.1.3) ; k<sub>mod</sub> = √(k<sub>mod,panneau</sub>·k<sub>mod,bois</sub>) (§2.3.2.2).",
        continuous: (n: 1 | 2): string =>
          n === 1
            ? "— d1 et a3,t,1 ne sont plus nécessaires (ils perdent leur sens : il n'y a pas d'extrémité réelle). La limite de positionnement des vis devient directement une extrémité virtuelle, interne et cachée, égale à b2/(2·|cos(θ)|), mesurée depuis l'intersection des axes des deux éléments."
            : "— d2 et a3,t,2/a3,c,2 ne sont plus nécessaires (ils perdent leur sens : il n'y a pas d'extrémité réelle). La limite de positionnement des vis devient directement une extrémité virtuelle, interne et cachée, égale à b1/(2·|cos(θ)|), mesurée depuis l'intersection des axes des deux éléments.",
        continuousDisabled:
          "— non disponible à θ=0°/180° : les axes des deux éléments coïncident (l'élément 1 et l'élément 2 seraient alignés sur la même droite).",
        steel1Spacing:
          "— élément 1 en tôle d'acier : une tôle n'a pas de fibre propre, donc a1/a2 de l'élément 1 ne sont pas une exigence physique et n'influencent pas le pas de la grille (EC5 §8.3.1.4) : s1/s2 dépendent uniquement de a1,élément2/a2,élément2 (déjà ×0,7).",
        steel1Dist:
          "— élément 1 en tôle d'acier : a4,t,1 = a4,c,1 = 1,5·1,1·d, a3,t,1 = 1,2·1,1·d (distance minimale de perçage dans la tôle, pas Tableau 8.2 bois)",
        a3t1Rule:
          "— règle : aucune vis ne peut être plus proche de a3,t,1 de l'extrémité réelle de l'élément 1 (ligne 3 du dessin), que ce soit dans le bois ou dans la tôle d'acier — les vis de cette zone sont marquées en rouge et ne comptent pas pour la résistance.",
        foro:
          '— « Sans pré-perçage » : la formule des distances minimales est déduite automatiquement de la famille de vis sélectionnée ci-dessus (KKT pour les familles KKT, formule HBS pour les familles HBS avec d = 6…12 mm, EC5 Tableau 8.2 pour toutes les autres vis et diamètres, conformément à l\'ETA-11/0030).',
        plateHw:
          "— famille HBS PLATE / LBS HARDWOOD EVO pour assemblage bois-bois : l'ETA-11/0030 exige a1 et a2 ×1,5 sur les deux éléments.",
        myk:
          "— My,k absent de la base de données des vis pour cette famille/diamètre : certains modes de rupture de Johansen ne sont pas pris en compte, le résultat peut ne pas être conservatif. Vérifier ou compléter les données dans Screw.xlsx.",
        limitGrid:
          "— limite désactivée : vous pouvez saisir plus de vis que géométriquement admissible ; celles hors de l'enveloppe restent marquées en rouge et dans le panneau ci-dessous, à vos risques",
      },
      gridSection: {
        rows: "rangées (le long de l'axe de l'élément 1)",
        rowsUnit: "max. selon θ/vis",
        cols2: "colonnes (le long de l'axe de l'élément 2)",
        cols23: "colonnes (le long de l'axe des éléments 2 et 3)",
        cltRowNote: "Élément 2 en CLT, côté narrow : une seule rangée de vis, dans l'axe de l'épaisseur de l'élément 2.",
        s1Unit: "mm · le long de l'axe de l'élément 1",
        s2Unit2: "mm · le long de l'axe de l'élément 2",
        s2Unit23: "mm · le long de l'axe des éléments 2 et 3",
        offsetX: "Décalage du centre, horizontal",
        offsetY: "Décalage du centre, vertical",
        spread: "Répartir les vis dans la zone admissible (espacement maximal, uniquement avec distances minimales automatiques)",
        stagger: "Décaler de 1d les vis de la rangée parallèle au fil (évite la réduction de n<sub>ef</sub>)",
        staggerTwoSides:
          "Décaler de 1d les vis des deux côtés si les pointes se chevauchent (sinon elles restent superposées, à condition que (t − t<sub>2</sub>) &gt; 4d)",
        maxScrews: "Calculer le nombre maximal de vis",
        applyMax: "Appliquer rangées/colonnes",
        limitGrid: "Limiter rangées/colonnes au nombre maximal admissible (selon θ et le diamètre de la vis)",
        limitGrazing:
          "Avec un angle rasant (θ≤20° ou θ≥160°) limiter à 2×b<sub>1</sub> la distance entre les vis situées aux extrémités de la grille",
      },
      view: {
        title: "Vue 3D",
        showDims: "Afficher les cotes",
        branchMeta: (theta: string, branch: string) => `θ = ${theta}°  ·  branche ${branch}`,
        branchPending: "branche —",
        hintControls:
          "Glisser = tourner · molette = zoom · bouton droit = déplacer · menu Vue = vues prédéfinies · menu Éléments = afficher ou masquer les composants.",
        hintTail: "le contexte géométrique ; les vis sont le produit vérifié par le calcul. Les vis hors enveloppe sont en rouge.",
        hintSubjects: {
          two: "Les deux éléments en bois sont",
          three: "Les trois éléments en bois sont",
          panel: "Le panneau et le bois sont",
          steel3: "Les trois éléments (deux tôles et le bois) sont",
          panel3: "Les trois éléments (deux panneaux et le bois) sont",
        },
        legend: {
          el2: "élément 2 (bois, fixe)",
          el2Central: "élément 2 (bois central, fixe)",
          el23: "éléments 2 et 3 (bois, fixes, identiques)",
          el1: "élément 1 (tourné de θ)",
          el1Panel: "élément 1 (panneau, tourné de θ)",
          el1Central: "élément 1 (central, tourné de θ)",
          el1PanelCentral: "élément 1 (panneau central, tourné de θ)",
          el13Steel: "éléments 1 et 3 (tôles, tournés de θ)",
          el13Panel: "éléments 1 et 3 (panneaux, tournés de θ)",
          screwOk: "vis admissible",
          screwBad: "vis non admissible",
          dims: "cotes (valeurs saisies)",
          fn: "Fn (axial)",
          fv: "Fv (tranchant)",
          fa: "Fa (arrachement, le long de l'axe de la vis)",
        },
        toolbar: {
          views: {
            iso: "Vue 3D",
            front: "Élévation",
            top: "Vue en plan",
            side: "Vue latérale",
          },
          menuView: "Vue",
          menuElements: "Éléments",
          resetView: "Réinitialiser la vue",
          elements: {
            element1: "Élément 1",
            elements13: "Éléments 1 et 3",
            element2: "Élément 2",
            elements23: "Éléments 2 et 3",
            screws: "Vis",
            forces: "Forces",
            grid: "Grille",
            solid: "Solide",
            ghost: "Semi-transparent",
            hidden: "Masqué",
          },
        },
        capture: {
          legibilityWarning: (lines: string) =>
            `Attention : certaines cotes des vues du rapport pourraient ne pas être lisibles (${lines}).`,
          legibilityLine: (view: string, clipped: number, overlapping: number) =>
            `${view} : ${clipped} cotes coupées, ${overlapping} superposées`,
        },
      },
      stats: {
        layout: "Disposition des vis",
        layoutOk: "Admissible",
        layoutBad: "Non admissible",
        combined: "Charge combinée (EC5 8.28)",
        fvRkScrew: "F<sub>v,Rk</sub> par vis",
        nefAxial: "n efficace (axial)",
        nefShear: "n efficace (cisaillement)",
        admissibleScrews: (ok: number, total: number, twoSides: boolean) =>
          `vis adm. ${ok}/${total}${twoSides ? " par côté (×2 côtés)" : ""}`,
        fvRkUnion: "F<sub>v,Rk</sub> assemblage",
        totalKn: "kN au total",
        faxRd: "F<sub>ax,Rd</sub>",
        kmodGammaM: "k<sub>mod</sub> · γ<sub>M</sub>",
        fvEd: "F<sub>v,Ed</sub>",
        fvRdUnion: "F<sub>v,Rd</sub> assemblage",
        kser: "K<sub>ser</sub> par plan de cisaillement",
        kserUnit: "(ELS)",
        notAvailable: "N.D.",
        shearCheck: "Vérification au cisaillement F<sub>v,Ed</sub>/F<sub>v,Rd</sub>",
        axialCheck: "Vérification à l'arrachement F<sub>ax,Ed</sub>/F<sub>ax,Rd</sub>",
        splitCheck: "Vérification au fendage de l'élément central",
        verified: "vérifié",
        notVerified: "NON vérifié",
      },
      violations: {
        title: "Vis hors enveloppe",
        allOk: "Toutes les vis respectent les distances minimales requises.",
        blockedPrefix: "Calcul non effectué : ",
      },
      solutions: {
        title: "Solutions compatibles",
        intro:
          "Pour chaque vis actuellement compatible (famille/diamètre/longueur, après les filtres d'épaisseurs/classe de service/type de connexion), recherche le nombre minimal de vis (rangées×colonnes, grille centrée) qui satisfait toutes les vérifications actives (cisaillement ; charge combinée et arrachement uniquement si F<sub>a</sub>&gt;0). Calcul lourd : il démarre sur demande ; après la première recherche il se met à jour tout seul à chaque modification des données d'entrée (le tableau précédent est effacé). Pendant la recherche, « Distances minimales automatiques » et « Décalage du centre »=0 sont temporairement forcés pour toutes les vis candidates, puis l'état actuel est rétabli.",
        predrilled: "Pré-perçage",
        washer: "Rondelle",
        optionsHint:
          "Sans coche : uniquement des solutions sans pré-perçage et sans rondelle (les réglages du formulaire ne comptent pas ; les vis pour lesquelles l'ETA impose le pré-perçage sont tout de même vérifiées avec pré-perçage). Avec une coche : uniquement des solutions utilisant cette option (avec « Rondelle » toutes les rondelles compatibles sont essayées).",
        search: "Rechercher des solutions compatibles",
        preparing: "Préparation...",
        progress: (processed: number, total: number, found: number) =>
          `Évaluation en cours : ${processed}/${total} vis candidates (${found} solutions trouvées jusqu'à présent)...`,
        missingTitle: (count: number) =>
          count === 1
            ? "Impossible de générer les solutions : remplir le champ indiqué."
            : "Impossible de générer les solutions : remplir les champs indiqués.",
        missingPanel: (panel: string) => `— panneau « ${panel} »`,
        none: (candidates: number) =>
          `Aucune solution trouvée parmi ${candidates} vis candidates : aucune combinaison famille/diamètre/longueur/grille ne satisfait toutes les vérifications dans la limite du nombre maximal de vis géométriquement admissible.`,
        summary: (found: number, candidates: number, shown: number) =>
          `${found} solutions trouvées parmi ${candidates} vis candidates évaluées — les ${shown} premières sont affichées, triées par % cisaillement (puis % combinée, puis % arrachement).`,
        selectNew:
          "La configuration actuelle n'est plus valide avec les données saisies : <b>sélectionner la nouvelle solution compatible</b> dans le tableau (bouton « Utiliser celle-ci »).",
        columns: {
          family: "Famille",
          grid: "Rangées×colonnes (tot.)",
          options: "Options",
          shear: "η cisaillement",
          combined: "η combinée",
          withdrawal: "η arrachement",
        },
        predrilledOption: "pré-perçage",
        apply: "Utiliser celle-ci",
      },
      formulas: {
        noDiameter:
          'donnée non disponible (aucun diamètre de vis valide sélectionné — voir l\'avertissement ci-dessus « Diamètre extérieur du filetage »).',
        staggerNef: (n: number) =>
          `Décalage ≥1d actif (EC5 Tableau 8.1, note 8) : aucune réduction, n<sub>ef</sub> = n = ${n} (vis admissibles).`,
        nefAxial: (n: number, pow: string, ninety: string, result: string) =>
          `n<sub>ef,axial</sub> = max(n<sup>0,9</sup> ; 0,9·n) — ETA-11/0030 (vis autoperceuses, rangée de n vis parallèle à la charge) — n=${n} → max(${pow} ; ${ninety}) = ${result}`,
        staggerNefShear: (n: number) =>
          `Décalage ≥1d actif (EC5 Tableau 8.1, note 8) : aucune réduction, n<sub>ef,cisaillement</sub> = n = ${n}.`,
        rowParallel: (
          nRow: number,
          a1: string,
          a1d: string,
          element: number,
          reps: number,
          extra: string,
        ) =>
          `Rangée de ${nRow} vis parallèle à la charge (pas effectif a1=${a1}mm=${a1d}d, élément ${element}), répétée ${reps} fois (rangées indépendantes)${extra}`,
        table81Steel: (a1Eff: string, ratio: string) =>
          ` (pour le Tableau 8.1, acier-bois : a<sub>1</sub>/0,7 = ${a1Eff}mm=${ratio}d)`,
        kefLine: (kef: string, n: number, nef0: string) =>
          `k<sub>ef</sub> = ${kef} (EC5 Tableau 8.1) → n<sub>ef,0</sub> = n<sup>k<sub>ef</sub></sup> = ${n}<sup>${kef}</sup> = ${nef0}`,
        angleLine: (angle: string, panel: string, nef0: string, nRow: number, result: string) =>
          `angle effectif effort/fibre = ${angle}°${panel} → n<sub>ef,cisaillement</sub> = n<sub>ef,0</sub> + (n − n<sub>ef,0</sub>)·(angle/90°) = ${nef0} + (${nRow} − ${nef0})×(${angle}/90) = ${result}`,
        panelNoInterp: " (panneau-bois : pas d'interpolation avec l'angle, comme dans le rapport de référence)",
        totalShear: (reps: number, result: string, crit: string) =>
          `n<sub>ef,cisaillement</sub> (total) = ${reps} × ${result}${crit}`,
        critElement: (element: number) => ` (élément ${element}, plus grand nombre de vis en rangée)`,
        doubleNote: "double cisaillement : la même vis traverse les trois éléments, 2 plans de cisaillement",
        steelNote: (sides: string) => `deux assemblages à simple cisaillement, un par côté : ${sides} avec bois central 2`,
        threeNote: "deux assemblages à simple cisaillement, un par côté : éléments 2 et 3 avec élément 1",
        sidePanels: "panneau 1 et panneau 3",
        sidePlates: "tôle 1 et tôle 3",
        govPlane: "déterminant",
        kserNoRho:
          "ρ<sub>mean</sub> non disponible (N.D. dans la base de données pour cette classe) : saisir manuellement ρ<sub>mean</sub> pour calculer K<sub>ser</sub>.",
        kserLvlNote: (family: string) =>
          `<span class="report-formula">ETA-11/0030 — vis ${family} en bois LVL de résineux, 5≤d≤10mm, indépendant de l'angle ε</span>`,
        kserPredrilled: "pré-percé",
        kserNotPredrilled: "non pré-percé",
        kserPanelRho: (rhok: string, rhoMean: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>panneau</sub>·ρ<sub>mean,2</sub>) = √(${rhok}×${rhoMean}) = ${rhoM} kg/m³ (ρ<sub>mean</sub> du panneau supposée égale à ρ<sub>k</sub>)`,
        kserMean2: (rhoM: string) => `ρ<sub>mean,2</sub> = ${rhoM} kg/m³`,
        kserTimberRho: (rho1: string, rho2: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>mean,1</sub>·ρ<sub>mean,2</sub>) = √(${rho1}×${rho2}) = ${rhoM} kg/m³`,
        kserSteelNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Tableau 7.1, comme pour les clous ${predrilled} — ${rho} ; ×2,0 pour les assemblages acier-bois (EC5 §7.1(3))</span>`,
        kserTimberNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Tableau 7.1, comme pour les clous ${predrilled} — ${rho}</span>`,
      },
      positioning: {
        title: "Positionnement des vis par rapport aux bords",
        meta: "élément 1 et élément 2, cotes en mm",
        metaThree: "élément 1 (central) et éléments 2 et 3, cotes en mm",
        metaSteel3: "éléments 1 et 3 (tôles) et élément 2 (bois central), cotes en mm",
        metaPanel3: "éléments 1 et 3 (panneaux) et élément 2 (bois central), cotes en mm",
        notAvailable: "Positionnement des vis non disponible : calcul non effectué.",
        notAvailableSee: "Positionnement des vis non disponible : calcul non effectué (voir l'avertissement à droite).",
        none: "Aucune vis admissible à positionner : modifier lignes/colonnes ou les données d'entrée.",
        missingDims: "Compléter les données géométriques pour voir le positionnement des vis.",
        captionElements13Plates: "Éléments 1 et 3 (tôles identiques)",
        captionElements13Panels: "Éléments 1 et 3 (panneaux identiques)",
        captionElement1: "Élément 1",
        suffixPlate: " (tôle)",
        suffixPanel: " (panneau)",
        suffixCentral: " (central)",
        captionView1: " — vue dans son propre plan : extrémité en bas (opposée à F), F vers le haut",
        captionView2: " — vue dans son propre plan : extrémité inférieure en bas",
        captionElements23: "Éléments 2 et 3 (identiques)",
        captionElement2Timber: "Élément 2 (bois central)",
        captionElement2: "Élément 2",
        continuousNoEnd: " · élément continu : aucune cote d'extrémité",
        axisElement1: "élément 1",
        axisElements23: "éléments 2 et 3",
        axisElement2: "élément 2",
        note: (p: {
          s1: string;
          s2: string;
          axis2: string;
          spread: boolean;
          s1Min: string;
          s2Min: string;
          rows: number;
          cols: number;
          staggered: boolean;
          nShow: number;
          nTotal: number;
          threeD: boolean;
          twoSides: boolean;
          steel3: boolean;
          panel1: boolean;
          stagger: boolean;
          d: string;
          dir: string;
        }) =>
          `Cotes en mm, du bord de l'élément à l'axe de la vis. Pas de la grille : s<sub>1</sub> = ${p.s1} mm (le long de l'axe de l'élément 1), s<sub>2</sub> = ${p.s2} mm (le long de l'axe de ${p.axis2})${p.spread ? ` — vis réparties aux limites de la zone admissible (pas minimaux requis ${p.s1Min} / ${p.s2Min} mm)` : ""} · ${p.rows} × ${p.cols} vis${p.staggered ? ", décalées" : ", alignées (non décalées)"} · ${p.nShow} admissibles${p.nShow < p.nTotal ? ` sur ${p.nTotal} ; les vis hors de l'enveloppe sont en rouge et ne doivent pas être installées` : ""}${p.threeD ? " · éléments 2 et 3 identiques : la même vis traverse les trois éléments (une seule grille)" : ""}${p.twoSides ? ` · éléments 2 et 3 identiques : les vis de l'élément 3 sont la copie symétrique de celles de l'élément 2 (${p.nShow} par côté, ${2 * p.nShow} au total)${p.stagger ? `, décalées de 1·d = ${p.d} mm (direction ${p.dir}) car L−h dépasse t2 + t1/2 : cercles vides = vis de l'élément 3 (non cotées)` : ""}` : ""}${p.steel3 ? ` · ${p.panel1 ? "panneaux 1 et 3 identiques" : "tôles 1 et 3 identiques"} : les vis du côté 3 sont la copie symétrique de celles du côté 1 (${p.nShow} par côté, ${2 * p.nShow} au total)${p.stagger ? `, décalées de 1·d = ${p.d} mm (direction ${p.dir}) car L−h dépasse t1 + t2/2 : cercles vides = vis de la tôle 3 (non cotées)` : ""}` : ""}.`,
        tableNo: "N°",
        tableElements13: (plates: string) => `Éléments 1 et 3 (${plates})`,
        tableElement1: "Élément 1",
        tableElements23: "Éléments 2 et 3",
        tableElement2: "Élément 2",
        tableDistance: "distance au bord [mm]",
        tableEnd: "extrémité",
        tableLeft: "gauche",
        tableRight: "droite",
        tableMinMaxTitle: "Distance minimale/maximale au bord [mm]",
        tableMin: "minimale",
        tableMax: "maximale",
        tableFromEnd1: "Élément 1 — depuis l'extrémité",
        tableFromLeft1: "Élément 1 — depuis le bord gauche",
        tableFromLeft2: (el2: string) => `${el2} — depuis le bord gauche`,
        tableFromBottom2: (el2: string) => `${el2} — depuis l'extrémité inférieure`,
        tableMoreThan40:
          "Au-delà de 40 vis, les coordonnées de chaque vis ne sont pas listées : les cotes d'encombrement de la figure et les pas de grille s<sub>1</sub>/s<sub>2</sub> s'appliquent.",
        platesWord: "tôles",
        panelsWord: "panneaux",
      },
      report: {
        title: "Rapport de calcul",
        note: "Aperçu du rapport PDF imprimé (données, vues, résultats, notes méthodologiques).",
        versionLabel: "Version du rapport",
        full: "Complète",
        fullHint: "(avec notes méthodologiques)",
        compact: "Compacte",
        compactHint: "(sans notes méthodologiques)",
        show: "Afficher le rapport",
        hide: "Masquer le rapport",
        generating: "Génération du rapport…",
        generatePdf: "Générer le rapport PDF",
        exportDxf: "Exporter DXF",
        viewer: {
          toolbar: "Commandes de la visionneuse du rapport",
          previousPage: "Page précédente",
          pageNumber: "Numéro de page",
          nextPage: "Page suivante",
          zoomOut: "Dézoomer",
          zoom: "Zoom",
          zoomIn: "Zoomer",
          fitWidthOption: (pct: number) => `Ajuster la largeur (${pct} %)`,
          fitPageOption: (pct: number) => `Ajuster la page (${pct} %)`,
          fitWidth: "Ajuster à la largeur",
          fitPage: "Ajuster à la page",
          continuous: "Défilement continu",
          singlePage: "Page unique",
          twoPages: "Deux pages côte à côte",
          blackWhite: "Aperçu en noir et blanc",
          blackWhiteShort: "N/B",
          paginating: "Mise en page du rapport…",
        },
        previewError: "Impossible de générer l'aperçu du rapport : réessayez.",
      },
    },
  },

  envelopeDesignerModule: {
    resultCodes: {
      errors: {
        INVALID_REQUEST_BODY: "Corps de requête non valide.",
        INVALID_REQUEST_UNKNOWN_ACTION: "Requête non valide : action inconnue.",
        INVALID_REQUEST_NO_LAYERS: "Requête non valide : aucune couche à calculer.",
        INVALID_REQUEST_NO_MATERIALS: "Requête non valide : les données des matériaux sont manquantes.",
        INVALID_REQUEST_NO_MONTHLY_CLIMATE: "Requête non valide : le climat mensuel (12 températures et pressions de vapeur) est manquant - chargez un fichier EPW.",
        INVALID_REQUEST_OUTSIDE_SIDE: "Glaser mensuel : la requête n'indique pas le côté extérieur.",
        LAYERS_TOO_MANY: (p: { max: number }) => `Requête non valide : ${p.max} couches au maximum.`,
        MONTHLY_OUTSIDE_TEMPERATURE_INVALID: "Donnée non valide : une température extérieure moyenne mensuelle est en dehors de -60 à 60 °C.",
        MONTHLY_OUTSIDE_VAPOUR_PRESSURE_INVALID: "Donnée non valide : une pression de vapeur extérieure moyenne mensuelle n'est pas comprise entre 0 et 10000 Pa.",
        MONTHLY_METHOD_UNKNOWN: "Donnée non valide : méthode de calcul inconnue.",
        MONTHLY_CONDENSATION_TOLERANCE_INVALID: "Donnée non valide : la tolérance de condensation doit être 0 (exacte) ou 1e-9.",
        MONTHLY_SERVER_ERROR: "Glaser mensuel : l'analyse a échoué sur le serveur.",
        CALCULATION_SERVER_ERROR: "Le calcul a échoué sur le serveur.",
        HUMIDITY_CLASS_INVALID: "Donnée non valide : la classe d'humidité UNI EN ISO 13788 doit être 1, 2, 3, 4 ou 5.",
        INSIDE_TEMPERATURE_INVALID: "Donnée non valide : la température intérieure doit être un nombre compris entre -10 et 50 °C.",
        INSIDE_RELATIVE_HUMIDITY_INVALID: "Donnée non valide : l'humidité relative intérieure doit être un nombre supérieur à 0 et au plus égal à 100 %.",
        EN15026_MONTHLY_VALUES_MISSING: "Donnée non valide : le climat intérieur EN 15026 Annex D nécessite les valeurs mensuelles calculées à partir du fichier climatique (chargez de nouveau le fichier EPW).",
        ASHRAE160_UNAVAILABLE: "Climat intérieur ASHRAE 160 : pas encore disponible - les valeurs de sa méthode simplifiée ne sont pas vérifiées.",
        INSIDE_MODEL_UNKNOWN: "Donnée non valide : modèle de climat intérieur inconnu.",
      },
      warnings: {} as Record<string, { title: string; detail: string }>,
    },
    inputErrors: {
      layer: (position: number, material: string) => `Couche ${position} (${material})`,
      stud: (layer: string, material: string) => `${layer}, montant ${material}`,
      bad: { nan: "pas un nombre", infinite: "infini", negative: (value: number) => `négatif (${value})`, zero: "0" },
      messages: {
        layerThickness: (subject: string, got: string) => `${subject} : l'épaisseur doit être supérieure à 0 (valeur : ${got}).`,
        layerLambda: (subject: string, got: string) => `${subject} : la conductivité thermique λ doit être supérieure à 0 (valeur : ${got}).`,
        layerMu: (subject: string, got: string) => `${subject} : le facteur de résistance à la vapeur μ doit être supérieur à 0 (valeur : ${got}).`,
        studWidth: (subject: string, got: string) => `${subject} : la largeur doit être supérieure à 0 (valeur : ${got}).`,
        studSpacing: (subject: string, got: string) => `${subject} : l'espacement doit être supérieur à 0 (valeur : ${got}).`,
        studDepth: (subject: string, got: string) => `${subject} : la profondeur doit être supérieure à 0 (valeur : ${got}).`,
        studDepthExceeds: (subject: string, _got: string, depthMM: number, thicknessMM: number) =>
          `${subject} : la profondeur (${depthMM} mm) dépasse l'épaisseur de sa couche (${thicknessMM} mm).`,
        studLambda: (subject: string) => `${subject} : le matériau n'a pas de conductivité thermique λ valide.`,
        studMu: (subject: string) => `${subject} : le matériau n'a pas de facteur de résistance à la vapeur μ valide.`,
        rsi: (_subject: string, got: string) => `La résistance superficielle intérieure Rsi doit être supérieure à 0 (valeur : ${got}).`,
        rse: (_subject: string, got: string) => `La résistance superficielle extérieure Rse doit être supérieure à 0 (valeur : ${got}).`,
        tInt: (_subject: string, got: string) => `La température intérieure doit être un nombre (valeur : ${got}).`,
        tExt: (_subject: string, got: string) => `La température extérieure doit être un nombre (valeur : ${got}).`,
        rhInt: (_subject: string, got: string) => `L'humidité relative intérieure doit être comprise entre 0 et 100 % (valeur : ${got}).`,
        rhExt: (_subject: string, got: string) => `L'humidité relative extérieure doit être comprise entre 0 et 100 % (valeur : ${got}).`,
      },
    },
    admin: {
      materials: "Matériaux",
      examples: "Compositions d'exemple",
      manufacturer: "Fabricant",
      variable: "variable",
      deleteMaterialQuestion: "Voulez-vous vraiment supprimer ce matériau ? Un matériau utilisé par un calcul ou un exemple ne peut pas être supprimé : masquez-le plutôt.",
      material: "matériau",
      subcategory: "Sous-catégorie",
      category: "Catégorie",
      categories: "Catégories",
      subcategories: "Sous-catégories",
      condCategories: "Catégories de condensation",
      rule: "Règle",
      condCategory: "Catégorie de condensation (EN ISO 13788)",
      hatch: "Hachure",
      thicknessMode: "Mode d'épaisseur",
      defaultThickness: "Épaisseur de référence",
      knownThicknesses: "Épaisseurs disponibles (séparées par des virgules)",
      minThickness: "Épaisseur minimale",
      maxThickness: "Épaisseur maximale",
      sdCurveHint: "Membranes à Sd variable uniquement : points avec HR en fraction 0-1 et Sd en mètres.",
      sectionMembrane: "Membrane",
      sectionTestDatabase: "Base de données d'essai (valeurs hygriques du fichier)",
      sectionCurves: "Courbes mesurées",
      sectionProvenance: "Sources des données",
      sdVariableMin: "Sd variable, min [m]",
      sdVariableMax: "Sd variable, max [m]",
      dryCupPerm: "Perméance dry-cup [US perm]",
      curvesHint: "Chaque courbe est une liste JSON de points, avec les clés indiquées dans son champ (vide : pas de courbe).",
      moistureCurve: "Courbe de sorption w(HR) [HR %, w kg/m³]",
      lambdaMoistureCurve: "λ(w) [w m³/m³, λ W/(m·K)]",
      muMoistureCurve: "μ(w) [w m³/m³, μ -]",
      sdHumidityCurve: "Courbe Sd variable avec l'humidité Sd(HR) [HR 0-1, Sd m]",
      suctionCurve: "Courbe de succion [log10 p_C Pa, w kg/m³]",
      liquidConductivityCurve: "Conductivité liquide [w kg/m³, log10 K_l s]",
      datasheetUrl: "Fiche technique (URL)",
      productPageUrl: "Page du produit (URL)",
      datasheetChecked: "Fiche vérifiée le",
      formerlyNamed: "Ancien nom",
      sourceDatabase: "Base de données d'origine",
      sourceLaboratory: "Laboratoire",
      sourceDate: "Date de la mesure",
      sourceFile: "Fichier d'origine",
      assumedFields: "Valeurs supposées (absentes de la fiche)",
      derivedFields: "Valeurs dérivées (calculées à partir de la fiche)",
      dataNote: "Note sur la fiche",
      dataNoteLambda: "λ de la fiche [W/(m·K)]",
      invalidCurve: (curve: string) => `${curve} : n'est pas une liste de points valide.`,
      label: "Libellé",
      element: "Élément",
      structure: "Structure",
      regions: "Régions",
      layerCount: "Couches",
      default: "par défaut",
      deleteExampleQuestion: "Voulez-vous vraiment supprimer cet exemple ?",
      example: "exemple",
      description: "Description",
      layersInsideOut: "Couches (intérieur → extérieur)",
      addBridge: "Ajouter montant/chevron",
      width: "largeur",
      spacing: "espacement libre",
      fullDepth: "toute l'épaisseur",
      addLayer: "Ajouter une couche",
    },
    errors: {
      materialAlreadyExists: "Un matériau avec le même nom et le même fabricant existe déjà.",
      materialInUse: "Le matériau est utilisé par un calcul ou un exemple : masquez-le au lieu de le supprimer.",
      exampleNotFound: "Exemple introuvable.",
      invalidExample: "Composition d'exemple non valide.",
      exampleAlreadyExists: "Un exemple avec le même code existe déjà.",
      invalidMaterial: "Matériau personnalisé non valide.",
      materialNotFound: "Une couche utilise un matériau absent du catalogue.",
      customMaterialNotFound: "Une couche utilise un matériau personnalisé inexistant.",
      invalidLayer: "Couche non valide.",
      invalidBridge: "Pont thermique non valide.",
      condCategoryNotFound: "Catégorie de condensation inconnue.",
      categoryAlreadyExists: "Une catégorie avec le même code existe déjà.",
      categoryInUseByMaterial: "Des matériaux sont de cette catégorie : masquez-la plutôt que de la supprimer.",
      categoryInUseBySubcategory: "Des sous-catégories sont de cette catégorie : supprimez-les ou déplacez-les d'abord.",
      subcategoryAlreadyExists: "Une sous-catégorie avec le même code existe déjà.",
      subcategoryInUseByMaterial: "Des matériaux sont de cette sous-catégorie : masquez-la plutôt que de la supprimer, et déplacez-la uniquement vers la catégorie de ses matériaux.",
    },
    nav: {
      project: "Projet",
      data: "Données",
      dataTitle: "Données : projet, site, élément, analyse et mode de calcul",
      stratigraphy: "Stratigraphie",
      stratigraphyTitle: "Stratigraphie : couches et matériaux de la paroi",
      report: "Rapport",
      reportTitle: "Rapport : aperçu et impression",
      general: "Général",
      materialsDatabase: "Base de données des matériaux",
      materialsDatabaseTitle: "Base de données des matériaux : parcourir, filtrer et ajouter des matériaux",
      settings: "Paramètres",
      settingsTitle: "Paramètres : unités, normes, calcul et raccourcis clavier",
      tutorial: "Tutoriel",
      tutorialTitle: "Tutoriel - une démonstration des principales fonctions",
      toggleSidebar: "Réduire/développer la barre latérale",
      analysisResults: "Analyse et résultats",
      analysisResultsTitle: "Analyse et résultats : l'analyse statique et l'analyse quasi-dynamique",
      quasiDynamic: "Quasi-dynamique",
      quasiDynamicLockedTitle: "Analyse quasi-dynamique : non disponible dans cette bêta",
      openQuasiDynamicTitle: "Ouvrir aussi l'analyse quasi-dynamique ?",
      openQuasiDynamicMessage: "L'analyse statique a été sélectionnée dans l'onglet Données. Souhaitez-vous également poursuivre avec l'analyse quasi-dynamique ?",
      openQuasiDynamicConfirm: "Oui, l'ouvrir",
      reportLockedNote: "Disponible une fois l'analyse statique calculée pour cette stratigraphie",
      reportLockedToast: "Le rapport : disponible une fois l'analyse statique calculée pour cette stratigraphie.",
      completeDataFirst: "Complétez d'abord l'onglet Données (projet, élément, analyse)",
      addLayerFirst: "Ajoutez d'abord au moins une couche à la stratigraphie",
      openStratigraphyFirst: "Ouvrez d'abord la Stratigraphie",
      statusTodo: "À compléter",
      statusOk: "Complet",
      statusWarn: "Satisfait, avec un avertissement",
      statusBad: "Au moins une vérification non satisfaite",
      statusBusy: "Calcul en cours…",
      undoPrevious: "Annuler l'action précédente",
      showRecentActions: "Afficher les actions récentes",
      redoNext: "Rétablir l'action suivante",
      recentUndoHistory: "Historique récent des modifications",
      undoMultiple: "Annuler plusieurs actions",
      noActions: "Aucune action",
      historyEmpty: "Vos modifications récentes apparaîtront ici.",
      historyFoot: "Sélectionnez une action pour revenir à l'état immédiatement antérieur. Survolez-la pour afficher le détail complet de la modification.",
    staticAnalysis: "Statique",
    staticAnalysisTitle: "Analyse statique : résultats (1D ou 2D), Glaser mensuel, paramètres",
    quasiDynamicTitle: "Analyse quasi-dynamique : données d'entrée et résultats",
    },
    preferences: {
      preferences: "Préférences",
      preferencesTitle: "Préférences : unités et normes",
      units: "Unités",
      unitsTitle: "SI : métrique. Imperial : toutes les valeurs converties (diffusion de vapeur en perméance, US perm). UK : SI, sauf la diffusion de vapeur, exprimée en résistance à la vapeur en MN·s/g. Les calculs eux-mêmes sont toujours effectués en SI - seuls l'affichage et l'impression changent.",
      ukTitle: "Les mêmes unités SI que partout ailleurs, sauf la diffusion de vapeur, exprimée en résistance à la vapeur en MN·s/g - l'unité employée par la physique du bâtiment au Royaume-Uni",
      unitsHint: "Enregistrées avec le calcul ; un nouveau calcul démarre avec les dernières unités choisies (sinon SI).",
      standards: "Normes",
      standardEuTitle: "Les menus affichent les options UNI / EN (EN 15026, UNI EN ISO 13788)",
      standardNaTitle: "Les menus affichent les options ASHRAE (ASHRAE 160)",
      standardsHint: "Les menus proposant des alternatives selon la norme (pluie dans l'analyse quasi-dynamique, climat intérieur de l'analyse Glaser mensuelle) n'affichent que les options de la norme choisie.",
    },
    settings: {
      changesPending: "Modifications pas encore appliquées - Terminé les applique",
      changesApplyOnDone: "Les modifications s'appliquent en appuyant sur Terminé",
      done: "Terminé",
      title: "Paramètres",
      keyboardShortcuts: "Raccourcis clavier",
      solver2d: "Solveur 2D",
      climate: "Climat",
      quasiDynamic: "Quasi-dynamique",
      quasiDynamicLockedTitle: "Analyse quasi-dynamique : non disponible dans cette bêta",
      quasiDynamicLockedLabel: "Quasi-dynamique : non disponible dans cette bêta",
      about: "À propos",
      shortcutsHint: "Cliquez sur un champ, puis appuyez sur la combinaison de touches souhaitée.",
      resetToDefaults: "Rétablir les valeurs par défaut",
      solver2dHeading: "Méthode hygrothermique stationnaire 2D — maillage, itérations et tolérances",
      solver2dIntro: "Régit la résolution couplée en volumes finis de la température et de la pression de vapeur du mode maillage 2D. Sans effet sur la méthode de Glaser 1D.",
      solvingMethod: "Méthode de résolution",
      solverCg: "Gradient conjugué (préconditionné Jacobi)",
      solverCgHint: "L'analyse statique 2D utilise toujours le gradient conjugué préconditionné Jacobi : il converge aussi avec des pare-vapeur, là où Gauss-Seidel et le cycle en V s'arrêtent avant que le bilan énergétique soit atteint.",
      meshRows: "Maillage (lignes dans l'épaisseur)",
      meshHint: "Auto (première position) recalcule avec 20→40→70→100→160→250→300 lignes et s'arrête dès que le flux thermique varie de moins de 0,1 % entre deux maillages et que le solveur lui-même a convergé (voir la carte Diagnostic). Faites glisser vers la droite pour fixer le maillage : 20, 40, 70, 100, 160, 250 ou 300 lignes - plus fin est plus précis et plus lent.",
      maxIterationsBefore: "Itérations max. (T et p",
      maxIterationsAfter: ", boucle couplée)",
      epsU: "εU — tolérance sur la valeur U / transmittance [-]",
      epsRH: "εRH — tolérance sur la pression de vapeur / RH [-]",
      toleranceHintBefore: "La tolérance ne s'applique qu'aux deux grandeurs qui comptent réellement pour le résultat : εU est la variation relative de la valeur U/du flux thermique entre deux passes couplées (le champ de température provenant de la même résolution thermique, elle régit aussi la stabilisation des températures) ; εRH est la variation de l'humidité relative entre deux passes couplées (RH=p",
      toleranceHintAfter: "(T), elle ne se stabilise donc qu'une fois stabilisés le champ de pression de vapeur et la température dont elle dépend). Un maillage plus grossier, des tolérances plus larges ou moins d'itérations convergent plus vite mais résolvent moins précisément le champ autour d'un élément d'ossature - les cartes d'état du maillage au-dessus du bouton Exemples indiquent si les paramètres actuels atteignent réellement la convergence. Gauss-Seidel et le gradient conjugué résolvent directement le maillage fin ; le multigrille en cycle V utilise en plus une hiérarchie de maillages plus grossiers, agrégés algébriquement, pour accélérer cette même résolution - les trois convergent vers le même champ.",
      aboutNotice: "© 2026 Rotho Blaas Srl · Envelope Designer. Bêta - version non officielle, encore en cours de développement.",
      methodology: "Méthodologie et limites",
      aboutBuild: "À propos de cette version",
      climateSources: "Sources climatiques",
      climateSourcesHint: "Choisissez, grandeur par grandeur, l'origine du climat (PVGIS, votre fichier EPW, estimations), voyez ce que propose chaque fournisseur et téléchargez l'année combinée au format .epw.",
      epwAdvanced: ".epw avancé…",
      epwWhereToGet: "Où trouver des fichiers météo EPW",
      epwHint: "Choisissez la station la plus proche du site, d'altitude et d'exposition similaires (EN 15026 §5.2.1) ; un aéroport côtier peut différer fortement d'un site intérieur ou de montagne. Utilisez une année type pour le cas de dimensionnement et l'option −2 K du panneau pour les problèmes hivernaux. Le fichier doit contenir une année complète de valeurs horaires (8760 lignes) ; le panneau le vérifie et signale les lacunes. Un fichier importé est envoyé avec chaque calcul et n'est pas conservé (le journal des requêtes n'en garde qu'une empreinte).",
      epwOneBuilding: "années types gratuites (TMYx) pour des milliers de stations météo dans le monde, un ZIP par station contenant l'EPW. Respectez les conditions d'utilisation du dépôt ; ne redistribuez pas les fichiers.",
      epwMap: "une carte des mêmes stations : cliquez sur la plus proche du site pour la télécharger.",
      epwPvgis: "l'outil \"Typical meteorological year\" exporte en EPW la même année type que la source PVGIS télécharge ici pour le point de la carte.",
      epwEnergyPlus: "la page EnergyPlus consacrée aux fichiers météo, qui renvoie au dépôt ci-dessus.",
      epwNsrdb: "années types issues de données satellitaires pour l'Amérique du Nord (inscription gratuite) ; téléchargées en CSV, à convertir en EPW.",
      epwLicensed: "jeux de données sous licence, à qualité contrôlée (Meteonorm : meteonorm.com), lorsqu'un projet l'exige.",
      qdHeading: "Analyse quasi-dynamique — calcul",
      qdIntro: "S'applique au prochain calcul du panneau Quasi-dynamique.",
      timeStep: "Pas de temps",
      timeStepAuto: "Automatique (1D : 5 min, 2D : 1 h)",
      timeStepHint: "Intégration temporelle implicite : tout pas est stable, mais les pas supérieurs à 1 h utilisent le climat moyenné sur le pas, ce qui lisse le cycle journalier (rafraîchissement nocturne, soleil de midi). L'EN 15026 travaille avec des données climatiques horaires : conservez 1 h ou moins pour les résultats de dimensionnement. Mesuré sur un mur à montants 2x6 (2D, 20 lignes, un an) : le pic de condensation était de 94 g/m² avec 1 h, 100 avec 6 h, 112 avec 24 h.",
      solutionMethod: "Méthode de résolution (maillage 2D)",
      qdMethods: {
        pcg: "Gradient conjugué préconditionné par lignes (PCG)",
        "pcg-jacobi": "Gradient conjugué préconditionné Jacobi",
        gs: "Gauss-Seidel",
        jacobi: "Jacobi",
        thomas: "Direct tridiagonal (Thomas)",
      },
      defaultDash: " - par défaut",
      solutionMethodHint: "Solveur des systèmes thermique et hydrique de chaque pas de temps, tous arrêtés sur le même résidu relatif. Le PCG préconditionné par lignes résout exactement chaque colonne de la grille à chaque itération (les membranes minces et les échanges superficiels couplent les cellules dans l'épaisseur bien plus que latéralement) et c'est le plus rapide ; le PCG préconditionné Jacobi est l'algorithme de référence du rapport de validation 05. Gauss-Seidel et Jacobi demandent beaucoup plus d'itérations (sur un mur 2x6, une année a pris plus de 12 minutes, au-delà des 300 s du serveur) : utilisez-les pour contre-vérifier des simulations courtes. La section Convergence affiche chaque heure. L'analyse 1D utilise toujours le solveur direct tridiagonal (Thomas).",
      tolerance: "Tolérance",
      defaultParen: " (par défaut)",
      maxIterations: "Itérations max.",
      toleranceQdHint: "Une résolution a convergé lorsque ‖b − Ax‖ / ‖b‖ est inférieur à la tolérance. À 1e−10, les grilles 2D comportant de fines lignes de membrane laissaient des résidus du bilan de masse d'environ 1e−5 ; 1e−12 les maintient sous 1e−6. Itérations max. : par résolution.",
      gridRows: "Lignes de la grille 2D",
      gridRowsOption: (rows: number) => `${rows} lignes`,
      gridRowsHint: "Mode maillage 2D uniquement. Des grilles plus fines résolvent mieux les interfaces mais prennent plus de temps ; le serveur arrête un calcul après 300 s. Mesuré sur un mur à montants 2x6, une année simulée avec le PCG préconditionné par lignes : 20 lignes 12 s, 40 lignes 18 s, 70 lignes environ 4 min (une seule année). Des grilles plus fines ne termineraient pas à temps.",
      restoreDefaults: "Rétablir les valeurs par défaut",
      lockedNote: "Non disponible dans cette bêta",
    notAppliedTitle: "Paramètres non appliqués",
    notAppliedMessage: "Vous avez modifié des paramètres qui ne sont pas encore appliqués : aucun onglet n'est mis à jour tant que vous n'avez pas cliqué sur Terminé. Comment souhaitez-vous procéder ?",
    keepEditing: "Continuer la modification",
    discardChanges: "Annuler les modifications",
    applyDone: "Appliquer (Terminé)",
    meshAuto: "Auto",
    meshRowsReadout: (rows: number) => `${rows} lignes`,
    },
    confirm: {
      cancel: "Annuler",
    },
    data: {
      stepLabel: (n: number, title: string) => `Étape ${n} : ${title}`,
      stepSite: "Site",
      stepElement: "Élément",
      stepAnalysis: "Analyse",
      stepCalculation: "Calcul",
      useThisSite: "Utiliser ce site",
      elements: {
        wall: "Mur",
        roof: "Toiture",
        ground: "Contact avec le sol",
      },
      analyses: {
        static: "Analyse statique",
        dynamic: "Analyse quasi-dynamique",
      },
      wallDesc: "Mur extérieur",
      roofDesc: "Toiture ou plancher haut",
      groundDesc: "Plancher sur terre-plein",
      groundDynamicNote: "L'analyse quasi-dynamique ne modélise pas le contact avec le sol : la surface extérieure est exposée à l'air extérieur.",
      staticOption: "Statique",
      staticDesc: "Condition de calcul et Glaser mensuel",
      quasiDynamic: "Quasi-dynamique",
      quasiDynamicDesc: "Heure par heure sur plusieurs années",
      simplified: "Simplifié",
      simplifiedDesc: "1D à travers les couches",
      meshMode: "Mode maillage",
      meshModeDesc: "Maillage 2D, montants résolus",
      meshNoteLabel: "Mode maillage 2D : méthodes numériques (note)",
      numericalMethods: "Méthodes numériques.",
      meshNote: "Le mode maillage 2D utilise des méthodes numériques : la vérification de leur validité, ainsi que de l'adéquation de chaque matériau et produit sélectionné dans la stratigraphie, pour le cas spécifique, relève de la seule responsabilité du concepteur responsable.",
      missing: {
        site: "site",
        element: "élément",
        analysis: "analyse",
      },
      stillToChoose: (list: string) => `Reste à choisir : ${list}.`,
      continueToStratigraphy: "Continuer vers la stratigraphie →",
      continueToAnalysis: {
        static: "Continuer vers l'analyse statique →",
        dynamic: "Continuer vers l'analyse quasi-dynamique →",
      },
      backToData: "← Données",
      howToStart: "Comment souhaitez-vous commencer ?",
      fromExample: "À partir d'un exemple",
      fromExampleDesc: (n: number, element: string) => `Choisissez l'une des ${n} compositions (${element}) et adaptez-la`,
      fromScratch: "De zéro",
      fromScratchDesc: "Une stratigraphie vide : ajoutez les couches depuis la bibliothèque de matériaux",
      back: "← Retour",
    },
    examples: {
      close: "Fermer",
      examplesTitle: (element: string) => `Exemples : ${element}`,
      elementName: {
        wall: "mur",
        roof: "toiture",
        ground: "contact avec le sol",
      },
      exampleLoaded: (name: string) => `Exemple chargé : ${name}`,
      changeExample: "Changer d'exemple",
      changeExampleTitle: (element: string) => `Remplace les couches par un autre exemple (${element}) ; Annuler les rétablit`,
      structures: {
        timber: "Ossature bois",
        mass_timber: "Bois massif",
        masonry: "Maçonnerie",
        concrete: "Béton",
        steel: "Acier",
        other: "Autre",
      },
      types: {
        wall: "Mur",
        roof: "Toiture / plancher",
        ground: "Plancher bas",
      },
      searchPlaceholder: "Rechercher des exemples...",
      exampleSingular: "exemple",
      examplePlural: "exemples",
      filtersLabel: "Filtres des exemples",
      region: "Région",
      yourLocation: (country: string) => `votre position : ${country}`,
      type: "Type",
      structure: "Structure",
      resetFilters: "Réinitialiser les filtres",
      noMatch: "Aucune composition ne correspond à la recherche ou aux filtres sélectionnés.",
      noneInRegion: "Aucune composition de ce type dans la région sélectionnée.",
      backToTypes: "Retour aux types",
    },
    climate: {
      loadError: "Les données climatiques n'ont pas pu être chargées.",
      suggestedCorrections: "Corrections suggérées",
      applyAndReload: "Appliquer et recharger",
      correctionsHint: "Rien n'est corrigé sans votre choix ; les corrections appliquées sont indiquées avec les résultats.",
      correctionsChosen: (list: string) => `Corrections choisies : ${list} (rien à corriger pour cette source).`,
      undo: "Annuler",
      cardTitle: "Climat du site",
      autoEpwTitle: "Le climat du point de la carte, généré automatiquement à partir d'un fournisseur (PVGIS, ERA5 via Open-Meteo, station TMYx).",
      autoEpw: "EPW automatique",
      importEpwTitle: "Importez un fichier EPW (TMYx, NSRDB, Meteonorm ou toute autre source).",
      importEpw: "Importer EPW",
      yearType: "Type d'année",
      yearTypical: "Année type, répétée",
      yearMinus2: "Année type −2 K (problèmes d'hiver, EN 15026 §5.2.1 c)",
      yearPlus2: "Année type +2 K (problèmes d'été)",
      provider: "Fournisseur de données climatiques",
      providerPvgis: "Année type PVGIS (JRC UE) - sans pluie",
      providerOpenMeteo: "ERA5 via Open-Meteo - pluie, températures du sol",
      providerTmyx: "Station météorologique TMYx (climate.onebuilding.org)",
      era5Year: "Année ERA5",
      era5YearTitle: "La première année type d'un site télécharge 10 ans de données horaires (environ 10-20 s) ; elle est ensuite conservée pour cette cellule de la grille.",
      era5Typical: "Année type (EN ISO 15927-4, 10 dernières années)",
      actualYear: (year: number) => `Année réelle ${year}`,
      tmyxStation: "Station TMYx",
      tmyxStationTitle: "Distance et différence d'altitude par rapport au site. La meilleure correspondance compte 100 m de dénivelé comme 10 km ; la température est corrigée de la différence d'altitude.",
      bestMatch: (station: string) => `Meilleure correspondance : ${station}`,
      lat: "Lat",
      latitude: "Latitude [°]",
      lon: "Lon",
      longitude: "Longitude [°]",
      elevTitle: "Altitude du site [m]. Vide : l'altitude des données climatiques (en gris). Saisissez une valeur pour corriger la température au site (0,65 K par 100 m).",
      elev: "Alt.",
      elevAuto: "auto",
      siteElevation: "Altitude du site [m]",
      viewClimateData: "Voir les données climatiques",
      viewClimateDataLoading: "Voir les données climatiques (chargement)",
      climateDataLoading: "Chargement des données climatiques…",
      loadingClimateData: "Chargement des données climatiques",
      climateData: "Données climatiques",
      backToMap: "Retour à la carte",
      map: "Carte",
      epwFile: "Fichier EPW",
      fileUnreadable: (name: string) => `Le fichier ${name} n'a pas pu être lu.`,
      weatherStationHint: "Une station météorologique proche du site est la source la plus fiable (EN 15026 §5.2.1).",
      elevationFromEpw: (elevation: number) => `${elevation} (de l'EPW)`,
      fromEpw: "de l'EPW",
      epwElevationTitle: "Vide : l'altitude de l'EPW. Saisissez une valeur pour corriger la température au site (0,65 K par 100 m).",
      notEpwFile: (name: string) => `${name} n'est pas un fichier EPW : choisissez un fichier avec l'extension .epw.`,
      readingClimate: "lecture du climat…",
      loaded: "chargé",
      replace: "Remplacer…",
      remove: "Supprimer",
      dropEpwHere: "Déposez un fichier EPW ici",
      epwSources: "TMYx, NSRDB, Meteonorm ou toute autre source",
      chooseEpw: "Choisir un fichier EPW…",
      noWindData: "Aucune donnée de vent dans ce fichier climatique.",
      loadingTheClimateData: "Chargement des données climatiques…",
      insideBasis: "UNI EN ISO 13788 classe 3",
      ashraeNotAvailable: ", ASHRAE 160 pas encore disponible",
      heatingDesignBasis: "température de base de chauffage 99,6 %",
      coldestHour: (day: number, month: string, time: string) => `heure la plus froide, ${day} ${month} ${time}`,
      aboveSaturation: (rh: string) => ` Cette pression de vapeur dépasse la saturation à Te (HR ${rh} %) : 100 % est proposé.`,
      climateOfSite: "le climat du site",
      suggestionTitle: (method: string, source: string, notes: string) => `${method}. Climat : ${source}.${notes} Les champs restent modifiables.`,
      fromTheClimate: (basis: string) => `D'après le climat (${basis}) :`,
      inUse: "utilisé",
      use: "Utiliser",
      searchFailed: "La recherche a échoué.",
      noPlaceFound: "Aucun lieu trouvé.",
      searchNotSent: "La recherche n'a pas pu être envoyée.",
      mapLabel: "Carte : cliquez pour choisir le site",
      searchPlaceholder: "Rechercher une ville, une rue ou un lieu",
      searchLabel: "Rechercher un lieu",
      searching: "Recherche en cours",
      usZonesTitle: "Zones climatiques IECC des États-Unis (PNNL Building America Solution Center)",
      usZones: "Zones climatiques US",
      addToFavourites: "☆ Ajouter aux favoris",
      favouritesFull: "Favoris pleins (10) : supprimez-en d'abord un.",
      favouriteName: "Nom du favori",
      cancel: "Annuler",
      add: "Ajouter",
      renameFavourite: (name: string) => `Nouveau nom de ${name}`,
      favouriteHint: "glisser pour réordonner, double-clic pour renommer",
      removeFavourite: (name: string) => `Retirer ${name} des favoris`,
      morePlaces: (n: number) => `${n} lieux de plus`,
      more: (n: number) => `${n} de plus`,
    monthsShort: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
    chartLabel: "Année de référence : moyenne journalière et plage journalière de chaque grandeur",
    quantitiesLabel: "Grandeurs affichées (cliquez pour masquer ou afficher)",
    allHidden: "Toutes les grandeurs sont masquées.",
    legendTitle: (name: string, hidden: boolean) => `${name} - cliquez pour ${hidden ? "afficher" : "masquer"}`,
    series: {
      outsideTemperature: "Température de l'air extérieur",
      insideTemperature: "Température intérieure",
      outsideRh: "Humidité relative extérieure",
      insideRh: "Humidité relative intérieure",
      solar: "Rayonnement solaire global sur le plan horizontal",
      wind: "Vitesse du vent à 10 m",
      rain: "Pluie sur le plan horizontal",
    },
    windRoseLabel: "Rose des vents : part des heures par direction et vitesse",
    windClassesLabel: "Classes de vitesse du vent (cliquez pour masquer ou afficher)",
    windSpeed: "vitesse du vent",
    windFrom: (direction: string, percent: string) => `Du ${direction} : ${percent} % des heures`,
    windMean: (value: string, unit: string) => `moyenne ${value} ${unit}`,
    windMax: (value: string, unit: string) => `max ${value} ${unit}`,
    },
    stratigraphy: {
      flipInsideOutsideTitle: "Inverser intérieur/extérieur - change le côté Intérieur/Extérieur (et Ti/RHi/Rsi avec Te/RHe/Rse) sans réordonner les couches",
      resetZoomTitle: "Réinitialiser le zoom (faites défiler sur le dessin pour zoomer/dézoomer, faites glisser pour vous déplacer une fois zoomé)",
      clearLayersTitle: "Supprimer toutes les couches (avec confirmation ; Annuler les rétablit)",
      condensationToggleTitle: "Afficher/masquer la condensation interstitielle sur le dessin - couches humides teintées en bleu, avec des marqueurs en forme de goutte aux points de condensation",
      fluxVectorsTitle: "Afficher les vecteurs de flux thermique sur la coupe (mode hygrothermique 2D uniquement) - la longueur de la flèche suit l'intensité locale du flux, la direction suit le gradient de température (perpendiculaire aux isothermes)",
      overlayTemperatureTitle: "Superposer le profil de température au dessin (faites glisser la poignée pour régler la part de la largeur couverte)",
      overlayHumidityTitle: "Superposer le profil d'humidité (RH) au dessin (faites glisser la poignée pour régler la part de la largeur couverte)",
      meshGridTitle: "Afficher les cellules du maillage de calcul hygrothermique 2D, avec ou sans superposition de température/humidité (mode maillage 2D uniquement, étendue réglée par la même poignée)",
      thickness: "Épaisseur",
      editMaterial: "Modifier le matériau…",
      changeMaterial: "Changer de matériau…",
      removeBridge: "Supprimer le pont thermique",
      resizeHandleTitle: "Faites glisser pour redimensionner ce cadre - double-clic pour réinitialiser",
      layerTable: "Tableau des couches",
      layerTableHide: "masquer",
      layerTableShow: "afficher les propriétés calculées de chaque couche",
      layerTableHint: "Propriétés thermiques calculées pour la stratigraphie actuelle, de l'intérieur vers l'extérieur. Faites glisser la poignée d'une ligne pour la déplacer, cliquez sur le nom d'un matériau (ou sur son icône crayon) pour l'afficher/le modifier - ou utilisez la coupe graphique ci-dessus pour d'autres actions (clic droit pour insérer/dupliquer/supprimer/ponts thermiques).",
      layerTable2DNote: "Mode hygrothermique 2D : les valeurs ci-dessous sont échantillonnées dans le champ 2D convergé le long de la colonne à mi-travée (sans élément d'ossature), et non une véritable valeur 1D par couche - consultez le panneau Hygrothermique 2D de la page Résultats pour le champ complet.",
      colMaterial: "Matériau",
      colTempMin: "Temp. min",
      colTempMinTitle: "Température sur la face la plus froide de cette couche, dans les conditions de calcul",
      colTempMax: "Temp. max",
      colTempMaxTitle: "Température sur la face la plus chaude de cette couche, dans les conditions de calcul",
      colSdTitle: "Épaisseur d'air équivalente pour la diffusion de vapeur (μ × épaisseur)",
      colCondensate: "Condensat",
      colCondensateTitle: "Taux de condensation interstitielle, le cas échéant, sur la face intérieure de cette couche",
      colWeight: "Poids",
      colWeightTitle: "Masse surfacique par unité de surface",
      colHeatCapacity: "Capacité thermique",
      colHeatCapacityTitle: "Capacité thermique massique",
      makeSideInside: "Cliquez pour faire de ce côté l'intérieur",
      makeSideOutside: "Cliquez pour faire de ce côté l'extérieur",
      sideSwitched: (top: boolean, outside: boolean) => `Côté ${top ? "supérieur" : "inférieur"} : ${outside ? "EXTÉRIEUR" : "INTÉRIEUR"}`,
      differsFromLibrary: "Diffère du matériau de la bibliothèque",
      variable: "variable",
      thicknessField: "épaisseur",
      checkPositive: (fields: string) => `Vérifiez ${fields} : un nombre positif est requis.`,
      stud: "Montant",
      material: "Matériau",
      thermalHygrometric: "Thermique et hygrométrique",
      acoustic: "Acoustique",
      permeance: "Perméance",
      membraneLocked: "Membrane Rothoblaas : valeurs de la fiche technique, non modifiables.",
      layerValuesHint: "Valeurs de cette couche uniquement, appliquées avec Entrée ou en quittant un champ.",
      studValuesHint: "Valeurs de ce montant uniquement, appliquées avec Entrée ou en quittant un champ.",
      checkedOn: (date: string) => `Vérifiée le ${date}`,
      dataSheet: "Fiche technique",
      resetToLibrary: "Rétablir les valeurs de la bibliothèque",
    outside: "EXTÉRIEUR",
    inside: "INTÉRIEUR",
    noLayersDrawing: "Aucune couche - ajoutez une couche ou chargez un exemple.",
    noLayersTable: "Aucune couche - faites glisser un matériau de la bibliothèque sur la coupe graphique ci-dessus.",
    moveUp: "Monter (vers l'extérieur)",
    moveDown: "Descendre (vers l'intérieur)",
    excludedClickToInclude: "Exclue du calcul - cliquez pour l'inclure",
    includedClickToExclude: "Incluse dans le calcul - cliquez pour l'exclure",
    exteriorResistance: "Résistance superficielle extérieure (Rse)",
    interiorResistance: "Résistance superficielle intérieure (Rsi)",
    dragToReorder: "Faites glisser pour réordonner",
    viewEditMaterial: "Voir/modifier le matériau",
    customMaterialRow: "Cette couche utilise un matériau personnalisé/modifié, pas une référence standard de la bibliothèque",
    supersaturated: "HR>100%",
    editStud: "Modifier le montant/chevron",
    removeStud: "Supprimer le montant/chevron",
    studSummary: (name: string, width: string, spacing: string, depth: string, unit: string) => `${name} · ${width}×${spacing}${unit}, profondeur ${depth}${unit}`,
    wholeComponent: "Composant entier",
    wholeComponentMidBay: "Composant entier (colonne à mi-travée)",
    sdName: {
      sdValue: "valeur sd",
      permeance: "perméance",
      vapourResistance: "résistance à la vapeur",
    },
    insertInterior: "↑ Insérer une couche (côté intérieur)",
    insertExterior: "↓ Insérer une couche (côté extérieur)",
    placeStuds: "Placer des montants ou chevrons…",
    removeStuds: "Supprimer les montants ou chevrons",
    duplicateLayer: "Dupliquer la couche",
    disableLayer: "Désactiver la couche (exclure du calcul)",
    enableLayer: "Activer la couche",
    deleteLayer: "Supprimer la couche",
    changeMaterialTitle: "Changer de matériau",
    clearAllTitle: "Supprimer toutes les couches ?",
    clearAllMessage: (n: number) => `${n === 1 ? "L'unique couche" : `Les ${n} couches`} de cette stratigraphie ${n === 1 ? "sera supprimée" : "seront supprimées"}. Vous pouvez les rétablir avec Annuler (Ctrl+Z).`,
    clearAllConfirm: "Tout supprimer",
    ashrae160Note: "ASHRAE 160 pas encore disponible (ses valeurs sont en cours de vérification) : le modèle UNI / EN est utilisé",
    },
    materialLibrary: {
      title: "Bibliothèque de matériaux",
      materials: "Matériaux",
      collapseExpand: "Réduire/développer la bibliothèque de matériaux",
      openLibrary: "Ouvrir la bibliothèque de matériaux",
      searchPlaceholder: "Filtrer les matériaux...",
      materialSingular: "matériau",
      materialPlural: "matériaux",
      noMatches: "Aucun matériau ne correspond.",
      backTo: (name: string) => `Retour à ${name}`,
      backToCategories: "Retour aux catégories",
      dragToInsert: "Faites glisser dans le tableau des couches pour insérer",
      dragHint: "Faites glisser un matériau sur la coupe graphique pour l'insérer comme nouvelle couche (Échap pour annuler).",
      chooseCategoryHint: "Choisissez une catégorie, puis faites glisser un matériau sur la coupe graphique pour l'insérer comme nouvelle couche.",
      generic: "Générique",
      filtersLabel: "Filtres de matériaux",
      min: "min",
      max: "max",
      minLambda: "Conductivité thermique minimale",
      maxLambda: "Conductivité thermique maximale",
      membranes: "Membranes",
      vapourDiffusionAs: "Diffusion de vapeur en",
      minimumOf: (name: string) => `${name} : minimum`,
      maximumOf: (name: string) => `${name} : maximum`,
      permeance: "Perméance",
      vapourResistance: "Résistance à la vapeur",
      manufacturer: "Fabricant",
      resetFilters: "Réinitialiser les filtres",
      thickness: "Épaisseur",
      variable: "variable",
      subcategories: {
        custom: "Personnalisés",
      },
    },
    shell: {
      calculating: "Calcul en cours…",
      cancelCalculation: "Annuler le calcul",
    },
    shortcuts: {
      undo: "Annuler",
      redo: "Rétablir",
      flip: "Inverser intérieur/extérieur",
      toggleMode: "Changer le mode de calcul",
      changeMaterial: "Changer le matériau de la couche sélectionnée",
      toggleLibrary: "Ouvrir/fermer la bibliothèque des matériaux",
      togglePanels: "Réduire/développer la barre latérale et la barre des résultats",
      pressKeys: "Appuyez sur les touches…",
      none: "(aucun)",
    },
    history: {
      saved: (time: string) => `Enregistré à ${time}`,
      undoTitle: (label: string) => `Annuler : ${label}`,
      undoLabel: (label: string) => `Annuler ${label}`,
      nothingToUndo: "Rien à annuler",
      redoTitle: (label: string) => `Rétablir : ${label}`,
      redoLabel: (label: string) => `Rétablir ${label}`,
      nothingToRedo: "Rien à rétablir",
      menuCount: (shown: number, available: number) => `${shown} sur ${available}`,
      steps: (steps: number) => `${steps} ${steps === 1 ? "étape" : "étapes"}`,
      actionTitle: (detail: string, steps: number) =>
        `${detail} Cliquez pour annuler ${steps === 1 ? "1 action" : `${steps} actions`}.`,
      initialState: "État initial",
      initialStateDetail: "État chargé au début de cette session.",
      sessionStart: "Début de session",
      fieldLabels: {
        thickness: "épaisseur",
        materialId: "matériau",
        enabled: "état de calcul",
        widthMM: "largeur",
        spacingMM: "entraxe",
        depthMM: "profondeur",
        mode: "mode de calcul",
        stratFlipped: "orientation intérieur/extérieur",
        Rsi: "résistance superficielle intérieure",
        Rse: "résistance superficielle extérieure",
        patm: "pression atmosphérique",
        meshRows: "lignes du maillage simplifié",
        meshRowsHygro2D: "lignes du maillage 2D",
        maxIterHygro2D: "limite d'itérations du couplage",
        epsU2D: "tolérance sur la transmittance",
        epsRH2D: "tolérance pression de vapeur / HR",
        solverMethod2D: "solveur 2D",
        autoMesh: "maillage automatique",
        meshTolerance: "précision du maillage",
        Ti: "température intérieure",
        Te: "température extérieure",
        RHi: "humidité relative intérieure",
        RHe: "humidité relative extérieure",
        name: "nom",
        notes: "notes",
      } as Record<string, string>,
      unknownMaterial: "Matériau inconnu",
      layerName: (position: number, material: string) => `couche ${position} (${material})`,
      enabled: "activée",
      disabled: "désactivée",
      notSet: "non défini",
      scopeChanged: (scope: string) => `${scope} : modifié.`,
      scopeDiff: (scope: string, changes: string) => `${scope} : ${changes}.`,
      layerAdded: "Couche ajoutée",
      layerAddedDetail: (layer: string, position: number, thickness: string) =>
        `Ajout de ${layer} en position ${position}, épaisseur ${thickness} mm.`,
      layerDeleted: "Couche supprimée",
      layerDeletedDetail: (layer: string, position: number, thickness: string) =>
        `Suppression de ${layer} en position ${position}, épaisseur ${thickness} mm.`,
      stratigraphyReplaced: "Stratigraphie remplacée",
      stratigraphyReplacedDetail: (from: number, to: number, added: number, removed: number) =>
        `Nombre de couches passé de ${from} à ${to} ; ${added} ajoutées et ${removed} supprimées.`,
      layersReordered: "Couches réordonnées",
      layersReorderedDetail: (layer: string, from: number, to: number) => `Déplacement de ${layer} de la position ${from} à ${to}.`,
      bridgeAdded: "Pont thermique ajouté",
      bridgeAddedDetail: (material: string, layer: string, width: string, spacing: string, depth: string) =>
        `Ajout d'un élément en ${material} à ${layer} ; largeur ${width} mm, entraxe ${spacing} mm, profondeur ${depth} mm.`,
      bridgeDeleted: "Pont thermique supprimé",
      bridgeDeletedDetail: (material: string, layer: string) => `Suppression de l'élément en ${material} de ${layer}.`,
      bridgeMoved: "Pont thermique déplacé",
      bridgeMovedDetail: (material: string, from: string, to: string) => `Déplacement de l'élément en ${material} de ${from} à ${to}.`,
      bridgeModified: "Pont thermique modifié",
      bridgeScope: (material: string, layer: string) => `Élément en ${material} dans ${layer}`,
      layerMaterialChanged: "Matériau de la couche changé",
      layerMaterialChangedDetail: (position: number, from: string, to: string) =>
        `Matériau de la couche ${position} changé de ${from} en ${to}.`,
      layerThicknessChanged: "Épaisseur de la couche changée",
      layerThicknessChangedDetail: (layer: string, from: string, to: string) =>
        `Épaisseur de ${layer} changée de ${from} mm à ${to} mm.`,
      layerDisabled: "Couche désactivée",
      layerDisabledDetail: (layer: string) => `${layer} exclue des calculs.`,
      layerEnabled: "Couche activée",
      layerEnabledDetail: (layer: string) => `${layer} incluse dans les calculs.`,
      layerModified: "Couche modifiée",
      flipped: "Intérieur/extérieur inversés",
      flippedDetail: "Orientation intérieur/extérieur affichée de la stratigraphie inversée.",
      modeChanged: "Mode de calcul changé",
      modeChangedDetail: (from: string, to: string) => `Mode de calcul : ${from} → ${to}.`,
      projectDetailsModified: "Données du projet modifiées",
      projectDetailsScope: "Données du projet",
      customMaterialAdded: "Matériau personnalisé ajouté",
      customMaterialAddedDetail: (name: string) => `Ajout du matériau personnalisé « ${name} » à la bibliothèque des matériaux.`,
      customMaterialDeleted: "Matériau personnalisé supprimé",
      customMaterialDeletedDetail: (name: string) => `Suppression du matériau personnalisé « ${name} » de la bibliothèque des matériaux.`,
      customMaterialModified: "Matériau personnalisé modifié",
      customMaterialModifiedDetail: (name: string) => `Modification du matériau personnalisé « ${name} ».`,
      materialLibraryModified: "Bibliothèque des matériaux modifiée",
      materialLibraryModifiedDetail: "Plusieurs matériaux personnalisés modifiés.",
      climateModified: "Climat de calcul modifié",
      climateScope: "Climat de calcul",
      settingsModified: "Paramètres de calcul modifiés",
      settingsScope: "Paramètres de calcul",
      projectModified: "Projet modifié",
      projectModifiedDetail: "Plusieurs valeurs du projet modifiées dans cette action enregistrée.",
    },
    materials: {
      panelDesc: "Matériaux intégrés et personnalisés. Les matériaux personnalisés sont enregistrés dans le stockage local de ce navigateur.",
      newMaterial: "+ Nouveau matériau",
      searchPlaceholder: "Rechercher par nom...",
      allCategories: "Toutes les catégories",
      allManufacturers: "Tous les fabricants",
      sortName: "Trier : Nom",
      sortDensity: "Trier : Masse volumique ρ",
      sortLambda: "Trier : λ",
      sortMu: "Trier : μ",
      sortWcap: "Trier : wcap",
      sortDirectionTitle: "Basculer croissant/décroissant",
      sortAsc: "▲ Croiss.",
      sortDesc: "▼ Décroiss.",
      colSymbol: "Symbole",
      colName: "Nom",
      colManufacturer: "Fabricant",
      customBadge: "personnalisé",
      edit: "Modifier",
      delete: "Supprimer",
      deleteConfirm: "Supprimer ce matériau personnalisé ?",
      variableSdTitle: (min: number, max: number) => `Sd variable : ${min}–${max} m (fiche technique)`,
      navHint: "Choisissez une catégorie ou recherchez par nom ci-dessus.",
      sourceAssumed: "Absent de la fiche technique : valeur supposée",
      sourceDerived: "Absent de la fiche technique : déduit de ses valeurs",
      dataNotes: {
        lambdaBelowAir: (lambda: number) => `le λ = ${lambda} W/(m·K) de la fiche technique est inférieur à celui de l'air immobile : non utilisé`,
        densityDerived: "masse volumique déduite des valeurs de la fiche technique",
      },
    },
    materialModal: {
      titleMembrane: "Membrane Rothoblaas",
      titleNew: "Nouveau matériau",
      titleEditCopy: "Modifier le matériau (enregistré comme copie personnalisée)",
      titleEdit: "Modifier le matériau",
      unnamed: "Matériau sans nom",
      membraneLockedHint: "Les membranes Rothoblaas sont affichées avec les valeurs de leur fiche technique et ne peuvent être ni modifiées ni copiées.",
      builtinHint: "Les matériaux intégrés ne peuvent pas être modifiés : l'enregistrement crée une copie personnalisée, et les couches de cette stratigraphie qui utilisent ce matériau passent à la copie.",
      technicalDataSheet: "Fiche technique ↗",
      productPage: "Page du produit ↗",
      checked: (date: string) => `vérifié le ${date}`,
      laboratoryNotStated: "laboratoire non indiqué",
      propertiesLabel: "Propriétés du matériau",
      tabIdentity: "Identité",
      tabGraphics: "Graphisme",
      tabThermal: "Thermique",
      tabMoisture: "Humidité",
      tabAcoustic: "Acoustique",
      name: "Nom",
      manufacturer: "Fabricant",
      optional: "facultatif",
      category: "Catégorie",
      categoryPlaceholder: "p. ex. insulation",
      thickness: "Épaisseur",
      thicknessVariable: "Épaisseur variable",
      thicknessKnown: "Épaisseurs connues (du fabricant)",
      reference: "Référence",
      referencePlaceholder: "p. ex. 100",
      min: "Min",
      max: "Max",
      knownThicknesses: "Épaisseurs connues",
      knownThicknessesHint: ", séparées par des virgules - la première est la référence",
      knownThicknessesPlaceholder: "p. ex. 40, 60, 80, 100, 120",
      source: "Source",
      laboratory: "Laboratoire",
      date: "Date",
      file: "Fichier",
      sectionSymbolCaption: "Symbole de coupe (hachure) :",
      automaticFromCategory: " - automatique, selon la catégorie",
      sectionSymbol: "Symbole de coupe",
      auto: "Auto",
      hatches: {
        "": "Auto (selon la catégorie)",
        concrete: "Béton / chape (pointillé)",
        masonry: "Maçonnerie pleine (assises de briques)",
        "masonry-hollow": "Maçonnerie creuse (assises de briques + alvéoles)",
        render: "Enduit / plâtre / mortier (points fins)",
        board: "Plaque rigide, type plâtre (unie)",
        wool: "Isolant fibreux (boucles)",
        foam: "Isolant en mousse rigide (cercles)",
        fiberboard: "Panneau de fibre de bois / granulaire (moucheté)",
        wood: "Bois (veinage)",
        membrane: "Membrane / film (trait épais)",
        stone: "Pierre naturelle (craquelures irrégulières)",
        metal: "Métal (diagonale fine)",
        generic: "Générique (hachure diagonale)",
      },
      thermalHint: "Une seule conductivité thermique par matériau (pas de λ(w) dans la version web).",
      sdValue: "Valeur Sd",
      vapourResistanceZ: "Résistance à la vapeur Z",
      variableSd: "Sd variable",
      variableSdRange: (min: number, max: number) => ` : ${min}–${max} m sur la fiche technique`,
      variableSdCurve: " (courbe Sd(HR) mesurée : voir Propriétés hygrothermiques)",
      admissibleCondensate: "Condensat admissible",
      admissibleCondensateTitle: "Condensat admissible de l'analyse de Glaser mensuelle (table héritée frmInfoVAP)",
      condDefault: (label: string) => `Par défaut : ${label}`,
      vapourNote: (thickness: string, unit: string, mu: string) => `Pour ${thickness} ${unit} : μ = ${mu}`,
      vapourNoteNeedsThickness: "Nécessite une épaisseur (de référence ou épaisseurs connues).",
      porosity: "Porosité",
      saturation: "Saturation",
      capillary: "Capillaire",
      waterAbsorption: "Absorption d'eau",
      airflow: "Résistance à l'écoulement",
      acousticHint: "Facultatif : utilisé par les outils acoustiques, pas par les calculs hygrothermiques.",
      errorMembrane: "Les membranes sont uniquement des produits Rothoblaas : un matériau personnalisé ne peut pas appartenir à la catégorie Membrane.",
      errorSdThickness: (label: string) => `Pour saisir la diffusion de vapeur en ${label}, donnez d'abord une épaisseur au matériau (épaisseur de référence ou épaisseurs connues).`,
      errorSdPositive: (label: string) => `${label} doit être un nombre supérieur à 0.`,
      save: "Enregistrer le matériau",
      cancel: "Annuler",
      close: "Fermer",
      density: "Masse volumique ρ",
      lambdaDry: "Conductivité thermique à sec λ",
      cp: "Capacité thermique massique c",
      mu: "Facteur de résistance à la vapeur μ",
      wCap: "Teneur en eau capillaire wcap",
      emissivity: "Émissivité ε",
    },
    materialHygro: {
      title: "Propriétés hygrothermiques",
      showTitle: "Afficher les propriétés hygrothermiques",
      hideTitle: "Masquer les propriétés hygrothermiques",
      tabSorption: "Sorption",
      tabSuction: "Succion",
      thickness: "Épaisseur",
      sdNamePermeance: "perméance",
      sdNameVapourResistance: "résistance à la vapeur",
      dryCup: "Dry cup [US perm]",
      muDry: "μ sec [-]",
      w80ClassAssumed: (cls: string) => `Pas de courbe de sorption mesurée : valeur de la classe « ${cls} » du modèle quasi dynamique`,
      noSorptionCurve: "Pas de courbe de sorption mesurée",
      porosity: "Porosité [%]",
      sdChartTitle: (thickness: string, unit: string) => `Sd en fonction de l'humidité relative (mesuré, ${thickness} ${unit})`,
      sorptionMeasured: "Isotherme de sorption (mesurée)",
      sorptionTable: "Isotherme de sorption (table du matériau)",
      sorptionLinear: "Teneur en eau : droite du modèle quasi dynamique",
      suctionTitle: "Courbe de succion (stockage d'humidité)",
      klTitle: "Conductivité liquide k(θ)",
      muwTitle: "Facteur de résistance à la vapeur μ(w)",
      membraneVariableNote: "Sd variable sur la fiche technique, mais pas de courbe Sd(HR) mesurée : les calculs utilisent l'état le plus fermé (Sd fixe).",
      membraneFixedNote: "Membrane : film non hygroscopique (w = 0), Sd indépendant de l'humidité.",
      footSdCurve: "Sd(φ) : mesuré selon EN ISO 12572, lu de façon log-linéaire entre les points (comme dans les analyses 1D, mensuelle et quasi dynamique) ; l'analyse 2D utilise l'état le plus fermé.",
      footTable: "w(φ) et μ(w) tels que les utilisent les analyses 2D et quasi dynamique.",
      footTableMeasured: "w(φ) et μ(w) tels que les utilisent les analyses 2D et quasi dynamique (sorption issue de la fonction de stockage, Kelvin à 20 °C).",
      footNoCurve: (cls: string) => `Pas de courbe de sorption mesurée : l'analyse 2D n'utilise aucune teneur en eau ; l'analyse quasi dynamique stocke w = ξ·φ (classe « ${cls} »).`,
      footLambda: "λ est une valeur unique (pas de λ(w)).",
      assumedNoData: "Supposé : aucune donnée mesurée pour ce matériau",
      assumedTag: "supposé",
      sorptionClasses: {
        membrane: "Membrane",
        woodBased: "Bois, matériaux à base de bois et fibres naturelles",
        gypsum: "Plaques et enduits de plâtre",
        nonHygroInsulation: "Isolant non hygroscopique",
        aac: "Béton cellulaire autoclavé",
        lightweightConcrete: "Béton léger",
        concrete: "Béton",
        concreteBlock: "Blocs béton",
        calciumSilicate: "Silicate de calcium",
        firedClayBrick: "Brique en terre cuite",
        naturalStone: "Pierre naturelle",
        metal: "Métal",
        mineralRender: "Enduits, mortiers et plaques minéraux",
        unclassified: "Non classé",
      },
    },
    calculation: {
      panelDesc: "Paramètres des analyses statiques : résistances superficielles et pression atmosphérique. Les résistances superficielles se règlent aussi sur le dessin de la stratigraphie.",
      advancedSettings: "Paramètres avancés",
      showHide: "afficher / masquer",
      atmosphericPressure: "Pression atmosphérique [Pa]",
      modelsHint: "Modèle de pression de saturation : approximation de Magnus-Tetens (fixe). Modèle de diffusion : méthode de Glaser stationnaire / diffusion de vapeur 2D à la condition de calcul (fixe). Modèle de condensation : discontinuité du flux de vapeur au plan de condensation (1D) / signalement local HR>100% (2D) (fixe). Ils sont indiqués ici par transparence ; l'utilisateur ne peut pas encore les remplacer. Les paramètres de maillage, d'itérations et de tolérances du solveur hygrothermique 2D ont été déplacés dans Paramètres (l'icône de roue dentée, en haut à droite) → onglet Solveur 2D.",
      diagnosticsTitle: "Diagnostic du solveur (maillage 2D)",
      diagnosticsIntro: "Le détail complet de la façon dont le dernier résultat hygrothermique 2D a été obtenu - méthode, taille du maillage, convergence, historique des résidus - séparément pour la résolution du champ thermique et de celui de la pression de vapeur (ils peuvent beaucoup différer : le champ de vapeur est généralement bien plus mal conditionné). La convergence du solveur (cette carte) et la précision technique (le tableau de convergence du maillage ci-dessous, avec maillage automatique) sont deux questions distinctes - une résolution entièrement convergée sur un maillage grossier peut rester imprécise.",
      thermal: "Thermique",
      vapourPressure: "Pression de vapeur",
      vapour: "Vapeur",
      addLayerForDiagnostics: "Ajoutez au moins une couche pour voir ici le diagnostic du solveur.",
      noDiagnosticsYet: "Pas encore de diagnostic.",
      methodUsed: "Méthode utilisée",
      autoSuffix: " (auto)",
      solverAuto: "automatique",
      meshCells: "Cellules du maillage",
      iterationsCycles: "Itérations / cycles",
      residualInitial: "Résidu (initial)",
      residualFinal: "Résidu (final)",
      reductionFactor: "Facteur de réduction",
      solverFlux: "Flux du solveur q",
      energyBalanceError: "Erreur du bilan énergétique",
      spdSystem: "Système SPD",
      conductivityContrast: "Contraste de conductivité",
      finalStatus: "État final",
      yes: "Oui",
      no: "Non",
      residualChartTitle: "Résidu par itération (échelle logarithmique)",
      compareSolvers: "Comparer CG et V-cycle sur ce problème",
      runCalculationFirst: "Lancez d'abord un calcul.",
      solvingBothMethods: "Calcul avec les deux méthodes…",
      colMethod: "Méthode",
      colQThermal: "q (thermique)",
      colIterations: "Itérations",
      colResidual: "Résidu",
      colStatus: "État",
      colTime: "Temps",
      meshLadder: "Séquence de convergence du maillage (ce calcul) :",
      colRows: "Lignes",
      colSolverConverged: "Solveur convergé",
      colAccepted: "Accepté",
    },
    charts: {
      noDataYet: "Pas encore de données",
      legendToggle: (label: string) => `${label} (cliquez pour masquer ou afficher)`,
    },
    search: {
      clearSearch: "Effacer la recherche",
      filters: "Filtres",
    },
    monthly: {
      title: "Glaser mensuel",
      panelDesc: "Analyse mensuelle quasi statique (1D), la méthode de l'ancienne application Thermal : Glaser à chaque interface mois par mois, la condensation s'accumulant et s'évaporant d'un mois à l'autre, alimentée par les moyennes mensuelles du climat du site. Elle s'exécute à chaque calcul, à côté des résultats Simplifié et Maillage 2D.",
      inputsTitle: "Données de calcul",
      summaryClass: (cls: number) => `classe d'humidité ${cls}`,
      summaryConstant: (ti: string, unit: string, rhi: string) => `Ti ${ti} ${unit}, RHi ${rhi} %`,
      climateOfSite: "climat du site",
      summaryDrying: "temps de séchage",
      outsideClimate: "Climat extérieur",
      outsideClimateTip: "Le climat du site choisi dans l'onglet Données (un site sur la carte ou un fichier EPW), le même que pour les autres analyses. Moyennes mensuelles des valeurs horaires : Te = moyenne de T, pv,e = moyenne de RH·psat(T).",
      loadingClimate: "Chargement du climat…",
      change: "modifier →",
      noClimate: "Pas encore de climat.",
      insideClimate: "Climat intérieur",
      tipIso13788: "Classes d'humidité, UNI EN ISO 13788 : Δp = 270 · classe · clamp((20 − Te) / 20; 0; 1) Pa, pi = pe + 1.10 · Δp, Ti = 20 °C.",
      tipEn15026: "EN 15026 Annex D : heure par heure à partir de la moyenne glissante sur 24 h de la température extérieure du fichier EPW, puis moyennée par mois.",
      tipConstant: "Température et humidité relative intérieures constantes chaque mois.",
      insideModel: "Modèle de climat intérieur",
      ashraeOption: "ASHRAE 160 - pas encore disponible : ses valeurs sont en cours de vérification",
      modelIso13788: "Classes d'humidité (UNI EN ISO 13788)",
      untilAshrae: " - en attendant ASHRAE 160",
      modelConstant: "Ti et RHi constantes",
      modelEn15026: "EN 15026 Annex D",
      humidityClass: "Classe d'humidité",
      classOption: (cls: number) => `Classe ${cls}`,
      moistureLoad: "Charge d'humidité",
      loads: {
        "normal+5": "Charge d'humidité normale + 5 % (cas de dimensionnement)",
        normal: "Charge d'humidité normale",
        low: "Charge d'humidité faible",
        high: "Charge d'humidité élevée",
      },
      dryingTime: "Temps de séchage",
      dryingTip: "Mois nécessaires pour sécher 1 kg/m² d'eau placée au milieu de la couche choisie, à partir d'octobre, avec le climat mensuel. Méthode de l'ensemble de l'analyse : UNI EN ISO 13788, Glaser mensuel avec accumulation.",
      dryingLayer: "Couche du calcul de séchage",
      chooseLayer: "Choisissez la couche…",
      monthlyClimate: "Climat mensuel",
      monthlyClimateUsed: "Climat mensuel et climat intérieur utilisé",
      monthlyClimateTip: (utcOffset: string) => `Moyennes mensuelles des valeurs horaires en heure locale (UTC${utcOffset}) : Te = moyenne de T ; pe = moyenne de RH·psat(T) ; RHe = pe / psat(Te).`,
      statusEmpty: "La stratigraphie est vide : ajoutez d'abord des couches dans le panneau Stratigraphie.",
      statusInternalWall: "Aucun côté extérieur (INTÉRIEUR des deux côtés : une paroi intérieure) : l'analyse mensuelle nécessite un climat extérieur et un climat intérieur.",
      statusBothOutside: "Aucun côté extérieur (EXTÉRIEUR des deux côtés) : l'analyse mensuelle nécessite un climat extérieur et un climat intérieur.",
      statusChooseClimate: "Choisissez le climat du site dans l'onglet Données (un site sur la carte ou un fichier EPW).",
      failed: "L'analyse mensuelle a échoué.",
      verified: "Vérifié",
      notVerified: (n: number) => `Non vérifié (${n} ${n > 1 ? "vérifications" : "vérification"})`,
      checkResidual: "bilan annuel",
      checkAdmissible: "condensation admissible",
      checkMould: "moisissures (f_Rsi)",
      allPass: "Aucune condensation restante à la fin du cycle, chaque quantité accumulée dans sa valeur admissible, f_{Rsi} supérieur à f_{Rsi,min}.",
      allPassBoth: "Aucune condensation restante à la fin du cycle, chaque quantité accumulée dans sa valeur admissible, f_{Rsi} supérieur à f_{Rsi,min}, dans les deux sections.",
      settingsUsed: "Paramètres utilisés",
      startOfCycle: "Début du cycle",
      subLayers: "Sous-couches",
      subLayersValue: (rMax: string) => `chaque couche avec R > ${rMax} m²K/W divisée en sous-couches égales`,
      humidityDependentSd: "Sd dépendant de l'humidité",
      condensationTest: "Vérification de la condensation",
      surfaces: "Surfaces",
      surfacesValue: (rsi: string, rse: string) => `R_{si} = ${rsi} m²K/W, R_{se} = ${rse} m²K/W (lames d'air selon EN ISO 6946, comme dans les autres analyses)`,
      sections: "Sections",
      sectionsValue: (list: string) => `${list} - sections 1D séparées, pas de méthode combinée`,
      admissibleCondensate: "Condensation admissible",
      mould: "Moisissures",
      uValue: (section: string) => `Coefficient U (${section})`,
      interfaces: (n: number) => `${n} interfaces`,
      maxAccumulated: "Condensation accumulée max.",
      interfacesCondensing: (n: number) => `${n} interface(s)`,
      noCondensation: "pas de condensation",
      leftAtEnd: "Restant à la fin du cycle",
      mustBeZero: "doit être 0",
      mouldStat: "Moisissures : f_{Rsi} / f_{Rsi,min}",
      criticalMonth: (month: string) => `mois critique ${month}`,
      noMonthAtRisk: "aucun mois à risque",
      months: "mois",
      dryingTag: (layer: string) => `1 kg/m² dans ${layer}, à partir d'octobre`,
      monthByMonth: (section: string) => `Mois par mois - ${section}`,
      month: "Mois",
      maTotal: "M_{a} total",
      noInterfaceCondenses: "Aucune interface ne condense, quel que soit le mois.",
      monthsNote: (unit: string) => `G_{c} : condensation (+) ou évaporation (−) du mois ; M_{a} : accumulée à la fin du mois [${unit}]. Interfaces numérotées depuis la surface extérieure.`,
      admissibleTitle: (section: string) => `Condensation admissible - ${section}`,
      interface: "Interface",
      maxMa: (unit: string) => `M_{a} max. [${unit}]`,
      admissibleColumn: (unit: string) => `Admissible [${unit}]`,
      materialAndRule: "Matériau et règle",
      notAssessed: "non évaluée",
      within: "respectée",
      exceeded: "⚠ dépassée",
      admissibleNote: "À une interface entre deux matériaux, la plus faible de leurs quantités admissibles.",
      glaserDiagram: "Diagramme de Glaser",
      abscissa: "Abscisse",
      thickness: "Épaisseur",
      diagramMonth: "Mois du diagramme",
      condensationOverCycle: "Condensation au cours du cycle",
      condensationNote: (n: number, months: string, maxG: string, unit: string, at: string) => `⚠ De la condensation se forme pendant ${n} mois (${months}) : au plus ${maxG} ${unit} à ${at} ;`,
      evaporatedBy: (month: string) => `elle s'est évaporée à la fin de ${month}.`,
      evaporatesWithin: "elle s'évapore au cours du cycle.",
      notAllEvaporates: "elle ne s'évapore pas entièrement au cours du cycle.",
      mouldTitle: (section: string) => `Moisissures - UNI EN ISO 13788 - ${section}`,
      mouldNote: (rh: string, rsi: string, fRsi: string, rMin: string) => `RH_{si} ≤ ${rh} %, R_{si} = ${rsi} m²K/W : f_{Rsi} de la section = ${fRsi}${rMin}. Mois avec T_{i} ≤ T_{e} : non évalués (—) ; f_{Rsi,min} ≤ 0 : aucune exigence ce mois-là.`,
      mouldRmin: (value: string, unit: string) => ` ; résistance thermique minimale des couches pour le mois critique : ${value} ${unit}`,
      curvesShown: "Courbes affichées (cliquez pour masquer ou afficher)",
      legendP: "pression de vapeur p",
      legendPsat: "pression de saturation p_{sat} (● face d'une couche, ○ à l'intérieur d'une couche)",
      legendWet: "interface avec condensation",
      glaserAria: (month: string) => `Diagramme de Glaser, ${month}`,
      depthAxis: (unit: string) => `profondeur depuis l'intérieur [${unit}]`,
      sdAxisSi: "Sd depuis l'intérieur [m]",
      sdAxisUk: "résistance à la vapeur depuis l'intérieur [MN·s/g]",
      sdAxisImperial: "résistance à la vapeur depuis l'intérieur [rep = 1/perm]",
      inside: "intérieur",
      outside: "extérieur",
      pressureAxis: "pression [Pa]",
      surface: "surface",
      node: (k: number) => `nœud ${k}`,
      psatTip: (point: string, psat: number) => `${point} : p_sat ${psat} Pa`,
      legendCondensed: "condensée dans le mois",
      legendEvaporated: "évaporée dans le mois",
      legendAccumulated: "accumulée à la fin du mois",
      noCondensateAnyMonth: "aucune condensation, quel que soit le mois",
      accumulationAria: "Eau condensée, évaporée et accumulée par mois",
      condensed: "condensée",
      evaporated: "évaporée",
      accumulated: "accumulée",
      sectionLabels: {
        bay: "Travée (entre les montants)",
        stud: "Montant",
        whole: "Paroi entière",
      },
      interfaceOuter: "surface extérieure",
      interfaceInner: (material: string) => `${material} | surface intérieure`,
      interfaceWithin: (material: string) => `dans ${material}`,
      insideIso13788: (cls: number, fs: string, ti: number) =>
        `Classe d'humidité UNI EN ISO 13788 ${cls}${cls === 3 ? " (DM 26/06/2015)" : ""} : Δp = 270·${cls}·clamp((20 − Te)/20; 0; 1) Pa, pi = pe + ${fs}·Δp (au plus psat(Ti)), Ti = ${ti} °C (Te lorsqu'elle est plus élevée)`,
      insideConstant: (ti: number, rhi: number) => `Constant : Ti = ${ti} °C, RHi = ${rhi} %`,
      insideEn15026: (load: string) => `EN 15026 Annex D, ${load} : valeurs horaires à partir de la moyenne glissante sur 24 h de la température extérieure, moyennées par mois`,
      startNotes: {
        OCTOBER_LEGACY: "octobre, comme l'ancienne méthode",
        NONE_CONDENSES: "aucun mois ne condense à partir d'un état sec : cycle à partir d'octobre",
        ALL_CONDENSE: "tous les mois condensent à partir d'un état sec : cycle à partir d'octobre",
        FIRST_CONDENSING: (month: string) => `premier mois qui condense après un mois sans condensation : ${month}`,
      },
      failureMould: (fRsi: string, fRsiMin: string, month: string) => `f_Rsi = ${fRsi} inférieur à f_Rsi,min = ${fRsiMin} (mois critique : ${month})`,
      failureResidual: (g: string, at: string) => `condensation restante à la fin du cycle : ${g} g/m² à ${at}`,
      failureAdmissible: (g: string, at: string, admissible: string, material: string, rule: string) =>
        `${g} g/m² à ${at}, au-delà des ${admissible} g/m² admissibles (${material} : ${rule})`,
      warningPicardCapped: (section: string, passes: number, tolM: number) =>
        `${section} : l'itération du Sd dépendant de l'humidité a atteint ${passes} passes dans au moins un mois sans se stabiliser à ${tolM} m près ; les valeurs de la dernière passe sont affichées.`,
      warningOutsideRhAbove100: "Dans au moins un mois, la pression de vapeur extérieure moyenne dépasse la pression de saturation de la température moyenne (RHe > 100 %) ; elle est utilisée telle quelle.",
      presetLabels: {
        legacy: "Thermal (ancienne méthode)",
        iso13788: "UNI EN ISO 13788 (par défaut)",
      },
      startRules: {
        legacy: "Octobre (ancienne méthode)",
        iso13788: "premier mois qui condense après un mois sans condensation (UNI EN ISO 13788), par section",
      },
      sdRules: {
        legacy: "Sd dépendant de l'humidité d'après la HR de la face intérieure de sa sous-couche (ancienne règle)",
        iso13788: "Sd dépendant de l'humidité d'après la HR moyenne des deux faces de sa sous-couche",
      },
      picardRule: (tolM: number, max: number) => `jusqu'à ce que la somme des |ΔSd| ≤ ${tolM} m, au plus ${max} passes`,
      pTolExact: "p ≥ psat exact (ancienne méthode)",
      pTolRule: (pTol: number) => `p ≥ psat·(1 − ${pTol})`,
      mouldRule: (rh: number, rsi: number) => `UNI EN ISO 13788 : f_Rsi,min par mois avec RH_si ≤ ${rh} %, R_si = ${rsi} m²K/W`,
      admissibleRule: "ancienne table frmInfoVAP par matériau ; à une interface, la plus faible des quantités admissibles des matériaux en contact ; \"30 d\" lu comme 30·ρ·d",
    },
    dynamic: {
      // Inputs tab
      desc: "Heure par heure sur plusieurs années avec des données climatiques réelles, dans le cadre de l'EN 15026 : températures, humidité relative, condensation et séchage de la stratigraphie actuelle.",
      preview: "Aperçu",
      why: "Pourquoi « quasi-dynamique » ?",
      hide: "masquer",
      settingsTitle: "Paramètres de calcul (Paramètres → Quasi-dynamique)",
      whyDynamicTerm: "Dynamique",
      whyDynamic: " : le temps est simulé heure par heure, de sorte que les couches stockent et restituent chaleur et humidité - humidification hivernale et séchage estival, humidité de construction qui sèche au fil des années. C'est ce que les calculs stationnaires (Glaser, 2D, mensuel) ne peuvent pas montrer.",
      whyQuasiTerm: "Quasi",
      whyQuasi: " : les propriétés des matériaux restent constantes. λ, ρc et µ ne varient pas avec l'humidité ; le stockage d'humidité est linéaire, w = ξ·φ avec ξ la sécante de la courbe de sorption à 80 % d'HR ; l'eau ne se déplace que sous forme de vapeur (pas de transport liquide/capillaire). La chaleur latente et la capacité thermique de la teneur en eau sont facultatives (Effets inclus dans le modèle, ci-dessous). Les membranes hygrovariables (CLIMA CONTROL) font en revanche varier leur Sd avec l'HR, et la condensation est suivie lors de sa formation et de son séchage.",
      whyFullBefore: "Le modèle ",
      whyFullTerm: "dynamique complet",
      whyFull: " de l'EN 15026 (comme dans WUFI) ajoute le stockage et la perméabilité dépendant de l'humidité, la conduction liquide et λ(w), ainsi que l'enthalpie de la vapeur et du liquide dans le bilan énergétique ; il nécessite des données hygriques complètes des matériaux et est validé sur le benchmark de l'Annex B de la norme. C'est la prochaine étape. Le modèle quasi-dynamique est fiable pour les parois régies par la diffusion de vapeur (parois légères en bois et parois isolées avec membranes) et constitue un outil d'évaluation préliminaire, pas une certification.",
      tabsLabel: "Analyse quasi-dynamique",
      tabInputs: "Données d'entrée",
      tabResults: "Résultats",
      runFirst: "Lancez d'abord l'analyse quasi-dynamique",
      outerSurface: "Surface extérieure",
      absorptance: "Absorptance solaire",
      emissivity: "Émissivité",
      insideLoad: "Climat intérieur (EN 15026 Annex D)",
      loads: {
        "normal+5": "Charge d'humidité normale + 5 % (cas de dimensionnement)",
        normal: "Charge d'humidité normale",
        low: "Charge d'humidité faible",
        high: "Charge d'humidité élevée",
      },
      ashrae160Note: "ASHRAE 160 pas encore disponible (ses valeurs sont en cours de vérification) : le modèle UNI / EN est utilisé.",
      modelAndRun: "Modèle et calcul",
      run: "Calcul",
      years: "Années",
      woodCriterion: "Critère bois [M.-%]",
      woodCriterionTitle: "Fixe : teneur en humidité des matériaux à base de bois ≤ 16 M.-%",
      construction: "Humidité de construction (HR initiale 95 %, sinon 80 %)",
      stepAuto: (step: string) => `automatique (${step})`,
      solver2d: (method: string, tolerance: string, rows: number) => `${method} · tolérance ${tolerance} · ${rows} lignes`,
      direct1d: "solveur 1D direct",
      calcSummary: (step: string, solver: string) => `Calcul : pas ${step} · ${solver} ·`,
      changeInSettings: "modifier dans les Paramètres ⚙",
      runButton: (mode: string) => `Lancer l'analyse quasi-dynamique (${mode})`,
      calculating: "Calcul en cours…",
      hint2d: "Mode maillage 2D (en-tête) : les montants et l'isolant forment une seule grille, couplée. Environ 10-20 s par année simulée avec 20-40 lignes ; les grilles plus fines prennent plus de temps, et le serveur arrête un calcul après 300 s.",
      hint1d: "Mode simplifié (en-tête) : une colonne 1D à travers les couches ; les montants ne sont pas pris en compte (passez l'en-tête en maillage 2D pour les inclure).",
      emptyStratigraphy: "La stratigraphie est vide : ajoutez d'abord des couches dans le panneau Stratigraphie.",
      noClimate: "Choisissez d'abord le climat : onglet Données, Climat du site (un site sur la carte ou un fichier EPW).",
      noOutdoorSide: "Aucun côté extérieur : l'analyse quasi-dynamique nécessite un côté EXTÉRIEUR et un côté INTÉRIEUR sur le dessin (onglet Stratigraphie).",
      analysisFailed: "L'analyse a échoué.",
      cancelled: "Le calcul a été annulé ; le résultat précédent (le cas échéant) reste affiché.",
      unreadable: "La réponse du serveur n'a pas pu être lue.",
      unreachable: "Le serveur n'a pas pu être joint.",
      timeout: "Le calcul a duré plus longtemps que ce que le serveur autorise (300 s). Simulez moins d'années, utilisez une grille 2D plus grossière ou un pas de temps plus long.",
      statusError: (status: number) => `Le serveur a répondu avec le statut ${status}.`,
      stopped: "Le calcul s'est arrêté avant la fin (le serveur a peut-être atteint sa limite de 300 s). Simulez moins d'années, utilisez une grille 2D plus grossière ou un pas de temps plus long.",
      lastResult: (mode: string, years: number, source: string) => `Dernier résultat : ${mode}, ${years} année(s), ${source} ·`,
      openResults: "ouvrir les Résultats →",
      mode2d: "Maillage 2D",
      mode1d: "1D",
      modeSimplified: "Simplifié",
      modeSimplified1d: "Simplifié (1D)",
      // effects included in the model
      effectsLegend: "Effets inclus dans le modèle",
      effects: {
        dynamic: {
          label: "Système dynamique",
          hint: "Stockage de chaleur et d'humidité (les termes ∂/∂t). Désactivé : chaque pas de temps est l'état stationnaire de son climat (Glaser heure par heure) ; seule la condensation s'accumule.",
        },
        waterHeatCapacity: {
          label: "Capacité thermique de la teneur en eau",
          hint: "ρc + w·c_w, avec c_w = 4190 J/(kg·K) et la teneur en eau w du pas de temps précédent.",
        },
        latentHeat: {
          label: "Chaleur latente",
          hint: "Chaleur libérée là où la vapeur est adsorbée ou se condense et prélevée là où elle s'évapore : h_v · apport net de vapeur de chaque cellule, h_v = 2500 kJ/kg (EN 15026:2007), à partir du champ de vapeur du pas de temps précédent.",
        },
        variableSd: {
          label: "Résistance à la vapeur variable µ(w), Sd(HR)",
          hint: "Sd(HR) des membranes avec une courbe mesurée (CLIMA CONTROL 80 et 160) et µ(w) des matériaux avec une courbe µ dépendant de l'humidité, mis à jour à chaque pas de temps. Désactivé : leurs valeurs fixes.",
        },
        rain: {
          label: "Pluie",
          hint: "Pluie battante sur la surface extérieure, à partir de la pluie et du vent du climat ; l'eau qui pénètre ressort par évaporation (pas de transport capillaire).",
        },
        conductivity: {
          label: "Conductivité variable λ(w)",
          hint: "Nécessite des données de λ dépendant de l'humidité pour chaque matériau.",
        },
        capillarity: {
          label: "Capillarité",
          hint: "Transport liquide : nécessite la courbe de sorption complète et les coefficients de transport liquide de chaque matériau.",
        },
      },
      availableSoon: "bientôt disponible",
      needsDynamic: "Nécessite le système dynamique (stockage).",
      noRain: "Le climat choisi ne contient pas de pluie (PVGIS n'en fournit pas) : choisissez ERA5 (Open-Meteo), une station TMYx ou un fichier EPW avec pluie.",
      rainSettings: "Paramètres de la pluie",
      waterEntering: "Eau pénétrant dans la paroi",
      rainModeTitle: "Défini par les normes des menus (Paramètres → Général)",
      rainAbsorb: "Façade absorbante (EN 15026) : 70 % dans la couche extérieure",
      rainDrain: "Bardage avec drainage (ASHRAE 160) : 1 % derrière celui-ci",
      rainAbsorbHint: "Enduit, maçonnerie apparente : 70 % de la pluie battante pénètre sous forme d'eau liquide dans les cellules les plus extérieures de la couche extérieure, jusqu'à la saturation capillaire du matériau (w_cap) ; le reste ruisselle.",
      rainDrainHint: "Bardage ou panneaux sur une lame d'air drainée : 1 % de la pluie battante se dépose dans la partie extérieure de la première couche derrière le bardage (la fuite de dimensionnement de l'ASHRAE 160).",
      exposure: "Exposition (coefficient de pluie battante R2)",
      exposures: {
        low: "Bâtiment bas (jusqu'à 10 m)",
        "tall-low": "Bâtiment haut, partie inférieure",
        "tall-mid": "Bâtiment haut, partie centrale",
        "tall-top": "Bâtiment haut, partie supérieure",
      },
      exposureOption: (label: string, r2: number) => `${label} — R2 = ${r2} s/m`,
      rainFormula: "Pluie sur la paroi = R2 × vitesse du vent × pluie horizontale × cos(angle entre le vent et la façade), uniquement côté au vent ; les toitures reçoivent en plus la pluie horizontale × cos(inclinaison).",
      // orientation of the outer surface
      directions: ["N", "NE", "E", "SE", "S", "SO", "O", "NO"],
      orientLabel: (tilt: number, direction: string, azimuth: number) => `Surface extérieure : inclinaison ${tilt}°, orientée ${direction} ${azimuth}°. Faites glisser ou utilisez les flèches du clavier pour la tourner.`,
      orientValue: (tilt: number, azimuth: number) => `inclinaison ${tilt}°, orientation ${azimuth}°`,
      outside: "extérieur",
      inside: "intérieur",
      turning: "↔ rotation (orientation)",
      tilting: "↕ inclinaison",
      tiltTitle: (now: string) => `Inclinaison : 0° toiture plate, 90° paroi verticale (actuellement : ${now})`,
      flatRoof: "toiture plate",
      wall: "paroi",
      roof: "toiture",
      azimuthTitle: (now: string) => `Orientation : direction de la surface extérieure, degrés depuis le nord, dans le sens horaire (actuellement : ${now})`,
      anyFlat: "quelconque, plate",
      orientHint: (wallMinTilt: number) => `Faites glisser la paroi : gauche/droite la tourne (orientation), vers le bas la redresse vers la verticale, vers le haut l'abaisse - une seule à la fois, selon le premier mouvement de chaque glissement. Flèches du clavier : pas de 1°, Maj 5°. Une inclinaison ≥ ${wallMinTilt}° est traitée comme une paroi, en dessous comme une toiture.`,
      // progress window
      running: "Analyse quasi-dynamique en cours",
      cancelling: "Annulation…",
      sending: "Envoi de la stratigraphie au serveur",
      comparison: " (comparaison)",
      simulatedUpTo: (day: string, years: number) => `simulé jusqu'au ${day} sur ${years} année(s)`,
      elapsed: (time: string) => `écoulé ${time}`,
      timeLeft: (time: string) => ` · environ ${time} restant`,
      // Results tab
      resultsDesc: "Résultat du dernier calcul. Faites glisser sur un graphique temporel pour sélectionner une période ; double-cliquez pour l'ensemble du calcul.",
      noResult: "Aucun résultat pour l'instant : lancez l'analyse dans Analyse quasi-dynamique.",
      checksNotSatisfied: (failed: number, total: number) => `⚠ ${failed} vérification(s) sur ${total} non satisfaite(s)`,
      allSatisfied: "toutes les vérifications satisfaites",
      noCheckApplicable: "aucune vérification applicable",
      verdictLine: (mode: string, years: number, source: string) => `${mode} · ${years} année(s) · ${source} · évaluation préliminaire, pas une certification · cliquez sur une vérification pour ses détails`,
      modeMismatch: (resultMode: string, headerMode: string) => `Ce résultat a été calculé en mode ${resultMode} ; l'en-tête est maintenant sur ${headerMode}. Relancez le calcul pour le mettre à jour.`,
      notApplicable: "n/a",
      satisfied: "satisfaite",
      notSatisfied: "non satisfaite",
      notSatisfiedWarn: "⚠ non satisfaite",
      sectionsLabel: "Sections",
      secRun: "Calcul et données climatiques",
      secPeriod: "Résultats sur la période",
      secPeriodTitle: "Résultats sur la période sélectionnée",
      secConv: "Convergence",
      secConvTitle: "Convergence sur la période sélectionnée",
      secCharts: "Graphiques",
      secChartsTitle: "Graphiques sur la période sélectionnée",
      secFields: "Champs 2D",
      secFieldsTitle: "Champs 2D à des heures remarquables",
      secHourly: "Heure par heure",
      secHourlyTitle: "Heure par heure (dernière année simulée)",
      secLayers: "Couches du modèle",
      resultBadge: (mode: string) => `résultat ${mode}`,
      source: "Source",
      sourceDbs: (radiation: string, weather: string) => ` (rayonnement ${radiation}, météo ${weather})`,
      location: "Emplacement",
      period: "Période",
      insideClimate: "Climat intérieur",
      attribution: "Attribution",
      runLine: (years: number, month: string, step: string, grid: string, cells: number, seconds: string, mass: string, energy: string) =>
        `Calcul : ${years} années à partir du 1er ${month}, pas de temps ${step}, ${grid}${cells} cellules ; calculé en ${seconds} s ; résidus de bilan : masse ${mass}, énergie ${energy}.`,
      gridText: (rows: number, columns: number) => `grille 2D ${rows} × ${columns} = `,
      rhInterfaces: "Humidité relative aux interfaces (moyenne journalière)",
      rhAxis: "HR [%]",
      tInterfaces: (unit: string) => `Température aux interfaces (moyenne journalière) [${unit}]`,
      outsideAir: "air extérieur",
      membraneSd: "Sd des membranes (moyenne journalière, bande = amplitude journalière)",
      condensateTitle: "Condensation (moyenne journalière) [g/m²]",
      total: "total",
      waterTitle: "Eau totale dans la paroi [kg/m²]",
      totalWater: "eau totale",
      rainTitle: "Pluie sur la surface extérieure et pénétrant dans la paroi (par jour) [kg/m²]",
      rainAxis: "kg/m² par jour",
      rainOnSurface: "pluie battante sur la surface",
      rainEntering: "pénétrant dans la paroi",
      woodTitle: "Teneur en humidité des couches à base de bois [M.-%]",
      rhMapTitle: "Humidité relative à travers la paroi, jour par jour",
      fieldColdest: "À l'heure la plus froide du calcul",
      fieldPeak: "Au pic de condensation",
      inPeriod: "dans la période",
      outsidePeriod: "hors de la période",
      fieldLine: (date: string, hour: string, te: string, unit: string) => `${date}, ${hour}:00 (heure du calcul) · extérieur ${te} ${unit}`,
      fieldCondensate: (value: string) => ` · condensation ${value} g/m²`,
      noCondensateRun: "Aucune condensation sur l'ensemble du calcul.",
      hourlyTitle: "Heure par heure : HR aux 4 interfaces les plus humides de la dernière année",
      hourlyLastYearOnly: (from: string) => `Les séries horaires ne couvrent que la dernière année simulée (à partir du ${from}).`,
      selectLastYear: "Sélectionner la dernière année",
      membraneVariable: (min: number, max: number) => `membrane, Sd ${min}–${max} m selon l'HR`,
      membraneFixed: (sd: string) => `membrane, Sd ${sd} m`,
      xiText: (xi: string, source: string) => `ξ = ${xi} kg/m³ (${source})`,
      // period filter
      dayLabel: (day: number, month: string, year: number) => `${day} ${month}, année ${year}`,
      yearN: (year: number) => `Année ${year}`,
      days: "jours",
      presetPeriods: "Périodes prédéfinies",
      wholeRun: "Calcul entier",
      season: (season: string, from: string, to: string, year: number) => `${season} (${from}–${to}), année ${year}`,
      winter: "Hiver",
      summer: "Été",
      from: "Du",
      to: "au",
      periodHint: "Faites glisser sur la bande ou sur un graphique ci-dessous pour sélectionner une période ; double-cliquez sur un graphique pour l'ensemble du calcul.",
      day: "Jour",
      month: "Mois",
      simulatedYear: "Année simulée",
      overviewLabel: "Période sélectionnée dans l'ensemble du calcul : faites glisser pour la modifier",
      overviewTitle: "température de l'air extérieur, ensemble du calcul",
      // results in the period
      surfaceTmin: "Surface intérieure, température min.",
      fRsiMin: "Facteur de température f_Rsi, min.",
      fRsiTag: (day: string, limit: number) => `${day} · limite ${limit}`,
      noHour10K: "aucune heure avec Ti − Te ≥ 10 K",
      condMax: "Condensation, max.",
      condTag: (day: string, days: number) => `${day} · ${days} jour(s) avec condensation`,
      noneInPeriod: "aucune sur la période",
      condLastDay: "Condensation le dernier jour",
      driedOut: "séchée",
      stillPresent: "encore présente",
      woodMax: "Teneur en humidité du bois, max.",
      rhMax: "HR à une interface, max. (horaire)",
      rainIn: "Pluie pénétrant dans la paroi",
      rainTag: (onSurface: string) => `sur ${onSurface} kg/m² de pluie battante sur la surface`,
      waterChange: "Eau dans la paroi, variation",
      waterTag: (from: string, to: string, trend: string) => `${from} → ${to} kg/m² · ${trend}`,
      netWetting: "humidification nette",
      netDrying: "séchage net",
      noNetChange: "aucune variation nette",
      outsideMean: "Air extérieur, moyenne",
      outsideTag: (rh: string, min: string, max: string, unit: string) => `HR ${rh} % · de ${min} à ${max} ${unit} (moyennes journalières)`,
      checksInPeriod: "Vérifications sur la période",
      screening: "évaluation préliminaire, pas une certification",
      checkCondensate: (limit: number) => `Condensation ≤ ${limit} g/m² sur toute la période`,
      checkCondensateDetail: (max: string, day: string, days: number, end: string) => `max. ${max} g/m² le ${day} ; ${days} jour(s) avec condensation ; ${end} g/m² le dernier jour`,
      noCondensatePeriod: "Aucune condensation sur la période.",
      checkWood: (limit: number) => `Matériaux à base de bois : teneur en humidité ≤ ${limit} M.-%`,
      checkWoodDetail: (name: string, max: string, day: string, daysOver: number) => `${name} : max. ${max} M.-% le ${day}${daysOver ? `, ${daysOver} jour(s) au-dessus` : ""}`,
      noWood: "Aucun matériau à base de bois.",
      checkFrsi: (limit: number) => `Facteur de température f_Rsi ≥ ${limit} (heures avec Ti − Te ≥ 10 K)`,
      checkFrsiDetail: (value: string, day: string) => `f_Rsi minimal ${value} le ${day}`,
      noHour10KPeriod: "Aucune heure avec Ti − Te ≥ 10 K sur la période.",
      accumulationNote: "La vérification de l'accumulation d'une année sur l'autre nécessite des années entières : voir les vérifications de fin de calcul ci-dessus.",
      membranesWood: "Membranes et matériaux à base de bois",
      colMembrane: "Membrane",
      colSdMean: "Sd moyen [m]",
      colSdMin: "Sd min [m]",
      colSdMax: "Sd max [m]",
      byRh: "selon l'HR",
      fixedSd: "fixe",
      noMembrane: "Aucune membrane dans la stratigraphie.",
      colWood: "Matériau à base de bois",
      colUMean: "u moyen [M.-%]",
      colUMax: "u max [M.-%]",
      colDaysOver: (limit: number) => `Jours > ${limit} M.-%`,
      woodNote: "u = pic journalier de la cellule la plus humide du matériau.",
      layersInPeriod: "Couches sur la période",
      colLayer: "Couche",
      colTmin: (unit: string) => `T min [${unit}]`,
      colTmean: (unit: string) => `T moyenne [${unit}]`,
      colTmax: (unit: string) => `T max [${unit}]`,
      colRhMean: "HR moyenne [%]",
      colRhMax: "HR max [%]",
      colWaterMean: "Eau moyenne [kg/m²]",
      colCondMax: "Condensation max [g/m²]",
      membraneFace: "membrane : en 1D, une face entre les cellules (voir son Sd et les interfaces de part et d'autre)",
      layersNote: "Couches de l'intérieur vers l'extérieur. Les valeurs de chaque couche sont des moyennes volumiques journalières ; le minimum et le maximum portent sur ces moyennes journalières.",
      interfacesInPeriod: "Interfaces sur la période",
      colInterface: "Interface",
      colRhMaxHourly: "HR max, horaire [%]",
      colDays80: "Jours avec HR moyenne > 80 %",
      interfacesNote: "T minimale et maximale des moyennes journalières ; HR maximale sur les heures de la période.",
      profileTitle: "Humidité relative à travers la paroi sur la période",
      // convergence
      convSettings: (method: string, tolerance: string, maxIter: number, step: string) => `${method} · tolérance ${tolerance} · max. ${maxIter} itérations par résolution · pas de temps ${step}`,
      hoursConverged: "Heures convergées",
      everyHour: "toutes les heures",
      hoursNotConverged: (hours: number) => `${hours} heure(s) non convergée(s)`,
      maxIterations: "Résolution linéaire, itérations max.",
      limitN: (limit: number) => `limite ${limit}`,
      directSolver: " · solveur direct",
      maxResidual: "Résolution linéaire, résidu max.",
      toleranceTag: (tolerance: string) => `tolérance ${tolerance}`,
      energyResidual: "Bilan énergétique, résidu max.",
      relativeLimit: (limit: string) => `relatif · limite ${limit}`,
      massResidual: "Bilan de masse, résidu max.",
      massTag: (limit: string, max: string) => `relatif · limite ${limit} · max. sur l'ensemble du calcul ${max} kg/m² par pas`,
      notConvergedTitle: "Heure par heure : heures non convergées signalées",
      causes: ["résolution thermique", "résolution de la vapeur", "ensemble actif de condensation", "Sd des membranes"],
      causeHours: (cause: string, hours: number) => `${cause} ${hours} h`,
      notConvergedList: (list: string) => `Non convergé : ${list}.`,
      allConverged: "Chaque heure de la période a convergé : toutes les résolutions linéaires ont atteint la tolérance, l'ensemble actif de condensation s'est stabilisé et l'itération sur le Sd des membranes a convergé.",
      residualNote: "Les résidus de bilan sont affichés séparément : un résidu de masse relatif supérieur à 1e−6 au cours d'une heure avec un flux presque nul à travers les surfaces peut correspondre à une quantité absolue infime.",
      iterationsTitle: (perHour: boolean) => `Itérations de la résolution linéaire (${perHour ? "par heure" : "heure la plus défavorable de chaque jour"})`,
      residualsTitle: (perHour: boolean) => `Résidus, log10 (${perHour ? "par heure" : "heure la plus défavorable de chaque jour"})`,
      iterations: "itérations",
      maxIterSeries: "itérations max. (chaleur ou vapeur)",
      linearRelative: "résolution linéaire (relatif)",
      energyBalance: "bilan énergétique",
      massBalance: "bilan de masse",
      failsTitle: "Heures non convergées (les 30 premières de la période)",
      colHour: "Heure",
      colNotConverged: "Non convergé",
      colIterations: "Itérations",
      colLinearResidual: "Résidu linéaire",
      converged: "convergé",
      linearNotConverged: "résolution linéaire non convergée",
      activeSetNotConverged: "ensemble actif ou Sd des membranes non convergé",
      // charts
      curvesShown: "Courbes affichées (cliquez pour masquer ou afficher)",
      rhCondensation: "100 % (condensation)",
      membrane: "membrane",
      profileLabel: "Humidité relative à travers la paroi",
      outsideCaps: "EXTÉRIEUR",
      profileMean: "moyenne des moyennes journalières",
      profileMax: "moyenne journalière la plus élevée",
      profileMin: "moyenne journalière la plus basse",
      insideAtBottom: "intérieur en bas",
      quantity: "Grandeur",
      fieldRh: "HR",
      fieldT: "Température",
      fieldWc: "Condensation",
      fieldHover: (layer: string, stud: boolean, x: string, y: string, temperature: string, unit: string, rh: string, condensate: string) =>
        `${layer}${stud ? " (montant)" : ""} · x ${x} mm, ${y} mm depuis l'intérieur · T ${temperature} ${unit} · HR ${rh} % · condensation ${condensate} kg/m³`,
      noCondensateHour: "aucune condensation à cette heure",
      fieldHint: "Survolez une cellule pour afficher ses valeurs. Largeur : la moitié de l'entraxe des montants, entre les plans de symétrie.",
    },
    results: {
      badge2D: "maillage 2D - dernier calcul",
      badgeSimplified: "simplifié - dernier calcul",
      badgeNone: "pas encore de résultats",
      warnings: "Avertissements",
      stratigraphy: "Stratigraphie",
      fieldTitle: "Champ à travers la stratigraphie",
      zoomResetTitle: "Réinitialiser le zoom (faites défiler sur le dessin pour zoomer/dézoomer)",
      standingNoteTitle: "Méthodes numériques et choix des matériaux",
      standingNoteDetail: "Des méthodes numériques sont utilisées lorsque c'est applicable (mode maillage 2D). La vérification de leur validité, ainsi que de l'adéquation de chaque matériau et produit choisi dans la stratigraphie, pour le cas spécifique, relève de la responsabilité du concepteur.",
      inputErrorTitle: "Calcul non effectué - données non valides",
      inputErrorTitleQuota: "Calcul non exécuté - temps de calcul du serveur épuisé",
      inputErrorTitleRateLimit: "Calcul non exécuté - limite de requêtes atteinte",
      cancelledTitle: "CALCUL ANNULÉ",
      cancelledDetail: "Calcul annulé par l'utilisateur. Les valeurs ci-dessous proviennent du dernier calcul effectivement terminé.",
      noWarnings: "Ajoutez au moins une couche à la stratigraphie pour voir ici les avertissements techniques.",
      uLimitLabels: {
        wall: "Mur extérieur (DE Bestand, 0,24)",
        roof: "Toiture / dernier plancher (DE Bestand, 0,24)",
        floor: "Plancher sur terre-plein/local non chauffé (DE Bestand, 0,30)",
        custom: "Limite personnalisée",
      },
      checks: {
        uValue: "Coefficient U",
        uValue2D: "Coefficient U (2D)",
        limit: (value: string, reference: string) => `limite ${value} (${reference})`,
        interstitial: "Condensation interstitielle et moisissures",
        interstitial2D: "Condensation interstitielle et moisissures (2D)",
        interstitialCondensation: "condensation interstitielle",
        possibleMould: "développement possible de moisissures",
        none: "aucune",
        wet1D: (rate: string, node: string) => `${rate} g/(m²·h) au nœud ${node} (EN ISO 13788, mois de calcul)`,
        wet2D: (cells: number, share: string) => `${cells} ${cells > 1 ? "cellules" : "cellule"} à HR > 100 % · ${share} % de la section (condition de calcul)`,
        maxRh: (where: string, rh: string) => `HR max. ${where} ${rh} % (condition de calcul)`,
        betweenLayers: "entre les couches",
        inTheField: "dans le champ",
        noFace: "aucune interface entre couches",
        surface1D: "HR de la surface intérieure (moisissures)",
        surfaceDetail1D: "limite 80 % · cliquez pour le diagramme psychrométrique",
        surface2D: "Surface intérieure (moisissures)",
        surfaceDetail2D: "HR max., limite 80 % · température min. · cliquez pour le diagramme psychrométrique",
        solver: "Solveur",
        notConverged: "non convergé",
        solverDetail: "champs thermique et de vapeur",
        monthly: "Glaser mensuel",
        notCalculated: "non calculé",
        checksNotSatisfied: (n: number) => `${n} ${n > 1 ? "vérifications non satisfaites" : "vérification non satisfaite"}`,
        verified: "vérifié",
        monthlyOk: "bilan annuel, condensation admissible, moisissures",
        monthlyFailure: {
          residual: "condensat restant en fin de cycle",
          admissible: "condensation admissible dépassée",
          mould: "risque de moisissures (f_Rsi)",
        },
        satisfied: "Satisfaite",
        notSatisfied: "Non satisfaite",
        ofLimit: (percent: number) => `${percent} % de la limite`,
        notSatisfiedCount: (bad: number, total: number) => `⚠ ${bad} sur ${total} vérifications non satisfaites`,
        allSatisfied: (total: number) => `les ${total} vérifications sont satisfaites`,
        mode2D: "Maillage 2D (stationnaire, volumes finis)",
        mode1D: "1D simplifié (EN ISO 6946 / EN ISO 13788)",
        clickForDetails: "cliquez sur une vérification pour ses détails",
      },
      temperature: "Température",
      humidity: "Humidité",
      humidityRh: "Humidité (HR)",
      vapourPressure: "Pression de vapeur",
      vapourPressurePa: "Pression de vapeur [Pa]",
      moistureContent: "Teneur en eau",
      secondProfileChart: "Second graphique de profil",
      field2DDesc: "Un demi-entraxe symétrique des montants (ou toute la travée sans élément d'ossature), hauteurs des rangées à l'échelle. Cliquez sur une cellule pour ses valeurs exactes. Faites glisser la ligne verticale pour amener à cette position les graphiques du profil vertical à côté",
      moistureNote: "Rouge = sursaturé (HR>100%) - l'équilibre de sorption w(HR) ne décrit plus correctement cette cellule, qu'un modèle d'humidité soit disponible ou non.",
      midBay: "mi-travée",
      midBayColumn: "colonne à mi-travée",
      studCentreline: "axe du montant",
      fromStud: (distance: string) => `${distance} de l'axe du montant`,
      verticalProfile: (where: string) => `Profil vertical (${where})`,
      temperatureThroughThickness: "Température dans l'épaisseur",
      rhThroughThickness: "HR dans l'épaisseur",
      psatChartLabel: "Pression de saturation et de vapeur dans l'épaisseur",
      rhChartLabel: "Humidité relative dans l'épaisseur",
      cell: {
        hint: "Cliquez sur une cellule de la carte ci-dessus pour voir ici ses valeurs.",
        interiorFilm: "Couche superficielle intérieure",
        exteriorFilm: "Couche superficielle extérieure",
        layer: (n: number) => `Couche ${n}`,
        material: "matériau",
        noMoistureModel: "non disponible (pas de modèle d'humidité)",
        dryNoModel: "(sec, pas de modèle)",
        saturated: "État : SATURÉ / RISQUE DE CONDENSATION",
        rawNote: ">100% - valeur technique, pas l'HR affichée ci-dessus",
      },
      field1DDesc: "La section montre l'unique condition de calcul fixe pour laquelle le calcul est résolu (modifiez Te/HRe/Ti/HRi directement sur la section graphique du panneau Stratigraphie), et non une moyenne annuelle, dessinée à la même échelle que le panneau Stratigraphie. Sans calcul 2D du pont thermique, le champ de couleur est 1D : il ne varie que dans l'épaisseur et les contours des éléments d'ossature sont dessinés uniquement à titre de référence (la couleur ne varie pas latéralement). Lorsqu'un calcul du pont thermique sur maillage 2D est disponible, la vue Température montre ce champ réellement résolu et la vue Humidité l'HR locale du même champ, calculée avec le profil 1D de pression de vapeur de Glaser (la résistance à la vapeur n'est pas résolue latéralement ; il s'agit donc d'une approximation et non d'un véritable calcul 2D de la vapeur - choisissez pour cela le mode de calcul maillage 2D).",
      sectionTitle: (field: string) => `${field} à travers la stratigraphie - condition de calcul`,
      sectionTitleShort: (field: string) => `${field} à travers la stratigraphie`,
      noResultsYet: "Pas encore de résultats.",
      diagramNote2D: (humidity: boolean): string =>
        humidity
          ? "Champ de température du maillage 2D + profil de vapeur 1D (pv non résolue latéralement), symétrisé par rapport à l'axe de chaque montant"
          : "Maillage 2D, symétrisé par rapport à l'axe de chaque montant (2 entraxes affichés)",
      profilesTitle: "Profils dans l'épaisseur (condition de calcul)",
      glaserSubtitleBefore: "Diagramme de Glaser — condensation là où ",
      glaserSubtitleAfter: ", à la condition de calcul",
      info: {
        uvalue: "Coefficient de transmission thermique, vérifié par rapport à la limite de référence choisie (ou à une valeur personnalisée).",
        rw: "Estimation simplifiée selon la loi de masse (dans l'esprit de l'EN 12354-1, Rw ≈ 37,5·log10(m′) − 42 dB) à partir de la seule masse surfacique totale - non valable pour les parois multicouches/à lame d'air (résonance, coïncidence non modélisées). Ne remplace pas un Rw mesuré ou certifié.",
        condensationGlaser: "Glaser 1D : pression de vapeur supérieure à la pression de saturation à une interface intérieure, à la condition de calcul fixe (panneau Stratigraphie). Une vérification stationnaire, pas un bilan annuel.",
        surfaceCondensation: "Température/HR à la surface intérieure, à la condition de calcul fixe. Au-delà d'environ 80% d'HR, les moisissures sont favorisées même sans condensation proprement dite ; à 100% d'HR la surface condense.",
        interstitialCondensation2d: "HR locale maximale dans le champ 2D convergé. Au-delà d'environ 80%, les moisissures sont favorisées ; à 100% la cellule est sursaturée (condensation).",
      },
      bar: {
        empty: "Ajoutez au moins une couche pour voir ici les résultats.",
        moreInfo: "Plus d'informations",
        previousSection: "Section précédente",
        nextSection: "Section suivante",
        sectionOf: (i: number, n: number) => `Section ${i} sur ${n}`,
        collapseToggle: "Réduire/développer la barre des résultats",
        thermal: "Thermique",
        thermal2D: "Thermique (2D)",
        hygrometric: "Hygrométrique",
        hygrometric2D: "Hygrométrique (2D)",
        acoustic: "Acoustique",
        uValue: "Coefficient U",
        uValue2D: "Coefficient U (2D)",
        transmittanceConvergence: "Convergence de la transmission",
        vapourConvergence: "Convergence pression de vapeur / HR",
        converged: "Convergé",
        notConverged: "Non convergé",
        solveTime: (time: string) => `Temps de calcul : ${time}`,
        surfaceCondensation: "Condensation superficielle",
        condensing: "Condensation",
        mouldRisk: "Risque de moisissures",
        ok: "OK",
        noSurfaceCondensation: "Pas de condensation superficielle",
        condensationGlaser: "Condensation (Glaser)",
        interstitialCondensation: "Condensation interstitielle",
        noCondensation: "Pas de condensation",
        suggestion: "Suggestion",
        leverIterations: "augmenter le nombre maximal d'itérations",
        leverMesh: "affiner le maillage (plus de rangées)",
        leverTolerance: "assouplir la tolérance (εU/εRH)",
        leverOr: ", ou ",
        suggestTry: (levers: string) => `Essayez de : ${levers} (Paramètres → Solveur 2D).`,
        suggestAllTried: "Itérations/maillage déjà augmentés et tolérance déjà assouplie par rapport aux valeurs par défaut, toujours sans convergence - vérifiez si la stratigraphie présente un rapport de conductivité extrême entre les matériaux.",
        weight: "Poids",
        rw: "Rw (estimation loi de masse)",
      },
      psychro: {
        title: "Diagramme psychrométrique",
        hint: "l'air intérieur jusqu'à 80 % d'HR",
        hintValue: (temperature: string) => `l'air intérieur jusqu'à 80 % d'HR : ${temperature}`,
        unavailable: "Disponible une fois la stratigraphie calculée.",
        insideAir: "Air intérieur",
        humidityRatio: "Teneur en eau W",
        theta80: "θ à 80 % d'HR",
        insideSurface: "Surface intérieure",
        insideSurfaceMin: "Surface intérieure, min.",
        noMouldRisk: "Surface au-dessus de θ80% : pas de risque de moisissures",
        mouldRisk: "Surface en dessous de θ80% : risque de moisissures",
        chartLabel: "Diagramme psychrométrique : l'air intérieur refroidi à teneur en eau constante jusqu'à 80 % d'HR",
        dryBulb: (unit: string) => `température sèche [${unit}]`,
        humidityRatioAxis: "teneur en eau W [g/kg d'air sec]",
        enthalpy: "enthalpie h [kJ/kg]",
      },
    },
    tutorial: {
      startTitle: "Démarrer le tutoriel ?",
      startMessage: "Une courte démonstration des principales fonctions sur le mur à ossature bois validé avec Ubakus, déjà calculé : chaque étape est réalisée pour vous, vous poursuivez avec Suivant. Votre projet est mis de côté et revient tel quel à la fin.",
      start: "Démarrer le tutoriel",
      preparing: "Préparation du tutoriel…",
      running: "Le tutoriel est en cours : appuyez sur Suivant, ou sur Quitter le tutoriel pour le quitter.",
      stepOf: (step: number, total: number) => `Étape ${step} sur ${total}`,
      showing: "Démonstration…",
      stepDone: "✓ Terminé",
      skip: "Quitter le tutoriel",
      next: "Suivant",
      startButton: "Commencer",
      finish: "Terminer",
      demoName: "Mur de démonstration",
      steps: {
        intro: {
          title: "Tutoriel",
          text: "Une courte démonstration des principales fonctions sur un exemple réel - le mur à ossature bois avec vide technique validé avec Ubakus, déjà calculé. Chaque étape est réalisée pour vous et montrée sur la page : appuyez sur Suivant pour continuer, ou sur Quitter le tutoriel pour le quitter. Votre propre projet est mis de côté et revient tel quel à la fin.",
        },
        name: {
          title: "Nom du projet",
          text: "Le projet reçoit un nom (facultatif) : il figure dans le rapport.",
        },
        site: {
          title: "Site et climat",
          text: "Le site définit le climat des vérifications de condensation : un clic sur la carte, un lieu recherché par son nom ou l'un des lieux de la carte. Ici, le site de l'exemple est conservé.",
        },
        element: {
          title: "Élément",
          text: "Mur : l'exemple est un mur extérieur. La toiture et le contact avec le sol fixent d'autres résistances superficielles.",
        },
        analysis: {
          title: "Analyse",
          text: "Statique : valeur U, condensation de Glaser à la condition de calcul et Glaser mensuel sur le climat du site. Quasi-dynamique : chaleur et humidité heure par heure sur plusieurs années du climat du site, dans ses propres onglets. L'exemple utilise l'analyse statique.",
        },
        stratigraphy: {
          title: "Stratigraphie",
          text: "L'onglet Stratigraphie : les couches du mur, de l'extérieur vers l'intérieur, avec leurs montants.",
        },
        addMaterial: {
          title: "Ajouter un matériau",
          text: "Un matériau est glissé depuis la bibliothèque sur le dessin : un espace montre où il ira, et il est ajouté là où il est relâché.",
        },
        thickness: {
          title: "Modifier une épaisseur",
          text: "Un clic sur une couche ouvre ses propriétés : l'épaisseur est saisie et confirmée avec Entrée.",
        },
        layerValues: {
          title: "Valeurs d'une seule couche",
          text: "Dans le même panneau, le λ de cette couche est modifié : seule cette couche change, le matériau de la bibliothèque reste tel quel.",
        },
        undo: {
          title: "Annuler",
          text: "Annuler (ou Ctrl+Z) reprend les modifications une à une, jusqu'à ce que l'exemple soit comme avant ; ▾ liste les modifications récentes.",
        },
        results: {
          title: "Analyse et résultats",
          text: "Analyse et résultats : l'exemple est déjà calculé.",
        },
        checks: {
          title: "Les vérifications",
          text: "Valeur U par rapport à la limite, condensation interstitielle (Glaser), humidité superficielle intérieure (moisissures) et le verdict du Glaser mensuel ; en dessous, la température et la pression de vapeur à travers le mur.",
        },
        monthly: {
          title: "Glaser mensuel",
          text: "Un clic sur la vérification Glaser mensuel : condensation et séchage mois par mois sur le climat du site.",
        },
        report: {
          title: "Rapport",
          text: "Le Rapport : tout dans un seul document, à imprimer ou à enregistrer en PDF.",
        },
        done: {
          title: "Terminé",
          text: "Voilà le parcours principal : projet et site, stratigraphie, résultats, rapport. Appuyez sur Terminer : votre propre projet revient tel que vous l'avez laissé. Tutoriel, dans la barre latérale, le montre à nouveau à tout moment.",
        },
      },
    },
    report: {
      printAndContents: "Impression et contenu du rapport",
      print: "Imprimer…",
      printInfo: (pages: number, bw: boolean) => `${pages} page${pages > 1 ? "s" : ""} · A4 · ${bw ? "noir et blanc" : "couleur"}`,
      contents: "Contenu",
      groups: {
        inputs: "Données d'entrée",
        results: "Résultats",
      },
      all: "tout",
      none: "aucun",
      notAvailable: "Non disponible pour le calcul actuel",
      fieldShown: "Champ affiché",
      sections: {
        project: "Projet",
        site: "Site et climat",
        conditions: "Conditions de calcul",
        drawing: "Dessin de la stratigraphie",
        layers: "Tableau des couches",
        checks: "Vérifications",
        uvalue: "Calcul du coefficient U",
        nodes: "Valeurs aux interfaces",
        temp: "Profil de température",
        profiles2d: "Profils verticaux 2D",
        compare: "Comparaison 1D / 2D",
        climate: "Année de référence du site",
      },
      relativeHumidity: "Humidité relative",
      untitledProject: "Projet sans titre",
      notes: "Notes",
      date: "Date",
      calculation2D: "Maillage 2D (régime permanent, volumes finis)",
      location: "Localisation",
      climateData: "Données climatiques",
      elevation: "Altitude",
      topSide: (side: string) => `Côté supérieur (${side}) : température / HR / résistance superficielle`,
      bottomSide: (side: string) => `Côté inférieur (${side}) : température / HR / résistance superficielle`,
      materialInsideOutside: "Matériau (intérieur → extérieur)",
      check: "Vérification",
      value: "Valeur",
      reference: "Référence",
      outcome: "Résultat",
      satisfied: "SATISFAITE",
      notSatisfied: "NON SATISFAITE",
      overallOutcome: "Résultat global",
      allSatisfied: "TOUTES LES VÉRIFICATIONS SATISFAITES",
      notAllSatisfied: "VÉRIFICATIONS NON TOUTES SATISFAITES",
      uFraming: "U avec l'ossature (méthode combinée EN ISO 6946)",
      interface: "Interface",
      insideNode: (n: number) => `${n} (intérieur)`,
      outsideNode: (n: number) => `${n} (extérieur)`,
      verdict: "Résultat",
      notVerified: (failures: string) => `non vérifié : ${failures}`,
      method: "Méthode",
      methodValue: (preset: string, start: string) => `${preset} ; début du cycle : ${start}`,
      monthlySection: (u: string, maxAccumulated: string, left: string, fRsi: string, fRsiMin: string) => `U ${u} W/(m²K) · max. accumulé ${maxAccumulated} · restant en fin de cycle ${left} · f_Rsi ${fRsi} / ${fRsiMin}`,
      glaserMonthShown: "Diagramme de Glaser (mois affiché sur la page)",
      quantity: "Grandeur",
      simplified1D: "1D simplifié",
      mesh2D: "Maillage 2D",
      insideSurfaceTemperature: "Température de surface intérieure",
      insideSurfaceRh: "HR de surface intérieure",
      min: "(min.)",
      max: "(max.)",
      empty: "Cochez dans la liste Contenu les sections à inclure.",
      disclaimer1: "Tous les calculs doivent être vérifiés et approuvés par le concepteur responsable avant toute utilisation.",
      disclaimer2: "Les valeurs des matériaux sont tirées des certificats ou des fiches techniques des produits ; l'utilisation de méthodes numériques (mode maillage 2D) et le choix des matériaux et produits de la stratigraphie relèvent de la responsabilité du concepteur.",
      pageOf: (page: number, pages: number) => `Page ${page} sur ${pages}`,
      reportPages: "Pages du rapport",
      viewer: "Visionneuse du rapport",
      previousPage: "Page précédente",
      nextPage: "Page suivante",
      page: "Page",
      zoomOut: "Zoom arrière",
      zoomIn: "Zoom avant",
      zoom: "Zoom",
      fitWidthPct: (percent: string) => `Ajuster à la largeur (${percent} %)`,
      fitPagePct: (percent: string) => `Ajuster à la page (${percent} %)`,
      fitWidth: "Ajuster à la largeur",
      fitPage: "Ajuster à la page",
      pageLayout: "Mise en page",
      layouts: {
        continuous: "Pages continues",
        single: "Page unique",
        two: "Deux pages côte à côte",
      },
      blackAndWhite: "Noir et blanc",
      blackAndWhiteOn: "Noir et blanc (activé : également à l'impression)",
      bwShort: "N/B",
    },
  },

  // Texts of the Design System V2 components (src/components/ds2).
  designSystem: {
    openInNewLabel: "S'ouvre dans un nouvel onglet",
    breadcrumbLabel: "Fil d'Ariane",
    openMenuLabel: "Ouvrir le menu",
    closeMenuLabel: "Fermer le menu",
    closeLabel: "Fermer",
    accountMenuLabel: "Menu du compte",
    paginationOfLabel: "sur",
    previousPageLabel: "Page précédente",
    nextPageLabel: "Page suivante",
    selectPlaceholder: "Sélectionner...",
    dropFilesLabel: "Déposez-le !",
    dropZoneLabel: "Zone de dépôt de fichiers",
    dropRejectedLabel: "Ce type de fichier n'est pas pris en charge",
    searchPlaceholder: "Rechercher",
    clearSearchLabel: "Effacer la recherche",
    toggleSubmenuLabel: (text) => `Basculer ${text}`,
    contextMenuTriggerLabel: "Ouvrir le menu d'actions",
    increaseLabel: (value: number) => `Augmenter ${value}`,
    decreaseLabel: (value: number) => `Diminuer ${value}`,
    datePicker: {
      clearButton: "Effacer la date",
      nextMonth: "Mois suivant",
      placeholder: "Choisir une date",
      previousMonth: "Mois précédent",
      selectMonth: "Choisir le mois",
      selectYear: "Choisir l'année",
      calendarLabel: "Choisir une date",
    },
    cancelLabel: "Annuler",
    okLabel: "OK",
    removeLabel: "Supprimer",
    requiredFieldLabel: "Ce champ est obligatoire.",
    fieldTooSmallLabel: "La valeur de ce champ est trop basse.",
    fieldTooLargeLabel: "La valeur de ce champ est trop élevée.",
    fieldInvalidFormatLabel: "La valeur de ce champ a un format invalide.",
    fieldInvalidValueLabel: "La valeur de ce champ n'est pas valide.",
    browseOrDropFileLabel: "Glissez-déposez, ou cliquez pour parcourir",
    browseOrDropImageLabel: "Glissez-déposez, ou cliquez pour importer une image",
    removeImageLabel: "Supprimer l'image",
    resetImageLabel: "Réinitialiser l'image",
    translationsLabel: "Traductions",
    increaseOrderLabel: "Augmenter l'ordre",
    decreaseOrderLabel: "Diminuer l'ordre",
    stagingLabel: "Environnement de test",
    unexpectedErrorLabel: "Une erreur s'est produite. Veuillez réessayer.",
    saveLabel: "Enregistrer",
    loadingLabel: "Chargement",
    browseFilesLabel: "Parcourir les fichiers",
    noOptionsLabel: "Aucune option",
    quantityOfLabel: (label: string) => `Quantité de ${label}`,
    chooseOrDropFileLabel: "Choisissez un fichier ou faites-le glisser ici pour le téléverser.",
    stepOfLabel: (current: number, total: number) => `Étape ${current} sur ${total}`,
    stepCompletedLabel: "terminée",
    backToStepLabel: (step: number) => `Revenir à l’étape ${step}`,
  },
};

export default fr;
